import type { TranslationDictionary, TranslationValue } from "../types";

const en: TranslationDictionary = {
  common: {
    english: "English",
    chinese: "中文",
    language: "Language",
    back: "Back",
    cancel: "Cancel",
    copy: "Copy",
    close: "Close",
    delete: "Delete",
    download: "Download",
    edit: "Edit",
    settings: "Settings",
    archived: "Archived",
    retry: "Retry",
    save: "Save",
    send: "Send",
    signOut: "Sign out",
    continue: "Continue",
    restore: "Restore",
    verify: "Verify",
    verifying: "Verifying",
    loading: "Loading...",
    manage: "Manage",
    moreActions: "More actions",
    moreOptions: "More options",
    gotIt: "Got it",
    error: "Error:",
    nextChooseSkills: "Next: Choose skills",
    closeNotification: "Close notification",
    closeModal: "Close modal",
    inviteMembers: "Invite members",
  },
  login: {
    title: "The 24/7 Operator for Your Business.",
    subtitle: "Stop working in your business. Start working on it again.",
    signIn: "Sign in or Sign up",
    signingIn: "Signing in",
    signOut: "Sign out",
    prompts: {
      failedOrders: "Fix failed orders in Shopify.",
      attentionToday: "Show me what needs attention today.",
      salesDown: "Explain why sales are down this week.",
      losingConversion: "Find products losing conversion.",
      highValueCustomers: "Identify my highest-value customers.",
      reEngagementCampaign: "Create a re-engagement campaign draft.",
      growStore: "Recommend 3 actions to grow this store.",
      revenueOrders: "Summarize today's revenue and orders.",
      trafficDrops: "Spot unusual traffic drops.",
      winBackCustomers: "Flag customers I should win back.",
    },
    cancelSignIn: "Cancel sign-in",
  },
  sidebar: {
    collapse: "Collapse Sidebar",
    expand: "Expand Sidebar",
    resize: "Resize sidebar",
    overview: "New Task",
    scheduled: "Scheduled",
    skills: "Experts",
    operations: "Tasks",
    addOperation: "Add expert",
    noRecentChats: "No recent chats",
    moreActions: "More actions",
    newTask: "New task",
    archive: "Archive",
    archiveLabel: "Archive {{label}}",
    archiveOperation: "Archive expert",
    archiveOperationTitle: "Archive expert?",
    archiveOperationConfirm:
      "This will archive {{name}}. You can restore it later from Archived.",
    archiveChatsTitle: "Archive {{count}} chat{{plural}}?",
    archiveChatsConfirm:
      "This will archive the selected chats. You can restore them later from Archived.",
    archiveAll: "Archive all",
    operationSettings: "Expert settings",
    revealInFinder: "Reveal in Finder",
    revealInExplorer: "Show in Explorer",
    revealInFolder: "Show in Folder",
    workspace: "Workspace",
    memberCount: "{{count}} member{{plural}}",
    inviteMembers: "Invite members",
    allWorkspaces: "All workspaces",
    expired: "EXPIRED",
    createWorkspace: "Create new workspace",
    profile: "Profile",
    general: "General",
    businessManagement: "Business management",
    preferences: "Preferences",
    businessGroups: "Business groups",
    membersAndRoles: "Members & roles",
    appsAndConnections: "Apps & connections",
    loadingWorkspace: "Loading {{name}}...",
    createOperationTitle: "Add an expert for Bustly",
    chooseSkillsTitle: "Choose skills for this expert",
    operationName: "Expert name",
    chooseIcon: "Choose avatar",
    operationDescription: "What should this expert handle for you?",
    operationNamePlaceholder: "e.g. Paid media review",
    operationDescriptionPlaceholder:
      "Describe the tasks, context, and outcomes this expert should handle.",
    deleteOperationTitle: "Delete expert",
    deleteOperationConfirm:
      "Are you sure you want to delete {{name}}? This action cannot be undone.",
    thisOperation: "this expert",
    heartbeat: "Heartbeat",
    pin: "Pin {{label}}",
    unpin: "Unpin {{label}}",
    pinChat: "Pin chat",
    unpinChat: "Unpin chat",
    renameChat: "Rename chat",
    archiveChat: "Archive chat",
    markAsUnread: "Mark as unread",
    copySessionId: "Copy task ID",
    pinOperation: "Pin expert",
    unpinOperation: "Unpin expert",
    pinned: "Pinned",
    archiveAllTasks: "Archive all tasks",
    archivedTasks: "Archived tasks",
    sortBy: "Sort by",
    sortCreated: "Created",
    sortUpdated: "Updated",
    sortManual: "Manual order",
    dragHandle: "Reorder {{label}}",
    dragInstructions:
      "Press Space to pick up an item. Use the arrow keys to move it, then press Space to drop or Escape to cancel.",
    dragStarted: "Picked up {{label}}.",
    dragMoved: "{{label}} is over {{target}}.",
    dragDroppedBefore: "Moved {{label}} before {{target}}.",
    dragDroppedAfter: "Moved {{label}} after {{target}}.",
    dragPinned: "Pinned {{label}}.",
    dragUnpinned: "Unpinned {{label}}.",
    dragCancelled: "Stopped moving {{label}}.",
    more: "More",
    moreOperationsActions: "More expert actions",
    collapsePinnedOperations: "Collapse pinned experts",
    expandPinnedOperations: "Expand pinned experts",
    collapseOperations: "Collapse experts",
    expandOperations: "Expand experts",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    showLess: "Show less",
    showMore: "Show more",
    waitingApproval: "Waiting approval",
    selectIcon: "Select avatar {{label}}",
  },
  archivedPage: {
    title: "Archived",
    operationsTitle: "Archived experts",
    tasksTitle: "Archived Tasks",
    unknownTime: "Unknown time",
    restoringOperation: "Restoring...",
    restoreOperation: "Restore expert",
    unarchiving: "Unarchiving...",
    unarchive: "Unarchive",
    empty: "No archived tasks.",
    emptyDescription: "Tasks archived from the sidebar will appear here.",
  },
  skills: {
    categories: {
      All: "All",
      Recommended: "Recommended",
      General: "General",
      "Ecommerce / DTC": "Ecommerce / DTC",
      "Retail Stores": "Retail Stores",
      "Food & Beverage": "Food & Beverage",
      "Health & Beauty": "Health & Beauty",
      "Professional Services": "Professional Services",
      "Home Services": "Home Services",
    },
    searchForOperation: "Search skills for this expert",
    loadingSkills: "Loading skills...",
    createRecommendationCopy:
      "Based on what this expert needs to handle, we've pre-selected a few recommended skills. You can add more from the skill library.",
    noSkillsFound: "No skills found",
    noMatchingSkills: "No matching skills",
    alreadyEnabled: "All available skills are already added",
    allEnabledDescription: "This expert already has every skill from the Hub.",
    addSkillsFromHub: "Add skills from Hub",
    addSkills: "Add skills",
    addSelected: "Add selected",
    addingSkillEnableHint: "Added skills are enabled for this expert.",
    noSkillsInHub: "No skills in the Hub yet",
    installOrUploadGlobal:
      "Install or upload skills before adding them to this expert.",
    skillsCount: "{{count}} skills",
    tryAnotherFilter: "Try another keyword or switch the current filter.",
    noSkillsToAdd: "No skills to add",
    noCategorySkillsToAdd: "No {{category}} skills to add",
    noMatchingSkillsToAdd: "No matching skills to add",
    tryAnotherCategory: "Try another category.",
    tryAnotherKeywordOrCategory:
      "Try another keyword or choose a different category.",
    tryAnotherCategoryOrSearch: "Try another category or search term.",
    pageTitle: "Skills",
    pageSubtitle:
      "Manage your skill library, and install or update what you need.",
    selectedCount: "Selected ({{count}})",
    clearAll: "Clear All",
    selectAll: "Select All",
    assigning: "Assigning...",
    assignToOperation: "Assign to expert",
    assignSelected: "Assign selected",
    assignSelectedSkills: "Assign selected skills",
    bulkAssignmentHint:
      "Assigning skills enables them for the selected experts.",
    noOperationsAvailable: "No experts available",
    working: "Working...",
    installSelected: "Install Selected",
    updateSelected: "Update Selected",
    uninstallSelected: "Uninstall Selected",
    manage: "Manage",
    batchInstall: "Batch Install",
    batchUpdate: "Batch Update",
    batchUninstall: "Batch Uninstall",
    upload: "Upload",
    search: "Search skills...",
    installed: "Installed",
    availableToInstall: "Available to Install",
    noSkillsAvailable: "No skills available yet",
    noWorkspaceSkills: "No workspace skills yet",
    noWorkspaceSkillsDescription:
      "Skills saved by people in this workspace will appear here.",
    noSkillsAvailableDescription:
      "Upload one from your machine, import it from GitHub, or build a new skill with Bustly.",
    noSkillsAvailableToInstall: "No skills available to install",
    everythingInstalled:
      "Everything in the current library is already installed.",
    noSkillsNeedUpdate: "No skills need update",
    latestVersion:
      "All installed skills are already on the latest published version.",
    noInstalledSkills: "No installed skills available",
    installFirstManage:
      "Install a skill first, then come back to manage it here.",
    installFirstAssign:
      "Install a skill first, then come back to assign it to an expert.",
    back: "Back",
    saving: "Saving...",
    saveOperation: "Save expert",
    installing: "Installing...",
    downloading: "Downloading...",
    install: "Install",
    download: "Download",
    updating: "Updating",
    update: "Update",
    uninstallSkill: "Uninstall",
    installUnavailable: "Install unavailable",
    viewDetails: "View details",
    capabilities: "Capabilities",
    requiredIntegrations: "Required integrations",
    assignedAgents: "Assigned experts",
    createdBy: "Created by",
    workspaceUser: "Workspace user",
    noAssignedAgents: "Not assigned",
    assignmentEnableHint: "Assigning a skill enables it for that expert.",
    noRequiredIntegrations: "No required integrations",
    enabled: "Enabled",
    installedButDisabled: "Installed but disabled",
    status: "Status",
    statusUpdateAvailable: "Update available",
    statusAssigned: "Assigned",
    statusInstalled: "Installed",
    statusNotAdded: "Not added",
    statusUnavailable: "Unavailable",
    actionManageAssignment: "Assign",
    actionAssign: "Assign",
    actionViewReason: "View reason",
  },
  expertHub: {
    tabs: {
      label: "Browse by",
      experts: "Experts",
      skills: "Skills",
      integrations: "Integrations",
    },
    categories: {
      all: "All experts",
      company: "Business & company",
      market: "Market, product & merchandise",
      brand: "Brand & content",
      growth: "Growth & sales",
      customer: "Customer operations",
      fulfillment: "Supply chain & fulfillment",
      governance: "Finance, tax & governance",
    },
    experts: {
      pageTitle: "Experts",
      pageSubtitle: "Find the right person by role and expertise.",
      searchLabel: "Search experts or business questions",
      searchPlaceholder:
        "For example: ad costs, hiring, tax, inventory, refunds, security",
      situationLabel: "Best for",
      responsibilitiesLabel: "Main responsibilities",
      filteredEmptyTitle: "No experts match this filter",
      filteredEmptyDescription:
        "Try the problem, team, or business metric you are working on.",
    },
    integrations: {
      pageTitle: "Integrations",
      pageSubtitle:
        "Browse the business apps Bustly currently supports connecting.",
      searchLabel: "Search integrations",
      searchPlaceholder: "Search apps or categories",
      supported: "Supported integration",
      emptyTitle: "No integrations match this search",
      emptyDescription: "Try another app name or category.",
    },
  },
  overview: {
    greeting: {
      morning: "Good morning, {{name}}",
      afternoon: "Good afternoon, {{name}}",
      evening: "Good evening, {{name}}",
    },
    businessHealth: "Business Health",
    integrations: "Integrations",
    priority: "Priority",
    priorityColumn: "Priority",
    findingSingular: "Priority",
    findingPlural: "Priority",
    descriptionColumn: "Description",
    operationColumn: "Expert",
    dateColumn: "Date",
    actionsColumn: "Actions",
    all: "All",
    archived: "Archived",
    healthy: "Healthy",
    warning: "Warning",
    critical: "Critical",
    suggestion: "Suggestion",
    findingHigh: "High",
    findingMedium: "Medium",
    findingSuggestions: "Suggestions",
    analyzing: "Analyzing...",
    analyzingDescription: "We're checking how your business is doing.",
    defaultAgentName: "Generalist",
    commandPlaceholder: "Assign a task to an expert",
    chooseExpert: "Choose expert",
    switchExpert: "Switch expert",
    chooseExpertToStart: "Choose an expert, then give them a task.",
    loadingExperts: "Loading experts...",
    expertsLoadFailed: "Experts couldn't be loaded. Try again.",
    retryExperts: "Retry",
    connectMore: "Connect more. Bustly understands more.",
    loadingConnections: "Loading connections...",
    connectionStatusUnavailable: "Connection status unavailable",
    activeConnections: "{{count}} Active Connection{{plural}}",
    connectedDataSources: "Connected Data Sources",
    manageIntegrations: "Manage integrations",
    justNow: "Just now",
    notYet: "Not yet",
    showLess: "Show less",
    showMore: "Show more",
    viewDetails: "View details",
    hideDetails: "Hide details",
    monitoringOn: "Monitoring On",
    enableMonitoring: "Turn on",
    toggleMonitoring: "Toggle monitoring",
    monitoringOffDescription:
      "Let Bustly find what needs your attention before you have to look for it.",
    turnOffMonitoring: "Turn off",
    gotIt: "Got it",
    onIt: "On it",
    takeAction: "Take action",
    customize: "Customize",
    customizeHeartbeatForOperation: "Customize what {{operator}} watches",
    closeHeartbeatCustomize: "Close monitoring goal customization",
    failedToLoadHeartbeat: "Failed to load monitoring instructions.",
    failedToSaveHeartbeat: "Failed to save monitoring instructions.",
    heartbeatInstructionsForOperation:
      "What should {{operator}} proactively watch for?",
    heartbeatInstructionsPlaceholderForOperation:
      "Describe the signals {{operator}} should watch and surface when something needs attention.",
    heartbeatInstructionsPlaceholders: {
      marketing:
        "Example: ad spend rising while orders drop, campaigns performing below target, creative or delivery issues, or broken attribution/data sync.",
      storeOps:
        "Example: orders unhandled for 24+ hours, low stock, shipping exceptions, store/payment connection issues, or catalog data problems.",
      customers:
        "Example: VIP customer complaints, repeated unanswered messages, rising negative reviews, unusual refund/return requests, or churn risk.",
      finance:
        "Example: failed payments, unusual refunds, missing payouts, reconciliation mismatches, chargeback spikes, or abnormal cost increases.",
    },
    allClear: "All clear",
    nothingNeedsAttention: "Nothing needs your attention at the moment.",
    nothingToActOn: "Nothing to act on yet",
    priorityEmptyDescription:
      "If we spot anything that needs your attention, we'll surface it here.",
    noArchivedItems: "No archived items yet",
    archivedEmptyDescription:
      "Items you've reviewed or acted on will appear here.",
    untitledIssue: "Untitled issue",
    unknownOperation: "Unknown expert",
    newConversation: "New conversation",
    closeArchiveDrawer: "Close archive drawer",
    newTask: "New task",
    openNewTask: "Open new task",
    closeCommandHub: "Close command hub",
    tellOperationWhatToDo: "Tell this expert what to do",
    selectOperation: "Select expert",
    operations: "Experts",
    addMenu: "Add files and more",
    addPhotosAndFiles: "Add photos & files",
    plugins: "Plugins",
    chromePlugin: "Chrome",
    chromePluginDescription: "Use signed-in Chrome tabs for this task",
    dropFilesToAttach: "Drop files to attach to task",
    preview: "Preview",
    failedToLoadIntegrations: "Failed to load workspace integrations.",
    simulateScan: "Simulate Scan",
    expertUpgradeNotice: {
      title: "Meet your expert team",
      titlePrefix: "Meet your ",
      titleHighlight: "expert team",
      intro:
        "Different tasks, different experts. Bustly equips you with specialized experts across key domains, bringing deeper business context and precise delivery.",
      artworkLabel: "A selection of specialized Bustly Experts",
      artworkPlaceholderTitle: "Upgrade artwork",
      artworkPlaceholderDescription:
        "Reserved for the final Operation-to-Expert campaign image.",
      workTitle: "Domain-matched experts",
      workDescription:
        "Product selection, ads, and operations each have dedicated experts solving domain problems.",
      capabilitiesTitle: "Ready to work",
      capabilitiesDescription:
        "Preloaded with e-commerce methods and tools—no prompt tuning needed, just assign tasks directly.",
      deliveryTitle: "Closed-loop delivery",
      deliveryDescription:
        "Executes multi-step tasks autonomously to produce reliable, ready-to-use business deliverables.",
      migrationFootnote:
        "* All chat history and schedules are preserved, so work continues as usual.",
      primaryAction: "Start task",
      close: "Close Expert upgrade notice",
    },
    userOnboardingGuide: {
      preparedTitle: "Your first expert is ready",
      preparedDescription:
        "Bustly set up {{expert}} based on your role, with its prompt and skills already configured.",
      nextToSettings: "Next 1/2",
      settingsTitle: "Customize it anytime",
      settingsDescription:
        "Open Expert settings to edit the name, prompt, or skills.",
      dismiss: "Dismiss onboarding tips",
    },
  },
  chatTimeline: {
    subagentStarted: "{{task}} started working",
    toolGroup: {
      exploredFile: "Explored {{count}} file{{plural}}",
      wroteFile: "Wrote file {{count}} time{{plural}}",
      editedFile: "Edited file {{count}} time{{plural}}",
      ranCommand: "Ran {{count}} command{{plural}}",
      managedProcess: "Managed process {{count}} time{{plural}}",
      attachedFile: "Attached file {{count}} time{{plural}}",
      browserTask: "Handled browser task {{count}} time{{plural}}",
      canvasAction: "Controlled canvas {{count}} time{{plural}}",
      nodeAction: "Controlled node {{count}} time{{plural}}",
      sentMessage: "Sent message {{count}} time{{plural}}",
      spokeText: "Spoke text {{count}} time{{plural}}",
      viewedAgentList: "Viewed agent list {{count}} time{{plural}}",
      viewedSessionList: "Viewed task list {{count}} time{{plural}}",
      viewedSessionHistory: "Viewed task history {{count}} time{{plural}}",
      sentSessionMessage: "Sent task update {{count}} time{{plural}}",
      spawnedSession: "Delegated {{count}} task{{plural}}",
      managedAgent: "Managed agent {{count}} time{{plural}}",
      viewedSessionStatus: "Viewed task status {{count}} time{{plural}}",
      searchedWeb: "Searched web {{count}} time{{plural}}",
      fetchedWeb: "Fetched web {{count}} time{{plural}}",
      searchedMemory: "Searched memory {{count}} time{{plural}}",
      readMemory: "Read memory {{count}} time{{plural}}",
      managedCron: "Managed scheduled task {{count}} time{{plural}}",
      managedGateway: "Managed gateway {{count}} time{{plural}}",
      whatsappLogin: "Handled WhatsApp login {{count}} time{{plural}}",
      discordAction: "Handled Discord {{count}} time{{plural}}",
      slackAction: "Handled Slack {{count}} time{{plural}}",
      usedTool: "Used {{label}} {{count}} time{{plural}}",
      more: "{{count}} more",
      hiddenActivity: "Hidden activity",
    },
    toolItem: {
      exploredFile: "Explored file",
      wroteFile: "Wrote file",
      editedFile: "Edited file",
      ranCommand: "Ran command",
      managedProcess: "Managed process",
      attachedFile: "Attached file",
      browserTask: "Handled browser task",
      canvasAction: "Controlled canvas",
      nodeAction: "Controlled node",
      sentMessage: "Sent message",
      spokeText: "Spoke text",
      viewedAgentList: "Viewed agent list",
      viewedSessionList: "Viewed task list",
      viewedSessionHistory: "Viewed task history",
      sentSessionMessage: "Sent task update",
      spawnedSession: "Delegated task",
      managedAgent: "Managed agent",
      viewedSessionStatus: "Viewed task status",
      searchedWeb: "Searched web",
      fetchedWeb: "Fetched web",
      searchedMemory: "Searched memory",
      readMemory: "Read memory",
      managedCron: "Managed scheduled task",
      managedGateway: "Managed gateway",
      whatsappLogin: "Handled WhatsApp login",
      discordAction: "Handled Discord",
      slackAction: "Handled Slack",
      usedTool: "Used {{label}}",
    },
    toolItemRunning: {
      exploredFile: "Exploring file",
      wroteFile: "Writing file",
      editedFile: "Editing file",
      ranCommand: "Running command",
      managedProcess: "Managing process",
      attachedFile: "Attaching file",
      browserTask: "Handling browser task",
      canvasAction: "Controlling canvas",
      nodeAction: "Controlling node",
      sentMessage: "Sending message",
      spokeText: "Speaking text",
      viewedAgentList: "Viewing agent list",
      viewedSessionList: "Viewing task list",
      viewedSessionHistory: "Viewing task history",
      sentSessionMessage: "Sending task update",
      spawnedSession: "Delegating task",
      managedAgent: "Managing agent",
      viewedSessionStatus: "Viewing task status",
      searchedWeb: "Searching web",
      fetchedWeb: "Fetching web",
      searchedMemory: "Searching memory",
      readMemory: "Reading memory",
      managedCron: "Managing scheduled task",
      managedGateway: "Managing gateway",
      whatsappLogin: "Handling WhatsApp login",
      discordAction: "Handling Discord",
      slackAction: "Handling Slack",
      usedTool: "Using {{label}}",
    },
    executionFailed: "Execution Failed",
    connectionInterrupted: "Connection interrupted",
    connectionInterruptedDescription:
      "Your task may not be complete. Check your network before trying again.",
    retryTask: "Retry task",
    reason: "Reason",
    retryNow: "Retry Now",
    editMessagePlaceholder: "Edit your request",
    sending: "Sending...",
    thinking: "Thinking",
    reconnecting: "Reconnect",
    openFile: "Open file",
    pdfDocument: "Document · PDF",
    openPdfAria: "Choose how to open {{file}}",
    openIn: "Open in",
    openPdfFailed: "Couldn't open this PDF. Try again.",
    processed: "Processed",
    processedWithDuration: "Processed in {{duration}}",
    executionCompleted: "Execution Completed",
    copyMessageAria: "Copy request",
    addToChat: "Add to Chat",
    editMessageAria: "Edit request",
    publishResult: "Publish",
    publishResultAria: "Publish this result",
    publishResultDescription: "Let Bustly publish the page from this result",
  },
  scheduled: {
    pageTitle: "Scheduled",
    pageSubtitle:
      "Manage recurring tasks and review the latest results in one place.",
    newScheduled: "New Scheduled",
    schedulerDisabled:
      "Scheduled tasks are saved, but they will not run until the gateway cron scheduler is enabled again.",
    current: "Current",
    active: "Active",
    paused: "Paused",
    loadingTasks: "Loading scheduled tasks...",
    noActiveTasks: "No active scheduled tasks yet.",
    noPausedTasks: "No paused scheduled tasks.",
    unknownTime: "Unknown time",
    unknownOperation: "Unknown expert",
    started: "Started",
    summary: {
      once: "One time on {{date}}",
      everyDays: "Every {{count}} day{{plural}}",
      everyHours: "Every {{count}} hour{{plural}}",
      everyMinutes: "Every {{count}} minute{{plural}}",
      everySeconds: "Every {{count}} second{{plural}}",
      dailyAt: "Daily at {{time}}",
      weekdaysAt: "{{weekdays}} at {{time}}",
      weekdaysSeparator: ", ",
      cron: "Cron {{expression}}",
      weekdays: { Mo: "Mo", Tu: "Tu", We: "We", Th: "Th", Fr: "Fr", Sa: "Sa", Su: "Su" },
    },
    taskInstructions: "Task Instructions",
    noTaskInstructions: "No task instructions configured.",
    handledBy: "Handled by",
    editTask: "Edit task",
    deleteTask: "Delete task",
    close: "Close",
    closeDrawer: "Close drawer",
    editTitle: "Edit Scheduled Task",
    newTitle: "New Scheduled Task",
    addOperationFirst:
      "Add an expert first. Scheduled tasks only run with an expert.",
    custom: "Custom",
    createCustomTask: "Create custom task",
    createCustomTaskDescription:
      "Start from scratch with your own rules and schedule",
    templatesCount: "{{count}} templates",
    taskName: "Task name",
    taskNamePlaceholder: "e.g. Morning Briefing",
    taskInstructionsLabel: "Task instructions",
    taskInstructionsPlaceholder:
      "e.g. Pull together a concise morning briefing with today's priorities, key updates, and anything that needs my attention",
    timePlaceholder: "e.g. 09:00 AM",
    ownerLabel: "Which expert should handle it?",
    ownerPlaceholder: "Select an expert...",
    ownerHelp: "Add an expert from the sidebar before scheduling work.",
    schedule: "Schedule",
    once: "Once",
    daily: "Daily",
    interval: "Interval",
    at: "at",
    every: "Every",
    hours: "hours",
    minutes: "minutes",
    days: "days",
    minimumInterval: "Minimum interval: {{count}} minutes.",
    selectDate: "Select date",
    chooseFutureTime: "Choose a date and time in the future.",
    pastTimeRestored: "That time has passed. Your previous selection was restored; choose another time.",
    selectYear: "Select year",
    selectMonth: "Select month",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    previousYear: "Previous year",
    nextYear: "Next year",
    previousYears: "Previous years",
    nextYears: "Next years",
    cancel: "Cancel",
    back: "Back",
    saving: "Saving...",
    creating: "Creating...",
    saveChanges: "Save Changes",
    createScheduled: "Create Scheduled",
    chooseOwnerBeforeCreate:
      "Choose an expert before creating a scheduled task.",
    failedToBindTasks: "Failed to prepare some scheduled tasks for execution.",
    failedToLoadTasks: "Failed to load scheduled tasks.",
    failedToOpenEditor: "Failed to open scheduled task editor.",
    failedToDeleteTask: "Failed to delete scheduled task.",
    failedToUpdateTask: "Failed to update scheduled task.",
    failedToCreateTask: "Failed to create scheduled task.",
    deleteConfirm: 'Delete the scheduled task "{{name}}"?',
    templateGroups: {
      briefings: "Briefings",
      marketing: "Marketing",
      storeOps: "Store Ops",
      customers: "Customers",
      finance: "Finance",
    },
    templates: {
      dailyStoreReport: {
        title: "Daily Store Report",
        prompt:
          "Review my store's latest performance across sales, orders, traffic, conversion rate, AOV, repeat purchases, refunds, and ad performance. Compare it with the previous period, highlight the biggest changes, explain what may be causing them, and tell me which issues or opportunities need my attention today.",
      },
      weeklyOpportunityBriefing: {
        title: "Weekly Opportunity Briefing",
        prompt:
          "Review recent sales, traffic, products, ads, customers, repeat purchases, and conversion trends. Highlight the most notable changes, potential opportunities, and issues worth reviewing this week. Suggest practical next steps based on the available data.",
      },
      adSpendWatch: {
        title: "Ad Spend Watch",
        prompt:
          "Review my recent ad spend, ROAS, CPA, CTR, conversion rate, sales contribution, and campaign-level changes. Identify which campaigns or ad sets should keep spending, which need budget adjustments, which may be wasting money, and which should be paused. Explain the reason behind each recommendation.",
      },
      trafficQualityReview: {
        title: "Traffic Quality Review",
        prompt:
          "Review my store's traffic performance, including visitors, sessions, traffic sources, paid traffic, organic traffic, campaign traffic, referral traffic, and returning visitors. Identify major traffic changes, explain what may be driving them, and tell me which channels need attention or optimization.",
      },
      weeklyCampaignReview: {
        title: "Weekly Campaign Review",
        prompt:
          "Review the past week's campaign performance across spend, clicks, traffic quality, conversions, revenue, ROAS, CPA, and audience performance. Summarize what worked, what declined, what should be tested next, and what I should change before the next week starts.",
      },
      orderFollowUpCheck: {
        title: "Order Follow-up Check",
        prompt:
          "Review recent orders and find any that need attention, such as paid orders waiting to ship, fulfillment delays, delivery issues, failed payments, pending orders, or orders that may need manual follow-up. Group the issues by type and tell me which ones should be handled first.",
      },
      inventoryRiskCheck: {
        title: "Inventory Risk Check",
        prompt:
          "Check my inventory for low-stock, out-of-stock, fast-selling, and slow-moving products. Identify items that may hurt sales if not restocked soon, products that are tying up inventory, and any inventory changes that need attention.",
      },
      productPerformanceReview: {
        title: "Product Performance Review",
        prompt:
          "Review product performance and key product pages. Find products with declining sales, low traffic, low conversion, weak add-to-cart rates, poor repeat purchase behavior, or product page issues such as missing images, weak descriptions, unclear pricing, or missing variants. Explain what may be causing the issue and suggest what to improve first.",
      },
      refundCancellationReview: {
        title: "Refund & Cancellation Review",
        prompt:
          "Review recent canceled and refunded orders. Group them by reason, product, channel, customer type, and fulfillment status where possible. Identify where the problems are happening, whether the issue is increasing, and what I should fix first.",
      },
      customerValueReview: {
        title: "Customer Value Review",
        prompt:
          "Review recent customers and identify high-value customers, repeat buyers, inactive customers, first-time buyers with repeat potential, and customers who may need follow-up. Suggest what message or action I should take for each group.",
      },
      payoutRefundCheck: {
        title: "Payout & Refund Check",
        prompt:
          "Review recent payouts, payments, refunds, fees, chargebacks, and unusual payment changes. Compare expected payouts with actual records where possible, flag mismatches or missing payouts, and tell me what needs to be reviewed.",
      },
    },
  },
  agentSettings: {
    fallbackOperation: "Expert",
    identity: "Identity",
    skills: "Skills",
    operationHandles: "What this expert handles",
    save: "Save",
  },
  website: {
    shopifyInstall: {
      workspaceUnavailable: "This workspace isn't available yet. Try again or choose another workspace.",
      workspaceUnavailableOption: "Workspace unavailable",
      validationError: "We couldn't check this Shopify connection. Try again.",
      reviewPlan: "Review workspace plan",
      title: "Connect Shopify to Bustly",
      successTitle: "Shopify is connected",
      missing: "Open Bustly from Shopify to connect your store.",
      invalid: "This Shopify connection link is unavailable for your account or has expired. Open Bustly from Shopify again to get a fresh link.",
      checking: "Checking your Shopify connection…",
      switching: "Selecting your workspace…",
      connecting: "Finish authorizing Shopify in the window that opened.",
      workspaceRequired: "Create a workspace or accept an invitation before connecting your store.",
      retry: "We couldn't finish connecting Shopify. Try again.",
      restricted: "This workspace can't connect this store. Review its connection requirements or choose another workspace.",
      success: "Your store is ready in Bustly.",
      workspace: "Workspace",
      chooseWorkspace: "Choose a workspace",
      connect: "Connect Shopify",
      connectingButton: "Connecting…",
      workspaceError: "We couldn't load your workspaces.",
      retryButton: "Try again",
    },
    marketIntelligence: {
      task: {
        allCategories: "All monitoring content",
        typeLabel: "Task type",
        "chooseSources": "Choose where to collect content.",
        "chooseMode": "Choose task type",
        "chooseContent": "Choose what to monitor",
        "backToMode": "Back to task type",
        "chooseCategory": "Choose category",
        "plan": "Monitoring schedule",
        "historyRange": "Content date range",
        "period": "Monitoring period",
        "cadence": "Update frequency",
        "updateLimit": "Limit per update",
        "totalLimit": "One-time collection limit",
        "collected": "Collected",
        "nextRun": "Next update",
        "upTo": "Up to {{count}} records",
        "records": "{{count}} records",
        "sourceScope": "Per source settings",
        "noEnd": "No end date set",
        "fromToday": "Collect from today and update at the selected frequency.",
        "collectionStart": "Collection start date",
        "retry": "Retry collection",
        "status": "Task status",
        "createdDescription": "Collection is being prepared. Collected content will appear here.",
        "category": "Monitoring content",
        "one_time": {
          "title": "One-time collection",
          "description": "Collect historical content from the selected date range in a single run.",
          "all": "All",
          "waiting": "Waiting",
          "collecting": "Collecting",
          "completed": "Completed",
          "partial": "Partially completed",
          "failed": "Collection failed",
          "unknown": "Status unavailable",
          "start": "Start collection",
          "success": "Collection task created",
          "reviewHelp": "Start collection saves your settings and runs once. It will not update automatically.",
          "empty": "No one-time collections yet",
          "emptyHelp": "Create a task to collect content in a specified range."
        },
        "continuous": {
          "title": "Continuous monitoring",
          "description": "Collect new content at your chosen frequency.",
          "all": "All",
          "active": "Monitoring",
          "updating": "Updating",
          "paused": "Paused",
          "ended": "Ended",
          "failed": "Update failed",
          "start": "Start monitoring",
          "success": "Monitor created",
          "reviewHelp": "Start monitoring saves your settings, starts collection, and schedules later updates.",
          "empty": "No continuous monitors yet",
          "emptyHelp": "Create a monitor to keep collecting new content on a schedule."
        }
      },
      overview: {
        title: 'Monitor overview', total: 'Total monitors', active: 'Enabled monitors', failed: 'Monitors with failed updates', unit: 'Monitors',
        totalSummary: 'All monitors in one place', totalHint: 'Includes enabled and paused monitors',
        activeSummary: '{count} other monitors are paused', activeHint: 'Scheduled updates are enabled',
        failedSummary: 'Open monitor details to see the cause', noFailures: 'No failed updates found',
        failedHint: 'Based on each monitor’s latest update', unknown: 'Update status unavailable for {count} monitors',
      },
      aggregate: {
        "loading": "Checking collection totals…",
        "loadingContents": "Loading collected content…",
        "loadError": "Collection totals couldn't load. Your monitors are still available below.",
        "contentsError": "More content couldn't load. Try again; the items already shown are unchanged.",
        "firstCounted": "First included in these statistics",
        "untitled": "Collected content",
        "openMonitor": "Open monitor",
        "retry": "Retry",
        "refresh": "Refresh totals",
        "selectPlatform": "Filter new content by platform",
        "allPlatforms": "All platforms · {count}",
        "recentPlatformCount": "{platform} · {count} items",
        "liveScope": "Individual collection records are available below. Unique totals and first-collected additions are not available yet.",

        "label": "Workspace overview",
        "titles": {
          "monitors": "Total monitors",
          "platforms": "Content source distribution",
          "contents": "Total raw content collected",
          "recent": "Content collected in last 7 days"
        },
        "units": { "recent": "items", "platforms": "platforms" },
        "otherPlatforms": "Other platforms",
        "sourceShareTooltip": "{platform} · {count} items · {percent}%",
        "platformCoverage": "Covering {count} platforms",
        "open": {
          "monitors": "View all monitors",
          "platforms": "View collection by platform",
          "contents": "View collected content",
          "recent": "View content added in the last 7 days"
        },
        "monitorStatus": "{active} enabled · {paused} paused",
        "monitorTypes": {
          "social": "Social {count}",
          "advertising": "Ads {count}",
          "commerce": "Commerce {count}",
          "search": "Search {count}",
          "legacy": "Mixed {count}"
        },
        "noMonitorTypes": "No monitors yet",
        "platformHint": "Platforms with collected content",
        "contentTypes": "Ads · Posts · Products · Comments",
        "deduplicated": "Unique content across monitors",
        "recentHint": "From {count} monitors",
        "unavailable": "Collection totals unavailable",
        "unavailableHint": "Complete collection statistics are not available yet.",
        "demoNotice": "Collection statistics use demo data",
        "showLive": "Use real statistics",
        "showExample": "Preview example data",
        "categories": {
          "ad": "Ads",
          "post": "Posts",
          "product": "Products",
          "comment": "Comments",
          "search": "Search",
          "other": "Other"
        },
        "itemCount": "{count} items",
        "monitorCount": "{count} monitors",
        "newCount": "{count} new items",
        "platformContent": "View content from {platform}",
        "contentScope": "Readable content included since statistics started. The same content is counted once across current monitors. Expired or withdrawn content is excluded.",
        "recentScope": "First included in these statistics in the last 7 calendar days, including today. This is not the historical first appearance. Time zone: {zone}.",
        "firstCollected": "First collected",
        "allDays": "All 7 days",
        "selectDay": "Filter by collection date",
        "selectCategory": "Filter by content type",
        "allTypes": "All types",
        "loadMore": "Load more",
        "noContent": "Collected content will appear here.",
        "noMonitors": "Your monitors will appear here once created."
      },
      preview: { badge: 'Demo data' },
      trend: {
        title: 'Collection by update', latest: 'Latest update (records)', scope: 'One point per update',
        loading: 'Loading trend', error: 'Trend unavailable. Open details to check.', empty: 'The trend appears after content is collected', single: 'One observation available. Waiting for more updates.', incomplete: 'Collection totals are unavailable for some updates',
      },
      card: {
        geography: { meta: 'Ad region', google_search: 'Search location', google_trends: 'Interest region', amazon: 'Marketplace' },
        creator: 'Created by', unknownCreator: 'Unknown creator',
        keywords: 'Monitored keywords', scope: 'Monitoring scope', category: 'Product category', platforms: 'Monitored platforms', latestCollection: 'Latest collection',
        collected: '{count} records', awaitingFirst: 'Waiting for the first update',
        countUnavailable: 'Collection count unavailable', failed: 'Update failed · Open details to retry', pending: 'Update in progress',
        actions: 'Actions for {name}', edit: 'Edit monitor',
      },
      store: {
        discoveryTitle: 'Discover store products',
        discoveryHelp: 'Store links use manual discovery within this run’s limit. Choose products from the saved results, then confirm a separate tracking monitor.',
        unavailable: 'Store collection is not enabled in this workspace. You can save the scope, but it will not collect until enabled.',
        candidatesTitle: 'Discovered products',
        candidatesHelp: 'Choose a saved update to review products. Reading this list does not start a new collection.',
        run: 'Discovery update', chooseRun: 'Choose a saved update', runNumber: 'Update {{number}}',
        runsLoading: 'Reading saved updates…',
        runsUnavailable: 'Saved updates could not be read. Close and reopen this monitor to try again.',
        noRuns: 'Products will appear after a store discovery update finishes.',
        reading: 'Reading saved products…',
        readError: 'These saved products could not be read. Your selection has not changed. Try again or choose another update.',
        retry: 'Read again',
        evidenceUnavailable: 'This update has no retained products available for selection. Choose another update or run discovery again.',
        bounded: 'These are the products returned within the discovery limit, not the store’s complete catalog.',
        someUnavailable: 'Some store results are unavailable. You can select products from the results shown.',
        empty: 'This saved update returned no candidate products. It does not mean the store has no products.',
        previous: 'Previous page', next: 'Next page', page: 'Page {{number}}',
        selected: '{{count}} products selected across pages', remove: 'Remove', limit: 'Up to 100 products can be selected for one tracking monitor.',
        trackingHelp: 'Tracking gets its own name, frequency and limit. Reviews are optional; no collection starts from this step.',
        configureTracking: 'Configure product tracking', trackingName: '{{name}} · Product tracking',
        selectionLocked: '{{count}} selected products. Close this draft to change the selection in the discovery results.',
        savedSelectionLocked: 'This monitor tracks {{count}} confirmed products. To track different products, create a separate monitor from the discovery results.',
      },
      monitorUsage: {
        title: 'Usage summary', view: 'View usage', loading: 'Loading…', retry: 'Try again', refresh: 'Refresh usage',
        scope: 'Across this monitor’s retained history. Reading usage does not start collection or analysis.',
        error: 'Usage could not be loaded. Please try again.', confirmedUnits: 'Confirmed Work Units', collectionUsage: 'Imported records (summed by task)',
        noUnits: 'No confirmed Work Units yet.', noCollections: 'No collection records yet.',
        details: 'Usage details and scope', retention: 'Includes currently retained tasks and reports from earlier configurations. Deleted history cannot be reconstructed; this is not a complete invoice.',
        separate: 'Only confirmed Work Units are included. Records are summed by task, not distinct content or charges.',
        asOf: 'Calculated at: {{time}}', stageUnits: 'Confirmed Work Units: {{amount}}',
        analysisCounts: '{{settled}} confirmed · {{pending}} unconfirmed · {{idle}} not started',
        collectionCounts: '{{count}} tasks · {{imported}} imported',
        records: 'Imported records: {{amount}} (summed by task, not distinct content)',
        supplementCounts: '{{count}} tasks · {{recorded}} saved processing records',
        supplementHelp: 'Task counts are not billable requests or successful downloads. No fees are estimated here.',
        stages: { collection: 'Content collection', comments: 'Comments', summary: 'Intelligence analysis', creative: 'Creative analysis', vision: 'Vision analysis', transcription: 'Transcription', media_inspection: 'Media inspection', media_preparation: 'Media preparation', landing: 'Landing product enrichment' },
      },
      delivery: {
        title: "Saved results", accepted: "Accepted items", rejected: "Invalid items excluded",
        excluded: "Valid items outside this budget", total: "Frozen total for this update",
        limited: "The collection budget was reached. Saved results remain available; this does not mean the market has no more content.",
        repairHint: "Retry importing the saved archive. This does not start another collection.",
        preparingEvidence: "Collected records are being prepared for reading. Do not start another collection.",
        attention: "This update needs investigation before recovery. Already saved records are retained; starting another collection is not a repair.",
        repair: "Retry saved archive", pending: "Submitting…", queued: "Archive retry queued",
        failed: "Unable to confirm the archive retry. Check the existing run before trying again.",
      },
      metaAdvertiser: {
        name: 'Advertiser',
        pageId: 'Page ID',
        openPage: 'Open Page',
      },
      searchEvidence: {"keywordStates":{"matched":"All words verified in this result","excluded":"Condition not found in this result","unknown":"Full content unavailable; match unconfirmed"},"page":"Search page {page}","paid":"Search ads","organic":"Organic search results","meaning":"Relative interest (0–100), not search volume. Each keyword is normalized independently; scores cannot be compared directly between keywords.","series":"Search interest over time","date":"Date","interest":"Relative interest","quality":"Data state","missing":"Unavailable","partial":"Partial period","observed":"Observed","pageBudget":"Up to {{total}} pages per keyword","pagesAllocation":"This update: up to {{total}} search pages","seriesAllocation":"This update: up to {{total}} interest series"},
      collectedSocial: {
        published: "Published",
        publishedUnknown: "Publication time unknown",
        timeDetails: "Time details",
        searchtitle: "Collected search content",
        searchdescription: "Browse the search results and interest data saved by this monitor.",
        searchempty: "No search content has been collected yet.",
        searchcount: "{count} search items collected",
        adtitle: "Collected ads",
        addescription: "Browse the ads and source links saved by this monitor.",
        adempty: "No ads have been collected yet.",
        "title": "Collected social content",
        "description": "Browse the posts, videos, and other social content saved by this monitor.",
        "failed": "Content is temporarily unavailable. Try again.",
        "loading": "Loading content…",
        "load": "View all content",
        "retry": "Retry this page",
        "count": "{count} items collected",
        "empty": "No social content has been collected yet.",
        "observed": "Observed",
        "source": "View source",
        "pagination": "Evidence pages",
        "previous": "Previous page",
        "page": "Page {number}",
        "next": "Next content page",
        "textUnavailable": "Text unavailable",
        "truncated": "Showing part of the text. Read the next archived segment or view the source.",
        "nextText": "Read next text segment",
        "metrics": {
          "likes": "Likes",
          "reactions": "Reactions",
          "shares": "Shares",
          "video_views": "Video views",
          "plays": "Plays",
          "comment_count": "Comments",
          "likes_or_score": "Likes / Reddit score",
          "replies": "Replies",
          "reposts": "Reposts",
          "quotes": "Quotes",
          "views": "Views",
          "bookmarks": "Bookmarks"
        }
      },
      products: {
        title: "Collected products",
        brand: "Source brand",
        availability: "Source availability",
        inventoryClaim: "Page stock claim",
        inventoryNotVerified: "not verified inventory",
        variants: "Observed variants",
        variantsScope: "Variants returned with this product, not a complete catalog or separately collected products.",
        variantCurrencyUnknown: "· currency not provided",
        variantsEmpty: "The source returned an empty list. Other variants may still exist.",
        variantsUnavailable: "No readable variant list was provided in this observation.",
        variantsCounts: "Source list: {count}; outside the reading limit: {omitted}; unreadable entries: {rejected}.",
        description: "Browse the Amazon products and source links saved by this monitor.",
        price: "Observed price",
        rating: "Rating",
        reviews: "Review count",
        truncated: "Product text is shortened. View the source for more detail.",
        titleUnavailable: "Product title unavailable",
        failed: "Products are temporarily unavailable. Previously loaded products are retained. Try again.",
        loading: "Loading products…",
        load: "View all products",
        retry: "Retry this page",
        count: "{count} products collected",
        empty: "No products have been collected yet.",
        observed: "Observed",
        source: "View product",
        pagination: "Product result pages",
        previous: "Previous page",
        page: "Page {number}",
        next: "Next product page",
      },
      reviews: {
        title: "Collected reviews", description: "Browse the Amazon product reviews and source links saved by this monitor.",
        review: "Review", rating: "Review stars", published: "Published date", helpful: "Helpful votes", purchase: "Source purchase label",
        verified: "Verified purchase", notVerified: "Not marked as verified", bodyUnavailable: "Review text is unavailable.",
        truncated: "Review text was shortened at collection.",
        failed: "Reviews are temporarily unavailable. Try again.", loading: "Loading reviews…", load: "View all reviews",
        retry: "Retry this page", count: "{count} reviews collected",
        empty: "No reviews have been collected yet.", observed: "Collected",
        source: "View product page", pagination: "Review result pages", previous: "Previous page", page: "Page {number}", next: "Next review page",
      },
      insights: {
        summaryTitle: "Bustly insights summary", findingsTitle: "Findings and report details",
        viewAllContent: "View all content", collapseContent: "Collapse content",
        title: "Insights", refresh: "Check for updates", loading: "Loading insights…",
        readFailed: "Could not check for updates. Previously loaded insights are retained. Try again later.",
        disabled: "Bustly insights summary is not generated yet. You can browse the collected public content below.",
        not_started: "No saved insight yet. Analysis is prepared after evidence is collected.",
        preparing: "Preparing the evidence for analysis.", queued: "New insights are awaiting analysis.",
        running: "Analysis is in progress. Results will appear here.", ready: "Insights are up to date.",
        failed: "This analysis did not complete. Collected evidence is unaffected.",
        unavailable: "No eligible analysis evidence is available. This does not mean there is no relevant content.",
        stale: "The previous insight is retained below. Check its analysis date.",
        generated: "Generated", window: "Observation window", eligible: "Evidence included",
        textAvailable: "Text available", partial: "Evidence has gaps. Conclusions apply only to observed content.",
        noFindings: "The collected content does not yet support a reliable conclusion.", citations: "Evidence references",
        action: "Suggested next step:", expandFinding: "Show explanation", collapseFinding: "Hide explanation",
        showAll: "More insights", showLess: "Key insights only", details: "About this analysis",
        relatedContent: "Related content", closeEvidence: "Hide related content",
        updatesOff: "Future automatic analysis is off. This insight remains available.",
        usageTitle: "Analysis usage",
        usageConfirmed: "Confirmed",
        usageUnit: "Work Units",
        usagePending: "Usage is still being confirmed. Any amount shown is not a final total.",
        usageNotStarted: "Analysis has not started for this version.",
        usageUnavailable: "Usage records are not available for this version.",
        usageScope: "Model analysis for this report only. Excludes content collection and other reports.",
        citationIds: "View reference IDs", evidence: "Browse content used in this analysis", next: "Next content page",
        source: "View source", evidenceFailed: "This evidence request failed. Previously loaded evidence is retained where access is still valid. Retry or check for updates.",
        citationEvidence: "View related content", retryEvidence: "Retry content request",
        citedEvidence: "Content related to this insight", versionEvidence: "Content used in this analysis",
        emptyEvidence: "No readable evidence was returned for this selection. This does not mean the market has no matching content.",
        textUnavailable: "Text unavailable",
      },
      eyebrow: "Market Intelligence",
      title: "Keyword monitors",
      description:
        "Create monitors to track updates across public ads, social media, ecommerce, and search channels for brands, competitors, and markets.",
      workspaceFallback: "Current workspace",
      currentWorkspace: "Current workspace",
      newMonitor: "New",
      newKeywordMonitor: "New monitor",
      monitorLink: {
        back: 'Back to monitors', invalid: 'This monitor link is incomplete or invalid. Open it again from your task.',
        loading: 'Checking workspace…', 'sign-in': 'Sign in to view this monitor.', signIn: 'Sign in',
        'workspace-error': 'We couldn’t load your workspaces. Try again.', unavailable: 'Your account cannot access this workspace. Contact its owner.',
        switch: 'This monitor belongs to another workspace. Switch to view it.', switchAction: 'Switch workspace and view monitor',
        retry: 'Try again', actionFailed: 'The action could not finish. Try again; the monitor link is preserved.',
        missing: 'This monitor was not found. It may have been deleted or is no longer accessible. You can view other monitors.',
      },
      reportLink: {
        title: 'Intelligence report', back: 'Back to monitors',
        invalid: 'This report link is incomplete or invalid. Open a report from your reminders.',
        loading: 'Checking report access…', 'sign-in': 'Sign in to open this report.', signIn: 'Sign in',
        'workspace-error': "We couldn't check workspace access. Try again.",
        unavailable: 'This workspace is not available to your account. Ask its owner to check your access.',
        switch: 'This report belongs to another workspace. Switch to it to continue.', switchAction: 'Switch workspace and open report',
        'permission-error': "We couldn't confirm your permissions. Try again or ask the workspace owner to check your access.",
        denied: 'Report reminders are available to Workspace Owners and Admins.', retry: 'Try again',
        actionFailed: "We couldn't finish that action. Try again; the report link is unchanged.",
      },
      reportNotifications: {
        title: "Report reminders", list: "Your report reminders", scope: "Your report reminders in this workspace. Opening a report does not start collection or analysis.",
        loading: "Reading report reminders…", empty: "New report reminders will appear here.",
        denied: "Only current Workspace Owners and Admins can view their report reminders.",
        failed: "We couldn't read report reminders. No settings changed. Try again.",
        retry: "Try again", ready: "Report ready", open: "View report", older: "Older reminders", refresh: "Latest reminders", back: "Back to reminders",
        pinned: "This is the report linked to this reminder, not the latest update.",
        refreshReport: "Reread this report", reportFailed: "This report couldn't be read. It may have expired or access may have changed. Retry or return to reminders; no new analysis was started.",
      },
      loadingShort: "Loading",
      paused: "Paused",
      comingSoon: "Coming soon",
      unavailable: "No data yet",
      unavailableShort: "—",
      socialUnavailable: "Waiting for the first social update",
      listTruthNotice:
        "Heat highlights changes in public ads and social discussion. It does not represent sales or market share.",
      openMonitor: "Open details for {name}",
      loadBeforeCreate:
        "Wait for this workspace's monitors to finish loading before creating a new one.",
      loadFailedNote:
        "No collection was started and the existing monitor configuration was not changed.",
      runUnavailable: "Live updates are not available for this monitor yet.",
      columns: {
        monitor: "Monitor",
        heat: "7-day heat",
        region: "Region",
        platform: "Platform",
        latestRun: "Last updated",
        social: "Latest collection",
      },
      filters: {
        monitorState: "Monitor state",
        taskType: "Monitoring schedule",
        allTaskTypes: "All monitoring schedules",
        all: "All",
        active: "Enabled",
        updating: "Collecting",
        updatingReadUnavailable: "Update progress is unavailable. Retrying.",
        paused: "Paused",
        search: "Search monitors or keywords",
        searchPlaceholder: "Search monitor name or keyword",
        region: "Filter by region",
        allRegions: "All regions",
        platform: "Filter by platform",
        allPlatforms: "All platforms",
        failed: "Update failed",
        runStatus: "Filter by latest run",
        allRuns: "All update states",
        neverRun: "Not updated yet",
        sort: "Sort monitors",
        latest: "Latest activity",
        keyword: "Keyword A–Z",
        heatUnavailable: "Heat change (building)",
        noResults: "No monitors match these filters.",
        noResultsDescription:
          "Clear the filters to return to the full keyword monitor list.",
        clear: "Clear filters",
        resultCount: "Showing {shown} of {total} monitors",
      },
      baseline: {
        building: "Trend building",
        daysObserved: "{days} days collected",
      },
      heat: {
        index: "Heat index",
        pending: "Trend building",
        unavailableHint: "More history needed",
        baselineInterpretation:
          "Keep updating this monitor and Bustly will show heat as its history grows.",
        unavailableInterpretation:
          "The first update will start building a heat trend here.",
        historyPending:
          "This update has content. Keep updating to compare heat over time.",
        meaning:
          "Heat describes change in observable public advertising and social signals relative to a historical baseline.",
        stage: {
          stable: "Observed signals are stable against the available baseline.",
          emerging:
            "Observable signals are beginning to emerge against the baseline.",
          rising:
            "Observable signals are rising against the available baseline.",
          peaking:
            "Observable signals are near a recent peak against the baseline.",
          cooling:
            "Observable signals are cooling against the available baseline.",
          baseline_building: "The trend is building.",
          insufficient_data:
            "More updates are needed before a trend can be compared.",
        },
      },
      runState: {
        complete: "Updated",
        partial: "Some content collected",
        failed: "Not updated",
        pending: "Updating",
      },
      repairState: {
        pending: "Repair pending",
        required: "Repair needed",
        not_required: "No repair needed",
        unavailable: "Repair status unavailable",
      },
      coverageMode: {
        estimated_open_world: "Estimated open world",
        enumerated: "Enumerated scope",
      },
      metrics: {
        cumulativeCollection: "Total collected",
        collectionLoading: "Loading…",
        collectionUnavailable: "Collection total unavailable",
        collectionRetry: "Retry",
        recordsObserved: "Collected in this update",
        recordsArchived: "Content saved",
        providerDeclared: "Source result count",
        pagination: "Read status",
        newAds: "New public ads",
        advertisers: "Advertisers",
        creatives: "Creatives",
        landingPages: "Landing pages",
        socialDiscussion: "Social discussion",
      },
      socialMetrics: {
        contents: "Public content",
        authors: "Unique authors",
        redditScore: "Reddit score",
        likes: "Likes",
        replies: "Replies",
        reposts: "Reposts",
        quotes: "Quotes",
        views: "Views",
        bookmarks: "Bookmarks",
      },
      socialTrend: {
        globalScope: "Global public content",
        comparable: "Trend available",
        baselineBuilding: "Trend building",
        baselineNotice:
          "This update has content. Keep updating to compare changes over time.",
        contentChange: "Related content change",
        vsPrevious: "vs previous window",
        redditSemantics: "Engagement comes from public Reddit content.",
        xSemantics: "Engagement comes from public X content.",
        observedActivity:
          "{platform} collected {contents} public items from {authors} authors.",
        listDelta: "Related content {value}",
      },
      socialEvidence: {
        redditList: "{posts} posts · {comments} comments",
        list: "{records} items collected",
        redditDetail:
          "{posts} posts and {comments} comments collected, with links to the original content.",
        detail:
          "{records} public items collected, with links to the original content.",
        observedActivity:
          "The latest {platform} update collected {records} public items.",
      },
      pagination: {
        complete: "Complete",
        partial: "Gaps remain",
      },
      days: "days",
      detail: {
        ariaLabel: "Keyword monitor details",
        tabsLabel: "Monitor details",
        tabs: {
          heat: "Overview",
          runs: "Update history",
          settings: "Details",
        },
        heat7d: "7-day heat",
        confidence: "Confidence",
        marketTotalUnknown: "Public content changes continuously",
        observedEvidence: "Collection overview",
        evidenceNotice: "See how collection volume changed over recent updates.",
        updateTime: "Updated at:",
        collectionStatus: {
          updating: "Updating",
          no_new_content: "No new content in this update",
          incomplete: "This update did not finish",
        },
        evidenceSourcesTitle: "Collected public content",
        evidenceSourcesEmpty: "Collected public content will appear here.",
        evidenceSourcesCount: "{count} items collected",
        evidenceSourcesLoading: "Loading content…",
        evidenceSourcesLoadMore: "Load more content",
        evidenceSourcesDescription:
          "Public ads, products, and social posts collected across platforms.",
        openOriginalContent: "Browse records",
        evidenceCollectingTitle: "Collecting public content",
        evidenceCollectingDescription: "You can view the content here once collection is complete.",
        evidenceReadUnavailable: "This content couldn't be shown right now. Try reading it again shortly.",
        evidenceSessionRequired: "Your sign-in has expired. Sign in again to view this content.",
        evidencePermissionDenied: "You don't have access to this content. Check your workspace permissions.",
        evidenceNoLongerAvailable: "This content is no longer available.",
        evidenceReadAgain: "Read again",
        viewSavedContent: "View saved content",
        backToOverview: "Back to overview",
        socialUnavailableNotice:
          "Posts and comment counts will appear after the first social update.",
        dataQuality: "Data status",
        contentStatus: "Content status",
        freshness: "Last updated",
        coverageRevision: "Coverage revision",
        archive: "Archive",
        compliance: "Compliance",
        plannedSlices: "Planned slices",
        completedSlices: "Completed slices",
        evidenceCountersUnavailable:
          "More category breakdowns will appear as future updates add them.",
        platformTrend: "Heat change",
        platformTrendUnavailable:
          "Complete at least two successful updates to compare content and engagement changes.",
        platformTrendPending:
          "This update has content. One more update will make change comparison available.",
        recentActivity: "Latest activity",
        recentActivityUnavailable:
          "A summary will appear when Bustly finds a noteworthy change.",
        contentSaved: "Content saved",
        contentEmpty: "No new content found",
        contentStatusUnavailable: "Update result not confirmed",
        contentNeedsAttention: "Some content will be added later",
        contentStatusPending: "Waiting for the first update",
        dataStatusNotice:
          "Public content changes continuously. This shows what the latest update actually collected.",
        technicalDetails: "View collection details",
        runCount: "Latest {count} updates",
        monitorVersion: "Monitor version",
      },
      drawerActions: {
        enableMonitor: "Enable monitor", pauseMonitor: "Pause monitor",
        runNow: 'Run now',
        edit: 'Edit', pause: 'Pause', resume: 'Resume', more: 'More monitor actions',
        resumeBeforeRun: 'Resume this monitor to update it.', resumeNotice: 'Resuming continues collection on the update schedule.', saving: 'Saving monitor changes',
        retiring: 'Retiring…', statusFailed: 'Could not change the monitor status. Try again.',
        retireFailed: 'Could not retire this monitor. Try again.',
      },
      settings: {
        edit: 'Edit monitor',
        saved: 'Monitor saved',
        comments: 'Collect related Reddit comments',
        commentThreads: 'Maximum threads per keyword',
        commentRecords: 'Comments per thread',
        commentDays: 'Comment lookback (days)',
        relatedMonitor: 'Related monitors exist. Similar keywords or websites do not require merging them.',
        scope: "Monitor scope",
        keyword: "Keywords",
        region: "Region",
        platforms: "Platforms",
        schedule: "Update schedule",
        cadence: "Update frequency",
        timezone: "Timezone",
        baselineWindow: "Baseline window",
        recordLimit: "Items per update",
        pauseTitle: "Pause this monitor",
        resumeTitle: "Resume this monitor",
        statusNotice:
          "Pausing preserves prior evidence and stops future scheduled collection.",
        statusActionUnavailable:
          "Pause and resume are disabled until the API can return every write-only scope field required for a lossless update.",
        pause: "Pause monitor",
        resume: "Resume monitor",
        retireTitle: "Retire this monitor",
        retireNotice:
          "Stops future updates and removes the monitor from active lists. Historical runs and evidence are preserved.",
        retire: "Retire monitor",
        retireConfirm:
          "Retire this monitor? Historical runs and evidence will be preserved.",
      },
      create: {
        businessSubject: "Business subject (optional)",
        businessSubjectHelp: "Name the business this monitor concerns and your relationship to it. Leave unconfirmed if unknown; this does not verify ownership or grant account access.",
        businessName: "Business name",
        businessRelationship: "Relationship",
        businessUnconfirmed: "Unconfirmed",
        businessDeclared: "Customer-declared business",
        businessRelationships: { owned: "Owned brand", agency: "Agency client", competitor: "Direct competitor", benchmark: "Category benchmark", other: "Other" },
        accountBusinessSubjects: "Account businesses (optional)",
        accountBusinessHelp: "Declare each account's business separately. The monitor's relationship is not automatically applied to these accounts.",
        title: "New intelligence monitor",
        description:
          "Name your monitor, then choose what you want to follow.",
        objectStep: "Name and type",
        monitorNamePlaceholder: "Enter a monitor name",
        monitorType: "Monitor type",
        monitorTypes: { targeted: "Targeted monitoring", social: "Social media", advertising: "Advertising", commerce: "Ecommerce", search: "Search & trends", legacy: "Mixed monitor" },
        newMonitorTitles: { social: "New social media monitor", targeted: "New targeted monitor", advertising: "New advertising monitor", commerce: "New ecommerce monitor", search: "New search & trends monitor" },
        monitorTypeDescriptions: { social: "Posts, videos and public discussion", advertising: "Track public ads and creative changes.", commerce: "Track Amazon stores and products.", search: "Track search visibility and interest." },
        creationTypes: {
          social: "Social media monitoring",
          targeted: "Targeted monitoring",
        },
        creationTypeDescriptions: {
          social: "Find keyword content across platforms.",
          targeted: "Follow communities and public accounts.",
        },
        keywordMonitorTitle: "New social media monitor",
        targetedMonitorTitle: "New targeted monitor",
        targetedMonitorStep: "Choose platforms and tracking scope",
        targetedMonitorDescription: "Choose the social platforms to monitor, then specify the communities, channels, or public accounts to follow.",
        targetedSettings: {
          reddit: {
            title: "New Reddit community monitor", step: "Community settings",
            description: "Follow posts and comments from specific communities.",
            targetLabel: "Communities to follow", targetHelp: "Enter Reddit community names, separated by commas.",
          },
          youtube: {
            title: "New YouTube channel monitor", step: "Channel settings",
            description: "Follow videos published by specific channels.",
            targetLabel: "Channels to follow", targetHelp: "Enter one YouTube channel URL per line.",
          },
          facebook: {
            description: "Follow content published by specific public accounts.",
            targetLabel: "Public accounts to follow", targetHelp: "Enter one Facebook username or public Page URL per line.",
          },
          instagram: {
            description: "Follow content published by specific public accounts.",
            targetLabel: "Public accounts to follow", targetHelp: "Enter one Instagram username or public profile URL per line.",
          },
        },
        legacyTypeNotice: "This existing monitor combines source types. Its sources, settings and history stay together; you do not need to recreate it.",
        allMonitorTypes: "All monitor types",
        advertisingPlatformStep: "Choose advertising platforms",
        searchPlatformStep: "Search sources",
        searchPlatformPlanStep: "Choose search platforms",
        googleTrends: {"help":"Relative search interest (0–100), not search counts. Each keyword is normalized independently; missing points are not zero and incomplete periods are marked.","country":"Interest region","global":"Worldwide","window":"Trend period","category":"Category","start":"Start date","end":"End date","unit":"One time series per keyword per run. Values from independently collected series cannot be directly compared across keywords.","budgetSummary":"This run allows up to {{pages}} search pages and {{series}} interest series. These are not counts of searches, links or people.","windows":{"past_hour":"Past hour","past_4_hours":"Past 4 hours","past_day":"Past day","past_week":"Past week","past_month":"Past month","past_3_months":"Past 3 months","past_year":"Past 12 months","past_5_years":"Past 5 years","all_time":"Since 2004","custom":"Custom dates"},"categories":{"0":"All categories","3":"Arts & Entertainment","5":"Computers & Electronics","7":"Finance","8":"Games","11":"Home & Garden","12":"Business & Industrial","13":"Internet & Telecom","14":"People & Society","16":"News","18":"Shopping","19":"Law & Government","20":"Sports","22":"Books & Literature","29":"Real Estate","44":"Beauty & Fitness","45":"Health","47":"Autos & Vehicles","65":"Hobbies & Leisure","66":"Pets & Animals","67":"Travel","71":"Food & Drink","174":"Science","299":"Online Communities","533":"Reference","958":"Jobs & Education"}},
        googleSearch: {
          searchNotice: "Google Search tracks result pages. Google Trends measures relative search interest; they are separate sources.",
          configurationOnly: "Configuration preview only",
          availability: "Observe saved search result pages, including links and their positions. Search results are not search-volume statistics.",
          match: "Keyword matching", matches: { ordinary: "Standard search", phrase: "Exact phrase" },
          matchHelp: "Applies to each keyword separately. Exact phrase keeps the words together.",
          country: "Search country", countryHelp: "Sets the search location, not the nationality of the people or sites returned.",
          language: "Result language", anyLanguage: "Any language",
          languageHelp: "Filters search results by language. This is not translation.",
          period: "Time window", periods: { all: "Any time", day: "Past 24 hours", week: "Past week", month: "Past month", year: "Past year", custom: "Custom dates" },
          periodHelp: "Uses Google's date filtering; dates are not independently verified publication times.",
          after: "From", before: "To",
          pages: "Maximum pages per keyword", pagesHelp: "A page contains roughly 10 results. Pages, returned links and billable items are different counts.",
          device: "Device", devices: { desktop: "Desktop", mobile: "Mobile" }, deviceHelp: "Choose the search result layout to observe.",
          domain: "Limit to one domain (optional)", domainHelp: "Enter a domain such as example.com, without a protocol, path or spaces.",
          extras: "Additional content (optional)", extrasHelp: "Off by default. Enabling either adds collection work and may increase costs when collection becomes available.",
          includeAds: "Include paid search results", includeAdsHelp: "Ads may be absent. Ad-search attempts can be charged even when no ads are returned; this does not monitor an advertiser's full campaign.",
          includeFullText: "Read result-page content", includeFullTextHelp: "Fetches the pages behind organic links. Pages may be inaccessible, and page reads are billed separately.",
          previewTitle: "Configuration review", review: "Review configuration", saveUnavailable: "Saving not available",
          notSaved: "Review the server-confirmed scope before saving. Collection starts only when run or scheduled.",
          pageSummary: "{{queries}} keywords × {{pages}} pages = up to {{total}} search pages",
          previewLimit: "This is a page allowance, not a guaranteed number of links, a market total or a cost quote. Some searches return fewer pages.",
          on: "On", off: "Off",
          trendsUnavailable: "Not connected", trendsHelp: "Google Trends will use a separate connection. Its relative interest index is not an absolute search count.",
          errors: { terms: "Add a keyword with no line breaks and no more than 32 words.", pages: "Enter a positive whole number of pages within a safe numeric range.", country: "Choose a supported search country.", language: "Choose a supported result language.", dates: "Enter valid dates with From no later than To.", domain: "Enter a single valid domain without a protocol, path or spaces.", options: "Review the selected search options." },
        },
        keywordsNotice: "Add at least one keyword. You can add related keywords to the same monitor.",
        keywordNumber: "Keyword {{number}}",
        addKeyword: "Add keyword",
        removeKeyword: "Remove keyword {{number}}",
        excludesOptional: "Exclude words (optional)",
        excludesPlaceholder: "One word or phrase per line",
        urlStep: "Store and product URLs",
        urlNotice: "Add at least one Amazon store, seller or product URL, or an ASIN. Choose a marketplace for each ASIN.",
        urlNumber: "Page URL {{number}}",
        urlTypeNumber: "Page type {{number}}",
        urlAutoType: "Detected: {{type}}",
        urlTypes: { auto: "Detect URL type", store: "Store page", product: "Product page", asin: "ASIN" },
        asinNumber: "ASIN {{number}}",
        asinMarketplace: "ASIN {{number}} marketplace",
        selectMarketplace: "Choose marketplace",
        productTargets: "Selected products",
        productSiteHelp: "Marketplaces come from each product URL or ASIN selection.",
        productCollectionHelp: "Each update checks the selected products for available product, price, stock and rating information. Review counts are not review text.",
        productVariantHelp: "The URL path selects the ASIN; repeated targets count once per marketplace. URL parameters do not select variants or add other marketplaces.",
        productUnavailable: "Product collection is not enabled here yet. You can save the scope after preview, but it will not collect until enabled.",
        productSaveHelp: "Preview the selected products and their marketplaces before saving.",
        addUrl: "Add URL",
        removeUrl: "Remove URL {{number}}",
        urlRecognitionNotice: "Check each page type. Store discovery and selected-product tracking are confirmed separately.",
        socialPlatformStep: "Social platforms",
        socialPlatformPlanStep: "Choose social platforms",
        commercePlatformStep: "Ecommerce platforms",
        commercePlatformPlanStep: "Choose ecommerce platforms",
        commercePlatformNotice: "Ecommerce monitoring currently supports Amazon. It is selected automatically.",
        urlSourcePending: "URL monitoring not connected",
        commerceReviewTitle: "Scope draft · not saved",
        commerceNotConnected: "You can review these URLs. Saving and collection will be available when URL monitoring is connected.",
        targetRegion: "Target region",
        commerceRegionNotice: "This is your intended market. The URL determines the store or product page being followed.",
        subjectType: "Object type",
        monitorName: "Monitor name",
        createdSuccess: "Monitor created",
        createdSuccessDescription: "Newly collected content will appear here.",
        primaryTerm: "Primary name",
        termsStep: "Aliases and exclusions",
        termsNotice:
          "Add one term per line or separate terms with commas. Aliases expand discovery; exclusions only remove false matches.",
        aliases: "Aliases",
        excludes: "Exclusions",
        keywordStep: "Keyword",
        keyword: "Keyword",
        keywordPlaceholder: "Enter a keyword",
        category: "Product category",
        categoryPlaceholder: "Maternity",
        regionStep: "Region",
        singleRegionNotice:
          "Choose all regions or one market. Historical evidence is compared only within the same scope.",
        allRegions: "All regions",
        platformStep: "Platforms",
        platformNotice:
          "Unavailable sources can still be saved as intended scope. Collection starts only after activation; missing coverage stays explicit.",
        sourceUnavailable: "Not enabled yet",
        sourceStatusUnknown: "Status temporarily unavailable",
        capabilitiesLoading: "Checking available channels…",
        capabilitiesFailed: "We couldn't check channel availability. Your monitors and form entries haven't changed. Try again.",
        capabilitiesRetry: "Retry channel check",
        platformComingSoon: "Coming soon",
        defaultPlatformSelection: "Default selection",
        decreaseNumber: "Decrease value",
        increaseNumber: "Increase value",
        unfilteredScopeNotice:
          "Public posts and discussions are not filtered by region. Meta Ads uses the country settings in its card. Available content does not represent complete coverage.",
        publicAccounts: "{{platform}} public accounts",
        accountScopeNotice: "Enter one public account name or profile URL per line. Only posts from these accounts are monitored; comments are not collected automatically.",
        metaCountry: "Meta country",
        amazonMarketplace: "Amazon marketplace",
        datePicker: { placeholder: "Pick a date", month: "Choose month", year: "Choose year", open: "Open calendar", clear: "Clear", today: "Today" },
        scopePlaceholder: "Select one or more",
        scopeSearch: "Search name, code or marketplace",
        languageSearch: "Search languages",
        scopeNoResults: "No matches. Try another name or code.",
        scopeSelected: "{{count}} selected",
        scopeDone: "Done",
        metaMultiScopeNotice: "Select all regions, or uncheck countries you do not need. Collection depends on the content available from the platform.",
        amazonScopeNotice: "Select multiple marketplaces. The catalog does not mean collection is enabled; check availability in the preview.",
        scopeBudgetNotice: "{{count}} / {{max}} source scopes configured (each country or marketplace counts separately). Each keyword collects within each scope. More selections increase the total limit and potential cost; check the preview.",
        runScopeBudgetNotice: "{{count}} / {{max}} source scopes configured. All keywords, sources and comments share the single-run record limit. More scopes do not increase it. Check the preview allocation; record limits do not cap fees.",
        scopeRecordLimit: "Record limit per keyword, per scope",
        runRecordLimit: "Total unique items per update (all sources, keywords and comments)",
        increaseCommentLimit: "Raise record limit to {{limit}}",
        commentRecordsAvailable: "Comments per thread: up to {{max}} with this record limit.",
        commentThreadsAvailable: "Threads per keyword: up to {{max}} with this record limit.",
        commentBudgetNoRoom: "Increase the record limit or reduce keywords, threads or comments.",
        commentThreadsRange: "Enter a whole number from 1 to 10.",
        commentRecordsRange: "Enter a whole number from 1 to 500.",
        disabledSourcesNotice: "{{platforms}} cannot collect right now. You can save these as planned scopes, but saving or selecting a marketplace does not enable a source. No new content will be returned from these sources until enabled.",
        budgetStep: "Region and schedule",
        updatePlanStep: "Update schedule",
        expandChannel: "Expand {{platform}} settings",
        collapseChannel: "Collapse {{platform}} settings",
        channelSchedule: {
          title: "Historical content",
          description: "Choose how far back to collect published content.",
          lookback: "Content publication date",
          startDate: "Start date",
          endDate: "End date",
          recordLimit: "Items per update",
          perChannelLimit: "One-time collection limit",
          perUpdateLimit: "Limit per update",
          channelLimitLabel: "{{platform}} items per update",
          xMinimumLimit: "X requires a collection limit of at least {{count}} items.",
          recordUnit: "items",
          advanced: "Advanced settings",
          advancedHelp: "Customize source, filtering, and content settings for this platform.",
          collapseAdvanced: "Hide settings",
          selectBeforeAdvanced: "Select {{platform}} first",
        },
        continuation: {
          title: "Ongoing monitoring",
          enable: "Keep monitoring new content",
          description: "When enabled, this monitor reuses the application settings above to track new content as it is published.",
          historyOnly: "Historical content only",
          window: "Duration",
          windows: {
            next_week: "1 week",
            next_half_month: "15 days",
            next_month: "1 month",
            next_three_months: "3 months",
            next_six_months: "6 months",
            next_year: "1 year",
          },
          cadenceHelp: {
            daily: "Updates daily at the time you create this monitor.",
            twice_daily: "Updates every 12 hours from the time you create this monitor.",
            weekly: "Updates weekly on the day and time you create this monitor.",
          },
        },
        channelAdvancedDescriptions: {
          meta: "Choose the ads, markets, and delivery status to watch. Turn on media, copy, or landing-page analysis only when you need it.",
          reddit: "Organize results by language and post signals. Include comments when you need the discussion behind each post.",
          youtube: "Organize video results by duration, language, and engagement.",
          facebook: "Add the public Pages to watch, then organize posts by content type, language, and engagement.",
          instagram: "Add the public accounts to watch, then organize posts by content type, language, and engagement.",
        },
        channelContentGroup: "Content to collect",
        channelCommentsGroup: "Comment scope",
        channelKeywordNotice: "Uses the keywords above. No additional settings are needed.",
        mediaType: "Ad media type",
        activeStatus: "Ad status",
        metaMode: "Discovery mode",
        metaModes: { keyword: "Keyword search", advertiser: "Specific advertisers" },
        advertiserPages: "Advertiser pages",
        advertiserPagesHelp: "Enter one Facebook Page or Ad Library advertiser URL per line. When you choose specific advertisers, the keywords above no longer filter the results.",
        advertiserPeriod: "Collection window",
        advertiserPeriodHelp: "Choose which ad publication period to check first. Actual coverage depends on what the Ad Library makes available.",
        advertiserPeriods: { all: "Any time", last24h: "Last 24 hours", last7d: "Last 7 days", last14d: "Last 14 days", last30d: "Last 30 days" },
        advertiserSort: "Collect first",
        advertiserSortHelp: "See the newest ads first, or use the source's impression order. This order does not represent sales or spend.",
        advertiserSorts: { most_recent: "Newest ads", impressions_desc: "Source impression order" },
        activeStatuses: { active: "Active ads", all: "All statuses", inactive: "Inactive ads" },
        publicDetails: "Include public ad details",
        publicDetailsHelp: "Attempts to add public reach and audience details and may use more capacity. Missing source data stays unknown.",
        postWindow: "Post lookback",
        commentHistory: {
          initial: "First lookback (days)", strategy: "How to update comments later",
          fixed: "Check the same number of days", adaptive_recheck: "Continue from the last update",
          fixedHelp: "Each update checks comments from the same recent period.", initialHelp: "The first update checks this recent period, then continues from where it stopped.",
          strategyHelp: "Choose whether to check the same recent period each time or continue from the last update.",
          overlap: "Recheck before continuing (hours)", overlapHelp: "Recheck 1–168 hours before continuing to reduce missed comments.",
          maximum: "Most days per update", maximumHelp: "Each update can check up to 30 days. Bustly asks you to confirm before using a longer period.",
          preview: "Comment history scope", summary: "Initial {{initial}} days · {{overlap}}-hour overlap · Up to {{maximum}} days",
          previewHelp: "Actual windows are calculated after posts are selected. The record budget is unchanged; this is not complete comment coverage.",
          maximumSummary: "Up to {{days}} days", days: "{{days}} days",
        },
        nativeHistory: {
          planDetails: "Initial dates: {{start}}–{{end}} · Overlap: {{overlap}} days · Up to {{maximum}} days per update",
          strategy: "Later updates", fixed: "Keep fixed dates", incremental_recheck: "Follow collection progress",
          strategyHelp: "Start with the dates above, then advance in bounded ranges. Existing gaps remain in the history.",
          overlap: "Overlap days", overlapHelp: "Recheck 1–30 days to catch late changes.",
          maximum: "Days per update", maximumHelp: "2–3,660 days. Must cover the initial range and exceed the overlap.",
          planHelp: "Dates describe the requested sample, not complete coverage. An update does not guarantee every post or video was collected.",
        },
        history: {
          confirmation: {
            review: "Review future updates", title: "Confirm future update range", savedTitle: "Update range confirmed",
            description: "Keep existing records and leave older gaps unfilled. Future updates can start within the range below; confirming does not start collection.",
            savedHelp: "Existing records and unfilled history remain. This confirmation did not start a collection; use the monitor's separate update action or existing schedule.",
            keyword: "Keyword or account", history: "Retained historical updates", initial: "Starting range", maximum: "Maximum lookback",
            dateInitial: "Initial dates", dateMaximum: "Days per update", dateOverlap: "Overlap days",
            thread: "Post", commentInitial: "Initial comment days", commentMaximum: "Maximum comment days", commentOverlap: "Comment overlap (hours)",
            budget: "Total records per update", sourceLimit: "Source record limit",
            accept: "I accept that older history stays unfilled and future updates use this range. This confirmation does not start collection.",
            confirm: "Confirm range", close: "Close", done: "Done", savedAt: "Confirmed",
            loading: "Checking range…", saving: "Saving confirmation…", recovering: "Checking confirmation…",
            uncertain: "We couldn't verify whether the confirmation was saved. Check its receipt before trying again; no collection will be started.",
            retryHelp: "The original confirmation has not been found. You can retry that same confirmation without starting collection.",
            retrySave: "Retry confirmation", check: "Check confirmation", reload: "Check range again",
            loadFailed: "We couldn't load the current range. Check again before confirming.",
            forbidden: "Your current Workspace access does not allow this action. Check your access or subscription before continuing.",
            unsupported: "This range cannot be confirmed here right now. Close this window and review the monitor settings.",
            conflict: "The range or history has changed. Check the latest range, then confirm it again.",
            storageUnavailable: "Your browser couldn't read or retain the confirmation for recovery. Allow site storage, then check again; we won't automatically retry or start collection.",
            blocks: {
              history_submit_outcome_unresolved: "An earlier submission still needs verification. Its outcome must be resolved before confirming another range.",
              history_attempt_in_flight: "An update or archive recovery is still running. Wait for it to finish, then check this range again.",
              no_unacknowledged_history: "There is no unconfirmed history in this range. No further confirmation is needed.",
            },
          },
          strategy: "Later updates",
          fixed: "Use the same lookback",
          adaptive_recheck: "Follow collection progress",
          initialHelp: "Use this range for the first collection. Comments keep their own lookback.",
          strategyHelp: "Follow progress to recheck recent content after each verified collection.",
          overlap: "Recheck overlap (hours)",
          overlapHelp: "Recheck 1–168 hours before the last verified attempt for late content and changes.",
          maximum: "Maximum lookback",
          maximumHelp: "Long pauses beyond this range require confirmation before another collection.",
          overlapSummary: "{{hours}}-hour overlap",
          preview: "Planned collection",
          previewHelp: "Rechecked before collection, within this maximum and the existing record limit. Not complete history.",
          run: "Collection scope",
          runHelp: "Saved with this update. A rolling search window, not proof that all content was collected.",
          phases: { initial_history: "First collection", history_catch_up: "Catching up on history", incremental_recheck: "Progress-based recheck", gap_recheck: "Earlier range recheck", needs_review: "Confirmation needed" },
          reasons: {
            history_time_unverified: "The last collection time needs checking before another update. Contact your administrator.",
            history_submit_outcome_unresolved: "The last submission needs checking before another collection can start. Contact your administrator.",
            history_gap_needs_review: "The earlier range reached a limit or could not be verified. Review the scope before collecting again.",
            history_window_exceeds_confirmation: "The required lookback exceeds your maximum. Review the range in monitor settings.",
            history_dispatch_window_expired: "This update waited too long to use its confirmed window. Review the scope before retrying."
          }
        },
        selectedEvidence: "Selected results",
        allEvidence: "All collected content",
        resultSelectionCounts: "{excluded} records excluded by result filters; {unknown} have unknown filter fields and are retained.",
        selectionUnknown: "Some filter fields are unknown. This record is retained, not confirmed as a match.",
        selectionExcluded: "Does not meet the result filters. Retained for reference; not used for findings.",
        subreddits: "Communities",
        subredditsHelp: "Optional. Enter community names such as Parenting or breastfeeding, separated by commas. Leave blank to include any community.",
        minPostScore: "Minimum post score",
        minPostScoreHelp: "Keep posts at or above this Reddit score. Leave blank for no minimum; posts with an unavailable score are still kept.",
        noMinimum: "No minimum",
        resultSort: "Display order",
        contentLanguages: "Content language",
        accountResultFilters: "Post filters",
        instagramDiscoveryDates: "Collection date range (optional)",
        youtubeDiscoveryHelp: "Set both dates or leave both blank. This range applies to keyword and channel discovery, not individual video links. Full coverage and exact UTC boundaries are not guaranteed.",
        instagramDiscoveryHelp: "Set both dates or leave both blank. This range is sent when collecting account posts; it does not guarantee full coverage or exact UTC boundaries.",
        discovery_start: "Collection start date",
        discovery_end: "Collection end date",
        clearDiscoveryDates: "Clear date range",
        discoveryDatesUnset: "No date range specified",
        youtubeResultFilters: "Video filters",
        amazonFilters: {
          title: "Collected product filters",
          help: "Apply per marketplace after collection. Compare prices only in the currency entered; keep unknown or different-currency values. Review totals and ratings are not review text or sales.",
          min_price: "Minimum price", max_price: "Maximum price", currency: "Price currency",
          min_rating: "Minimum rating (0–5)", min_reviews: "Minimum review count", unset: "No limit",
        },
        amazonReviews: {
          automaticLimit: "Allocate from the total",
          variantScope: "Product variants",
          allVariants: "All variants",
          currentVariant: "Current variant only",
          product: "Product information", reviews: "Review text", title: "Review collection and filters",
          limit: "Review limit per product", budgetHelp: "Shares the total per update with product information. Check the final allocation in preview.",
          published_after: "Earliest review date", published_before: "Latest review date",
          min_rating: "Minimum review stars (1–5)", max_rating: "Maximum review stars (1–5)",
          filterHelp: "Filters select collected reviews, including both date and star limits. Unknown values remain; no extra reviews are collected.",
          filterHelpWithoutDates: "Filter collected reviews by star rating. Unknown values remain; no extra reviews are collected.",
          unavailable: "Review collection is not enabled. You can save this scope, but it will not collect reviews yet.",
          allocation: "Up to {{total}} review records within the shared total.",
        },
        youtubeDuration: {
          min_duration_seconds: "Minimum duration (seconds)",
          max_duration_seconds: "Maximum duration (seconds)",
          unlimited: "No limit",
          help: "Includes both limits. Missing duration stays unknown; duration does not identify Shorts.",
        },
        youtubeResultHelp: "Select saved videos by publication date (inclusive UTC days) and the conditions below. All original content is retained; collection limits and charges stay unchanged.",
        youtubeResultHelpWithoutDates: "Choose the video duration, language, and engagement you want to see. Videos with missing details are still kept.",
        youtubeLanguage: "Title and description language",
        youtubeLanguageHelp: "Language is based on the title and description. Videos remain included when it cannot be determined.",
        youtubeMetricsHelp: "Keep videos that meet the engagement minimums. Videos remain included when the source does not provide a value. Comment counts do not include comment text.",
        metaResultFilters: "Ad filters",
        metaPresentation: {
          title: "Result display", grouping: "Display as", sort: "Sort ads by",
          ads: "Individual ads", creative: "Creative groups",
          collection_order: "Collection order", observed_desc: "Latest observed", checked_desc: "Latest checked", started_desc: "Latest ad start",
          help: "Grouping keeps every ad. Shared fingerprints are grouped within an advertiser, not by semantic similarity. Observed and checked times refer to this report window; ad start is source-reported. Missing dates sort last.",
          group: "Creative group", groupHelp: "Same advertiser and stored fingerprint. Each ad stays separate; a group may continue on another page.",
          observed: "Observed in this report", checked: "Checked in this report", started: "Source ad start",
        },
        metaResultHelp: "Choose the ad platforms, languages, and landing-page details you want to analyze. Ads with missing details are still kept.",
        metaPublishers: "Ad platforms",
        allPublishers: "All ad platforms",
        active_from: "Active range from",
        active_to: "Active range to",
        metaActiveRangeHelp: "Keep ads whose source-reported delivery dates overlap this range. These dates do not prove continuous delivery.",
        landing_domains: "Landing domains",
        landing_domainsHelp: "One domain per line, up to 20. Includes subdomains; do not enter full URLs.",
        cta_texts: "CTA text",
        cta_textsHelp: "One label per line, up to 20. Match the displayed wording, ignoring case and extra spaces.",
        accountMatching: "Matching mode",
        accountMatchModes: { all_account_posts: "All account posts", keywords: "Keywords in post text" },
        accountMatchingHelp: "With ‘Keywords in post text,’ keep posts that contain the primary keyword or an alias and do not contain an exclusion.",
        accountMediaTypes: "Content type",
        allContentTypes: "Any type",
        accountTypes: { image: "Image", video: "Video", carousel: "Carousel", reel: "Reels", text: "Text" },
        accountMediaHelp: "Choose the content types you want to see. Posts remain included when the type cannot be identified.",
        published_from: "Start date (UTC)",
        published_to: "End date (UTC)",
        accountLanguageHelp: "Language is based on the post text. Short or unclear posts remain included.",
        accountMetrics: { min_reactions: "Minimum reactions", min_likes: "Minimum likes", min_comments: "Minimum comments", min_shares: "Minimum shares", min_video_views: "Minimum video views", min_plays: "Minimum plays" },
        allLanguages: "Any language",
        contentLanguagesHelp: "Choose the languages you want to see. Content remains included when the language cannot be determined; posts and comments are checked separately.",
        resultSortHelp: "Choose which posts you see first. This does not change the collected content.",
        resultSorts: { collection_order: "Collection order", published_desc: "Newest published", score_desc: "Highest post score", comments_desc: "Most post comments" },
        resultSelectionHelp: "Use these conditions to organize collected content. Items that do not match, or lack enough information, remain available under All collected content.",
        postWindows: { from_today: "From today", past_hour: "Past hour", past_day: "Past day", past_week: "Past week", past_month: "Past month", past_year: "Past year", all_time: "All available history" },
        postWindowHelp: "The source searches this window within the collection limit. This does not guarantee all posts; comment lookback is set separately.",
        mediaTypes: { all: "All media", image: "Images", video: "Videos" },
        activeAdsOnly: "Active ads only",
        metaScopeNotice: "Choose the public Meta ads you want to review. Country or region means the ad market, not the advertiser’s location.",
        redditPostWindow: "Posts from the past week",
        redditCommentScope: "Comments: up to {{threads}} posts × {{records}} per post · past {{days}} days",
        commentsOff: "Comment collection off",
        accountCount: "{{count}} accounts · public posts",
        accountNeeded: "Add a confirmed public account to collect posts.",
        youtubeScope: "Keyword video discovery",
        youtubeMode: "Monitor by",
        youtubeModes: { keyword: "Keywords", channel: "Channel URLs", video: "Video URLs" },
        youtubeUrls: "YouTube URLs",
        youtubeUrlCount: "URLs: {{count}}",
        youtubeUrlsHelp: {
          channel: "One channel URL per line, up to 20. Use an @handle or /channel/ ID; playlists are not supported. Public keywords stay unchanged.",
          video: "One video URL per line, up to 20. Each URL collects one video; shared URLs are deduplicated. Playlists are not supported.",
        },
        plannedChannel: "Planned source only. Collection is not connected.",
        sourceWillCollect: "Ready to collect",
        sourceWillNotCollect: "Will not collect",
        previewAccounts: "Accounts",
        sourceBudget: "Planned ceiling: {{total}} records, including up to {{comments}} comments",
        allocationUnknown: "Record allocation unavailable",
        inspection: {
          title: "Media conditions", help: "Optional. Inspect a bounded sample of saved Meta Ads media before selecting assets for media analysis.",
          minSeconds: "Minimum duration (s)", maxSeconds: "Maximum duration (s)",
          minSecondsHelp: "Leave blank for no minimum. Video/audio only; up to 600 s.", maxSecondsHelp: "Leave blank for no maximum. Images have no duration filter.",
          minWidth: "Minimum width (px)", minHeight: "Minimum height (px)",
          minWidthHelp: "Width after source orientation; 1–8,192 px.", minHeightHelp: "Height after source orientation; 1–8,192 px.",
          ratios: "Display aspect ratios", ratiosHelp: "Optional; separate up to 10 ratios with commas, e.g. 16:9, 9:16. Any ratio may match. Equivalent duplicates are not allowed.",
          maxRecords: "Ads to inspect", maxAssets: "Media assets to inspect", sourceMB: "Media read limit (MB)",
          maxRecordsHelp: "1–100 saved ads per analysis version.", maxAssetsHelp: "1–100 source assets, shared across these ads.",
          sourceMBHelp: "1 MB = 1,000,000 bytes; up to 2,000 MB. Divided across assets; each asset is also capped at 20 MB.",
          limitsHelp: "These limits bound source inspection only. They do not raise the collection limit or authorize AI calls. Missing or oversized media is not replaced by unlimited refilling.",
          unrestricted: "No restriction", previewTitle: "Media inspection scope", off: "Media inspection is off.",
          scopeHelp: "Conditions select assets, not whole ads. Known mismatches are excluded from media analysis; unknown facts are retained. Original ads and independent ad copy reports are unchanged. This is a bounded sample, not full-market coverage.",
          results: "Source media details", readHelp: "Read saved source facts only—no new collection or AI call. Counts describe sampled assets, not all ads. Unknown facts stay unknown; excluding media does not remove the original ad or its text report.",
          view: "View saved media details", refresh: "Refresh from first page", loading: "Reading saved details…",
          readFailed: "Saved media details could not be read. Refresh to retry; this does not start collection.",
          executionOff: "New source inspection is not enabled for this scope. Saved details remain readable.",
          sample: "Examined {{records}} ads; {{without}} had no eligible media. Planned {{assets}} media assets.",
          asset: "Asset {{number}}", source: "View source ad", unknown: "Unknown", kind: "Media type", duration: "Source duration",
          encoded: "Encoded dimensions", oriented: "Oriented dimensions", displayRatio: "Display aspect ratio",
          noResults: "No saved media details on this page.", back: "First page", more: "Next page", conditions: "Saved conditions and limits",
          kinds: { image: "Image", video: "Video", audio: "Audio" },
          selection: { matched: "Matches conditions", excluded: "Outside conditions", unknown: "Not enough information", pending: "Awaiting inspection", unavailable: "Source not readable" },
          states: {
            disabled: "No source inspection was configured for this version.", not_started: "Source inspection has not started for this version.",
            planning: "Preparing a bounded media sample.", queued: "Source inspection is in progress; refresh to read saved progress.",
            partial: "Some sampled assets could not be inspected. Available source facts are shown below.", unavailable: "Source media details are not available for this version.",
          },
          reasons: {
            duration_below_min: "Duration below minimum", duration_above_max: "Duration above maximum", duration_unknown: "Duration unknown",
            width_below_min: "Width below minimum", width_unknown: "Oriented width unknown", height_below_min: "Height below minimum", height_unknown: "Oriented height unknown",
            aspect_ratio_excluded: "Display ratio outside selection", aspect_ratio_unknown: "Display ratio unknown",
          },
        },
        landing: {
          filtersTitle: "Product filters (optional)",
          filtersHelp: "Filter published product details, not the source ads. Products with unconfirmed conditions stay in the results.",
          systems: "Commerce systems", systemsHelp: "Choose any. Based on the page's own declaration, not a verified installation.",
          categories: "Product categories", categoriesHelp: "One category per line, up to 20. Matches the page's category text; not Meta ad categories.",
          categoriesPlaceholder: "Baby / Feeding\nAccessories", unrestricted: "Any",
          selectionSummary: "{{matched}} products match · {{unknown}} kept with unconfirmed conditions",
          excludedCount: "{{count}} products outside the selection remain in the original claims.",
          noSelectedProducts: "This page's product details are outside your selection. The original claims remain available below.",
          selectionUnknown: "Some filter conditions are unconfirmed; this product is kept.",
          selectionEvidence: "Page declarations and selection", declaredSystem: "Declared system", systemUnknown: "Not confirmed",
          originalClaims: "Original product claims ({{count}})",
          commerceStates: { pending: "Page attributes are awaiting processing. Saved product details remain available.",
            unavailable: "Page attributes could not be confirmed. Saved details remain available; this read does not recollect the page." },
          "title": "Landing-page products",
          "help": "Read product details published on the pages linked from these ads.",
          "maxRecords": "Ads to examine",
          "maxRecordsHelp": "Up to 100 ads per report, including ads whose links cannot be read.",
          "maxPages": "Pages to collect",
          "maxPagesHelp": "Up to 100 distinct pages, shared across all selected ads.",
          "sourceMegabytes": "Page content limit (MB)",
          "sourceMegabytesHelp": "Shared across pages. Each page receives a fixed share, up to 2 MB; unused space is not reassigned.",
          "previewTitle": "Landing-page collection",
          "off": "Landing-page collection is off.",
          "perPageMegabytes": "Per-page content limit (MB)",
          "quoteHelp": "Collection cost is not yet available. Content-size limits are not a money budget.",
          "results": "Landing-page products",
          "readHelp": "Read saved product details and their source ads. This does not start collection.",
          "view": "View products",
          "refresh": "Reload products",
          "loading": "Reading products…",
          "readFailed": "We couldn't read these products. Reload to try again; no collection was started.",
          "executionOff": "Automatic landing-page collection is off. Saved results remain readable.",
          "states": {
            "disabled": "Landing-page collection is off for this report.",
            "not_started": "Products will appear after landing-page collection runs.",
            "planning": "Pages are being selected for this report.",
            "queued": "Pages are being collected for this report.",
            "ready": "Saved product details will appear here.",
            "partial": "Available product details will appear here.",
            "unavailable": "Product details are not available for this report."
          },
          "pageStates": {
            "pending": "This page is awaiting collection.",
            "unconfirmed": "This attempt could not be confirmed; it will not be collected again automatically.",
            "unavailable": "This page could not be read."
          },
          "page": "Page {{number}}",
          "pagination": "Product result pages",
          "previous": "Previous page",
          "next": "Next page",
          "details": "Scope and usage",
          "sample": "{{ads}} ads examined · {{pages}} pages selected · {{archived}} pages saved",
          "notExamined": "{{count}} other ads in this report have not been examined.",
          "omissions": "{{links}} links were outside the page limit; links for {{ads}} ads could not be read.",
          "cardScope": "Card coverage is unknown for {{unknown}} ads and bounded for {{limited}} ads; {{unusable}} link references were unusable.",
          "confirmedBytes": "Confirmed saved content: {{amount}} bytes.",
          "usageUnknown": "The total is not confirmed. Confirmed saved content is not the total consumption.",
          "untrusted": "This is a bounded sample of publisher claims, not a verified product catalog.",
          "encoding": "This page's text encoding could not be interpreted.",
          "noProducts": "No structured product details were found in this saved page. It may still contain products.",
          "more": "Show all products",
          "less": "Show less",
          "sources": "Source ads ({{count}})",
          "ad": "View ad {{number}}",
          "adUnavailable": "Ad {{number}} · public link unavailable",
          "sourcePaths": "{{count}} link references",
          "moreSources": "Show all source ads",
          "parsed": "Structured-data blocks: {{parsed}} parsed of {{observed}} observed; {{invalid}} invalid.",
          "parseLimits": "Some structured data could not be interpreted or exceeded the reading limits.",
          "unnamed": "Unnamed product",
          "brand": "Brand",
          "sku": "SKU",
          "category": "Category",
          "claims": "Published prices and availability",
          "priceUnavailable": "Not provided",
          "currencyUnavailable": "currency not provided",
          "price": "Published price",
          "low_price": "Published low price",
          "high_price": "Published high price",
          "availability": "Published availability",
          "claimHelp": "These are the publisher's claims when the page was saved, not verified prices or current stock.",
          "provenance": "Evidence location: data block {{index}} · {{pointer}}",
          "availabilityValues": {
            "InStock": "In stock",
            "OutOfStock": "Out of stock",
            "PreOrder": "Preorder",
            "PreSale": "Presale",
            "BackOrder": "Backorder",
            "Discontinued": "Discontinued",
            "LimitedAvailability": "Limited availability",
            "OnlineOnly": "Online only",
            "InStoreOnly": "In-store only",
            "SoldOut": "Sold out"
          }
        },
        creative: {
          title: "Ad copy analysis", help: "Analyze saved Meta Ads text separately from images, audio and the market report.",
          dimensions: "Copy analysis focus", dimensionsHelp: "Choose one or more. Ad claims and promotional terms are not verified performance or current prices.",
          kinds: { product_feature: "Selling points", offer: "Offers", setting: "Use cases" },
          chooseDimension: "Choose at least one focus, or turn off ad copy analysis.",
          language: "Report language", languageHelp: "Applies to new copy analysis, not keyword matching.",
          maxRecords: "Ads to examine", maxRecordsHelp: "1–100 stored ads, including those with missing text.",
          maxModelCalls: "Model call limit", maxModelCallsHelp: "1–200 calls, including analysis and synthesis.",
          workUnits: "Estimated Work Units limit", workUnitsHelp: "0.01–10,000 Work Units, separate from other features.",
          previewTitle: "Ad copy analysis scope", off: "Ad copy analysis is off.",
          quoteHelp: "These are limits, not a price quote. Execution checks pricing and permissions separately; saving does not enable the analysis worker.",
          results: "Ad copy insights", view: "View saved analysis", loading: "Reading analysis…", refresh: "Refresh saved analysis",
          readHelp: "Read saved analysis and its supporting ads. This does not start a new analysis or collection.",
          readFailed: "We couldn’t read this analysis. Refresh to try again; no new analysis was started.",
          executionOff: "Automatic copy analysis is off for this report. Saved results remain readable.",
          states: { not_enabled: "Copy analysis was not selected for this report.", queued: "Copy analysis is waiting to run.",
            running: "Copy analysis is in progress.", partial: "Analysis stopped before a final summary was ready. Confirmed usage is shown below.",
            unconfirmed: "The last attempt is being checked. It won’t be repeated automatically.",
            unavailable: "A complete copy analysis isn’t available for this report. Its saved ads remain available." },
          action: "Suggested action:", evidence: "View supporting ads", closeEvidence: "Hide supporting ads",
          more: "Show all findings", less: "Show fewer findings", details: "Scope, limitations & usage",
          sample: "Examined {{examined}} ads: {{available}} with usable text, {{truncated}} truncated. {{omitted}} more were outside the record limit.",
          omittedLimitations: "{{count}} additional qualifications are outside this bounded summary. Do not treat it as exhaustive.",
          usage: "Confirmed Work Units:", usagePending: "Usage is still being reconciled; any amount shown is only the confirmed part.",
          usageUnknown: "Confirmed usage is not available yet.", usageNotStarted: "No model call is recorded for this analysis.",
          untrusted: "Based only on sampled public ad text, not verified sales, effectiveness or market share.",
          evidenceLoading: "Reading supporting ads…", evidenceFailed: "We couldn’t read all supporting ads. Try reading them again; no collection was started.",
          retry: "Read again", source: "View original ad", textUnavailable: "The saved body text is not available.",
        },
        media: {
          title: "Images & audio",
          help: "Choose each feature separately. Its limits apply to this report, independently of collection and text analysis.",
          vision: "Image & frame analysis",
          visionHelp: "Read visible text, product details, offers and settings in images or sampled video frames.",
          dimensions: "Analysis focus",
          dimensionsHelp: "Choose one or more. They share the image analysis limits below; ad claims and offers are not verified product facts or current prices.",
          dimensionLabels: { visible_text: "Visible text", product_feature: "Selling points", offer: "Offers", setting: "Scenes" },
          chooseDimension: "Choose at least one focus, or turn off image analysis.",
          dimensionPreview: "Analysis focus: {{values}}",
          transcription: "Audio transcription",
          transcriptionHelp: "Transcribe selected video clips. Clips do not represent the full video.",
          maxRecords: "Ads to examine",
          maxRecordsHelp: "Up to 100 source ads; ads without usable media still count.",
          maxAssets: "Source assets",
          maxAssetsHelp: "Up to 100 assets across these ads, not per ad.",
          sourceMb: "Source size limit (MB)",
          sourceMbHelp: "1 MB = 1,000,000 bytes. Divided across assets, up to 20 MB each; unused capacity is not redistributed.",
          maxModelCalls: "Model calls",
          maxModelCallsHelp: "Allow at least one call per asset and sample; up to 200 calls.",
          workUnits: "Estimated Work Units limit",
          workUnitsHelp: "Set an independent limit for this feature. This is not a quote and does not cap collection fees.",
          frames: "Frame positions (seconds)",
          framesHelp: "Enter 1–5 ascending positions, separated by commas, between 0 and 600 seconds. Images use one sample.",
          clips: "Audio clips",
          clipsHelp: "Add 1–5 ordered, non-overlapping clips within the first 600 seconds. Each clip can be up to 60 seconds.",
          start: "Start (seconds)",
          duration: "Length (seconds)",
          addClip: "+ Add clip",
          removeClip: "Remove clip {{index}}",
          previewTitle: "Media analysis scope",
          off: "Off",
          framePreview: "Video frames at {{values}} seconds.",
          clipPreview: "Audio clips: {{values}} seconds.",
          quoteHelp: "Saving sets the scope; execution requires workspace enablement. Work Units are quoted at execution, not on this preview.",
          results: "Image & audio results",
          readHelp: "Read a fixed collection version, updated independently of the text report. Refreshing here does not start analysis.",
          view: "View stored results",
          refresh: "Refresh results",
          loading: "Reading stored results…",
          readFailed: "We couldn't load these results. Refresh to try again; no new analysis was started.",
          notStarted: "Results will appear after this feature runs.",
          disabled: "This feature is off for this report.",
          pending: "Analysis is in progress.",
          partial: "Available results are shown below.",
          unavailable: "No usable media result was recorded for this report.",
          ready: "Analysis recorded.",
          executionOff: "Automatic media execution is not enabled for this report. Saved results remain readable.",
          sample: "Sample at {{seconds}} seconds",
          source: "View source ad",
          more: "Next results",
          back: "First results",
          usage: "Recorded Work Units",
          usageUnknown: "Usage is still being confirmed.",
          usagePending: "Confirmed so far; not a final total.",
          noResults: "No analysis samples are available on this page.",
          limitations: "Notes",
          untrusted: "Analysis is based on sampled public media, not verified sales or market share.",
        },
        reportSchedule: {
          label: "Report frequency",
          per_update: "After content updates", daily: "Once a day", weekly: "Once a week",
          help: {
            per_update: "Generate reports after new content is collected, when analysis is enabled.",
            daily: "After 00:00 UTC, if new content is available and analysis is enabled.",
            weekly: "After Monday 00:00 UTC, if new content is available and analysis is enabled.",
          },
        },
        reportNotification: {
          label: "Report notifications", off: "Off", ready: "When a report is ready",
          help: "Notify the Workspace Owner and Admins about new reports only. Automatic analysis and workspace notifications must be enabled; saving this preference does not enable them.",
        },
        analysisBudget: {
          title: "AI analysis",
          help: "Applies to each new analysis of collected content. These limits do not change collection settings or enable workspace analysis.",
          mode: "Automatic analysis",
          legacy: "Keep existing behavior", disabled: "Off", enabled: "Use analysis limits",
          legacyHelp: "This monitor has no saved analysis limit. Existing behavior is unchanged; choose limits to set a budget for future analyses.",
          disabledHelp: "This plan turns off future automatic analysis once saved. Collected content and existing reports remain available.",
          maxRecords: "Content limit", maxRecordsHelp: "1–5,000 items, selected in report order. All collected originals are retained.",
          maxModelCalls: "Model call limit", maxModelCallsHelp: "1–200 calls, including intermediate analysis and report synthesis.",
          workUnits: "Estimated Work Unit limit", workUnitsHelp: "0.01–10,000 Work Units, up to two decimal places. Collection fees are separate.",
          previewTitle: "AI analysis plan",
          quoteHelp: "These are configured limits, not a quote. Each call is checked against an estimate before it starts; actual usage may differ. Execution also requires workspace analysis to be enabled.",
        },
        consumption: {
          title: "Per-update allocation",
          initialJobs: "Initial job ceiling",
          allocationHelp: "Collection and comments share the record ceiling. Details are not counted again.",
          collection: "Ads, posts & products",
          comments: "Comments",
          enrichment: "Public Meta details",
          analysis: "AI analysis",
          records: "Up to {{count}} records",
          unquoted: "Not estimated",
          jobs: "Up to {{count}} initial jobs",
          jobsUnknown: "Initial jobs not estimated",
          shared_collection_budget: "Within the shared ceiling",
          included_in_collection: "Enriches the same ads; no extra job",
          separate_unquoted: "Separately enabled and billed",
          costHelp: "Fees are not quoted. Record limits do not cap fees. Initial jobs exclude follow-up checks and retries and cannot be used to calculate charges.",
          unavailable: "An itemized collection plan is not available in this preview.",
        },
        runImmediately:
          "Start the first collection after saving. This may incur charges.",
        logicalSlices: "Logical slices",
        physicalRequests: "Physical requests",
        maximumRecords: "Maximum records",
        estimatedCost: "Estimated run cost",
        costUnknown: "Cost unknown",
        recurringCost: "Estimated recurring cost",
        unavailableSources: "Saved gaps",
        globalScopeNotice:
          "Reddit and X currently monitor global public content. Your selected region is still used for business analysis and display.",
        platformDescriptions: {
          meta: "Public ad activity",
          tiktok: "Public creative activity",
          reddit: "Public discussion signals",
          x: "Recent public conversations",
        },
        available: "Available",
        runtimeUnavailable: "Runtime unavailable",
        comingSoon: "Coming soon",
        advanced: "Update options",
        baselineNotice:
          "Trends start with the first update. Further updates let you compare changes in heat.",
        preview: "Next (preview)",
        previewing: "Reviewing monitor settings",
        previewReady: "Scope is ready for confirmation",
        reviewTitle: "Review monitor settings",
        reviewReadyTitle: "Settings checked and ready",
        reviewReadyDescription: "Review the monitoring targets, sources, and update plan before creating this monitor.",
        reviewReadyEditDescription: "Review the monitoring targets, sources, and update plan before saving these changes.",
        reviewMonitorName: "Monitor name",
        reviewNewBadge: "New monitor",
        reviewChangeBadge: "Monitor changes",
        reviewTargets: "Monitoring targets",
        reviewKeywords: "Keywords",
        reviewSources: "Monitoring sources",
        reviewCadence: "Update frequency",
        reviewRecordLimit: "Items per update",
        reviewRecords: "Up to {{count}} items",
        reviewAdvancedSettings: "Advanced settings",
        reviewAllCommunities: "Any community",
        reviewEngagementMinimums: "Engagement minimums",
        reviewVideoDuration: "Video duration",
        reviewEnabled: "On",
        reviewDisabled: "Off",
        reviewNotLimited: "No limit",
        reviewSeconds: "{{count}} seconds",
        reviewStartsNow: "The first collection will start immediately after creation.",
        reviewStartsLater: "The first collection will not start immediately. Future collections will follow the update plan.",
        reviewScopeTitle: "Monitoring scope",
        reviewCollectionTitle: "Collection plan",
        reviewCollectionDescription: "Review the collection limit for each update and the settings used for each platform.",
        reviewCreateHelp: "Nothing is saved until you select Start monitoring.",
        reviewEditHelp: "Saved changes apply to the next update.",
        backToEdit: "Back to edit",
        confirmSave: "Confirm and save",
        creatingMonitor: "Creating monitor",
        savingChanges: "Saving changes",
        previewScope:
          "{slices} public sources confirmed, with up to {records} items per update.",
        previewWarnings: {
          provider_scope_all_while_business_region_preserved:
            "A selected source observes globally while the chosen region remains the business analysis region.",
          reddit_comments_are_separately_budgeted:
            "Reddit will collect posts together with their related comments.",
        },
        confirmCreate: "Start monitoring",
        creating: "Creating",
        create: "Create monitor",
      },
      loading: "Loading market intelligence",
      workspaceRequiredTitle: "Select a workspace to continue",
      workspaceRequiredDescription:
        "Market Intelligence is scoped to one workspace. Choose a workspace from the sidebar before loading monitors.",
      unavailableTitle:
        "Market Intelligence isn't available for this workspace",
      unavailableDescription:
        "Market Intelligence is not enabled for this workspace yet. No update was started.",
      loadFailedTitle: "We couldn't load Market Intelligence",
      summaryDisplayUnavailable: "Some details couldn't load",
      summaryDisplayRetry:
        "Some monitor details couldn't load. Retry from the monitor list; this won't start a new collection.",
      loadFailedDescription:
        "The workspace data couldn't be confirmed. Try again before creating or running a monitor.",
      tryAgain: "Try again",
      emptyTitle: "Keyword monitors",
      emptyDescription:
        "Create a monitor to follow new activity around your brand, competitors, and market across ads, social media, commerce, and search.",
      createFirst: "New monitor",
      monitors: "Monitors",
      keywordsShort: "keywords",
      active: "Active",
      runNow: "Run now",
      collecting: "Updating",
      runtime: {
        loading: "Checking whether real-data collection is available.",
        simulated:
          "This workspace is showing demo data, which is not used for final intelligence conclusions.",
        collection_disabled:
          "Real-data collection is not enabled yet. Contact the Bustly team before running this monitor.",
        workspace_not_allowed:
          "Market Intelligence collection is not enabled for this workspace. Contact the Bustly team to enable it.",
        execution_mode_not_allowed:
          "Only demo data is available. Contact the Bustly team to enable live updates.",
        unavailable:
          "Collection availability could not be confirmed. Try again shortly.",
        retry: "Check again",
      },
      keywordPanel: "Keyword panel",
      fixedPanel: "Fixed for this monitor version",
      lastSuccessful: "Last successful run",
      metaPublicAds: "Public Meta ad evidence",
      coverage: "Coverage",
      observedOpenMarket: "Observed open market",
      universeUnknown: "Total matching inventory is unknown",
      latestRun: "Latest update",
      latestRunDescription: "The latest saved update for this monitor.",
      queryReference: "Query ID",
      executionMode: {
        provider: "Public data",
        fake: "Demo data",
        pending: "Preparing",
      },
      noRunTitle: "No updates yet",
      noRunDescription: "Updates will appear here after collection starts.",
      runHistory: "Update history",
      runHistoryDescription:
        "Review when each update ran, where it looked, and what it collected.",
      loadingRunHistory: "Loading update history",
      runHistoryLoadFailed:
        "Update history couldn't be loaded. The monitor configuration is unchanged.",
      collectionInProgress: "Update in progress",
      collectionInProgressDescription:
        "Bustly is collecting the latest public content. This may take a moment.",
      evidenceObserved: "Evidence observed",
      publicAdRecords: "public ad records returned",
      recordsObserved: "Observed",
      recordsArchived: "Archived",
      archiveComplete: "Archive complete",
      archiveIncomplete: "Archive has gaps",
      archiveUnknown: "Archive completeness unknown",
      complianceFresh: "Compliance state current",
      complianceStale: "Compliance state needs refresh",
      complianceUnknown: "Compliance freshness unknown",
      readySummary: "This update is complete",
      partialSummary:
        "Some content was collected and the rest will be added in a future update",
      openWorldDisclaimer:
        "Public content changes continuously. Heat indicates direction, not total market size or sales.",
      freshness: "Freshness",
      planVersion: "Plan version",
      budgetLimited: "Budget limit affected this run",
      runFailedTitle: "The run couldn't be confirmed",
      runError: {
        title: "We couldn't confirm the update result",
        description: "Check update records again shortly. Collected content is still available.",
        details: "Error details",
      },
      runFailedDescription:
        "This update did not finish. Collected content and monitor settings are preserved.",
      checkAgain: "Check existing run",
      drawerTitle: "Create keyword monitor",
      drawerDescription:
        "Choose the keyword, market, and public sources you want Bustly to follow.",
      close: "Close",
      cancel: "Cancel",
      saving: "Saving",
      saveMonitor: "Save monitor",
      form: {
        name: "Monitor name",
        namePlaceholder: "Enter a monitor name",
        category: "Product category",
        categoryPlaceholder: "For example, Maternity",
        region: "Market",
        mediaType: "Media type",
        keywords: "Keywords",
        keywordsPlaceholder: "Enter a keyword",
        keywordsHint:
          "Enter the main phrase customers would use to describe this topic or product.",
        cadence: "Update frequency",
        cadenceHint:
          "Choose how often Bustly should look for new public activity.",
        recordLimit: "Items per update",
        recordLimitHint:
          "Controls how much content each update collects without affecting saved history.",
        coverageNotice:
          "Results come from public content and indicate change direction, not total market size.",
      },
      media: {
        all: "All media",
        image: "Images",
        video: "Videos",
      },
      cadence: {
        manual: "Manual updates only",
        daily: "Once a day",
        twice_daily: "Twice daily",
        weekly: "Once a week",
      },
      subjectType: {
        brand: "Brand",
        product: "Product",
        topic: "Topic",
        ad_creative: "Ad creative",
      },
      status: {
        accepted: "Accepted",
        running: "Running",
        ready: "Ready",
        partial: "Partial",
        failed: "Failed",
      },
      unknowns: {
        coverage_not_reconciled: "Coverage not reconciled",
        universe_size_unknown: "Market size unknown",
        collection_gaps_remain: "Collection gaps remain",
        slice_collection_incomplete: "Some keyword slices are incomplete",
        archive_incomplete: "Evidence archive is incomplete",
        compliance_not_fresh: "Compliance state is not current",
        compliance_freshness_unknown: "Compliance freshness is unknown",
        baseline_not_ready: "Baseline not ready",
        insufficient_history: "Historical window is incomplete",
        paid_signal_unavailable: "Paid signal unavailable",
        organic_signal_unavailable: "Organic signal unavailable",
        coverage_incomplete: "Coverage is incomplete",
        target_collection_scope_unbound: "Target collection scope is not bound",
        paid_coverage_unavailable: "Ad coverage evidence is unavailable",
        organic_coverage_unavailable: "Social coverage evidence is unavailable",
        social_baseline_incomplete: "Social trend baseline is incomplete",
        collection_bounded_by_limit:
          "Collection reached its configured safety limit",
        other: "Additional coverage uncertainty",
      },
      watchTargets: {
        title: "Brand and creative heat targets",
        description:
          "Define provider-neutral targets now. Heat appears only after enough paid and organic observations build a comparable baseline.",
        newTarget: "New target",
        loading: "Loading watch targets",
        loadFailed:
          "Watch targets couldn't be loaded. Existing monitor data is unchanged.",
        empty:
          "No heat targets yet. Add a brand, product, or ad creative to start building a baseline.",
        targetType: "Target type",
        displayName: "Display name",
        category: "Category",
        region: "Market",
        language: "Language",
        identifier: "Stable identifier",
        identifierNotice:
          "Bustly generates this identifier to link the same public content across observations.",
        save: "Save target",
        saveFailed:
          "The target wasn't saved. Your inputs are still here; try again.",
        validationRequired: "Enter a display name and stable identifier.",
        loadingHeat: "Loading seven-day heat state",
        heatFailed: "Heat state couldn't be confirmed. No score is shown.",
        sevenDayWindow: "Seven-day signal window",
        confidence: "Confidence",
        noPrematureScore:
          "Bustly will not invent a score before coverage and historical baseline requirements are met.",
        types: {
          brand: "Brand",
          product: "Product",
          adCreative: "Ad creative",
        },
        stages: {
          baseline_building: "Baseline building",
          insufficient_data: "Insufficient comparable data",
          stable: "Stable",
          emerging: "Emerging",
          rising: "Rising",
          peaking: "Peaking",
          cooling: "Cooling",
        },
        unknowns: {
          baseline_not_ready: "Baseline not ready",
          insufficient_history: "Historical window is incomplete",
          paid_signal_unavailable: "Paid signal unavailable",
          organic_signal_unavailable: "Organic signal unavailable",
          coverage_incomplete: "Coverage is incomplete",
          other: "Additional signal uncertainty",
        },
      },
      validation: {
        keyword_required: "Enter a keyword.",
        region_required: "Select a region.",
        platform_required: "Select at least one available platform.",
        platform_unavailable:
          "One or more selected platforms are not currently available.",
        name_required: "Enter a monitor name.",
        primary_term_required: "Add at least one keyword.",
        facebook_keyword_too_long: "Facebook keywords must be at most 100 characters each.",
        duplicate_terms: "A keyword or exclusion is repeated. Keep each term once.",
        commerce_url_required: "Add at least one Amazon store or product URL, or an ASIN.",
        commerce_url_invalid: "Use a supported Amazon HTTPS URL, or a 10-character ASIN with its marketplace. Check that the input type matches.",
        media_type_invalid: "Choose all media, images or videos for Meta Ads.",
        commerce_url_duplicate: "The same page appears more than once. Remove the duplicate URL.",
        commerce_type_required: "Choose a page type for each URL that could not be identified.",
        commerce_url_limit: "Add up to 20 URLs per monitor.",
        business_subject_invalid: "Enter the business name and choose a relationship, or select Unconfirmed. Nothing has been saved.",
        media_budget_invalid: "Check each enabled feature’s limits and sample positions. Calls must cover all assets and samples; nothing has been saved.",
        media_inspection_invalid: "Check media duration, ratios, dimensions and inspection limits. Nothing has been saved.",
        creative_budget_invalid: "Choose an ad copy focus and check its language, record, call and Work Units limits. Nothing has been saved.",
        landing_budget_invalid: "Check the ad, page and content-size limits. Nothing has been saved.",
        analysis_budget_invalid: "Enter 1–5,000 items, 1–200 model calls and 0.01–10,000 Work Units (up to two decimal places), or turn automatic analysis off. Nothing has been saved yet.",
        report_schedule_invalid: "Choose a report frequency and preview again. Nothing has been saved yet.",
        notification_config_invalid: "Choose a report notification option and preview again. Nothing has been saved yet.",
        monitoring_period_invalid: "Choose a valid monitoring start and end date. The end date cannot be earlier than the start date.",
        commerce_budget_insufficient: "The total limit must cover every selected product. Increase the limit or remove products.",
        target_already_monitored:
          "This object is already monitored. Open the existing monitor instead of creating a duplicate.",
        category_required: "Enter a product category.",
        keywords_required: "Add at least one keyword.",
        too_many_keywords:
          "This controlled preview supports one keyword per monitor.",
        record_limit_invalid:
          "Set a whole-number safety ceiling between 1 and 5000.",
        x_record_limit_invalid: "Set X’s collection limit to a whole number between 50 and 5000.",
        instagram_keyword_limit_invalid: "Instagram keyword monitoring supports up to 500 posts per update. Set the collection limit to a whole number between 1 and 500.",
        comment_fields_invalid: "Check Reddit comments: 1–10 threads, 1–500 comments per thread and 1–30 lookback days, all whole numbers.",
        channel_options_invalid: "Check the selected channel options, then preview again. Nothing has been saved.",
        collection_history_invalid: "Use 1–168 whole overlap hours. The maximum lookback must cover both the initial range and the overlap. Nothing has been saved.",
        comment_history_invalid: "Use 1–168 whole overlap hours and up to 30 comment days. The maximum must cover the initial lookback and exceed the overlap by more than 5 minutes. Nothing has been saved.",
        native_history_invalid: "Set valid initial dates, 1–30 whole overlap days and 2–3,660 days per update. The update range must cover the initial dates and exceed the overlap. Nothing has been saved.",
        instagram_discovery_dates_invalid: "Set both Instagram collection dates, with the start before the end, or clear both. Nothing has been saved.",
        youtube_discovery_dates_invalid: "Set both YouTube collection dates, with the start before the end, or clear both. Nothing has been saved.",
        youtube_url_invalid: "Enter 1–20 YouTube URLs matching the selected mode, one per line. Playlists are not supported.",
        reddit_community_required: "Add at least one Reddit community to follow.",
        facebook_account_required: "Add at least one Facebook public account to follow.",
        instagram_account_required: "Add at least one Instagram public account to follow.",
        advertiser_url_required: "Enter 1–100 valid Facebook Page or Ad Library advertiser URLs, one per line.",
        comment_budget_exceeded: "The record limit is too low. Reduce threads or comments per thread.",
        countries_required: "Select at least one Meta country. All regions cannot be combined with specific countries.",
        marketplaces_required: "Select at least one valid Amazon marketplace.",
        social_account_required: "Add a public Facebook or Instagram account to monitor, one username or profile link per line. Post links are not account links.",
        too_many_source_scopes: "Each monitor currently supports up to {{max}} source scopes. Select fewer countries, marketplaces or platforms. Your inputs are preserved.",
        keyword_condition_unconfirmed: "We couldn’t confirm that every word is required. Try the preview again.",
        preview_failed:
          "We couldn't preview this monitor. Your inputs are still here; try again. Nothing has been saved or collected in this step.",
        submission_failed:
          "We couldn't confirm the saved monitor. Your inputs are still here. Close this dialog and refresh the list to check before trying again.",
      },
    },
    pluginInstall: {
      label: "Use BizSidekick in AI apps",
      description:
        "Install BizSidekick to use it in ChatGPT, Claude, WorkBuddy, and more.",
      sidebarHint: "Open here anytime to copy prompts for your AI apps.",
      button: "Get BizSidekick Plugin",
    },
    pluginSetup: {
      close: "Close",
      title: "Get BizSidekick ready in the AI app you already use",
      inviteSuccessTitle: "You’re in. Install BizSidekick in your AI apps",
      chooseApp: "Choose an AI app",
      stepCopyTitle: "Copy prompt, then paste in {{app}}",
      stepAuthorizeTitle: "Send prompt to open authorization",
      screenshotPlaceholderAlt: "Product screenshot placeholder for {{app}}",
      screenshotPlaceholderTitle: "{{app}} product screenshot",
      screenshotPlaceholderDescription: "Product screenshot placeholder · 16:7",
      screenshotAlt:
        "{{app}} message input showing where to paste the BizSidekick prompt",
      copyButton: "Copy prompt for {{app}}",
      copied: "Prompt copied",
      helper:
        "Paste and send the prompt to open the authorization page in your browser.",
      copyError: "We couldn't copy the prompt. Please try again.",
    },
    pricing: {
      title: "Pricing for Bustly",
      pluginTitle: "Pricing for BizSidekick",
      pluginPlanName: "BizSidekick",
      pluginSoloPlanDescription:
        "Ask @BizSidekick once in ChatGPT, Claude, or WorkBuddy to analyze and handle approved business tasks across your connected stores and accounts.",
      pluginTeamPlanDescription:
        "Ask @BizSidekick once in ChatGPT, Claude, or WorkBuddy to help your team coordinate and handle approved business tasks across your connected stores and accounts.",
      pluginWorkspaceConnectionsTitle: "Workspace & connections",
      pluginFeatureUser: "1 workspace user",
      pluginFeatureTeamUsers: "Up to {{count}} workspace users",
      pluginFeatureConnections:
        "Up to 10 total connections, including up to 2 stores",
      pluginFeatureManageConnections: "Manage connections and permissions",
      pluginFeatureAgent:
        "Let the Agent handle business tasks within the permissions you grant",
      pluginFeatureReview:
        "Review what the Agent accessed and changed in Bustly Admin",
      pluginFeatureTeamCollaboration:
        "Slack, Feishu, and DingTalk integrations included",
      pluginAiPlatformsTitle: "Use BizSidekick in your AI apps",
      pluginPlanUnavailable:
        "This plan is temporarily unavailable. Try again in a moment.",
      pluginRestoreAutoRenewal: "Renew",
      pluginRenewSubscriptionTitle: "Renew BizSidekick?",
      pluginRenewSubscriptionDescription:
        "This will turn auto-renewal back on for your BizSidekick subscription. It takes effect immediately.",
      pluginConfirmRenewal: "Renew subscription",
      pluginSubscriptionRenewedToast: "BizSidekick subscription renewed.",
      pluginFailedToRenewSubscription:
        "We couldn't renew your BizSidekick subscription. Please try again.",
      andMore: "And more",
      mostPopular: "Most Popular",
      custom: "Custom",
      perMonth: "/mo",
      processing: "Processing...",
      contactSales: "Contact Sales",
      viewPlans: "View plans",
      contactSalesCta: "Contact Sales →",
      unavailable: "Unavailable",
      availableSoon: "Available soon",
      startSubscription: "Start subscription",
      activatePaidPlan: "Activate Paid Plan",
      switchToSolo: "Switch to Solo",
      switchToTeam: "Switch to Team",
      switchPlan: "Switch plan",
      currentPlan: "Current Plan",
      switchToYearly: "Switch to Yearly",
      switchToMonthly: "Switch to Monthly",
      upgradeToTeam: "Upgrade to Team",
      upgrade: "Upgrade",
      downgrade: "Downgrade",
      downgradeScheduled: "Downgrade scheduled",
      pluginRenewAndSwitchToPlanTitle: "Renew and switch to {{plan}}?",
      pluginRenewAndSwitchToPlanDescription:
        "Your current subscription will renew. {{plan}} will take effect on {{date}}.",
      pluginRenewAndSwitchToPlanDescriptionWithoutDate:
        "Your current subscription will renew. {{plan}} will take effect at your next renewal.",
      pluginRenewAndSwitchToPlan: "Renew and switch",
      pluginRenewAndScheduledPlanTakesEffectOn:
        "Renew to switch to {{plan}} on {{date}}.",
      renewAndSwitchToPlanTitle: "Renew and switch to {{plan}}?",
      renewAndSwitchToPlanDescription:
        "Your current subscription will renew. {{plan}} will take effect on {{date}}.",
      renewAndSwitchToPlanDescriptionWithoutDate:
        "Your current subscription will renew. {{plan}} will take effect at your next renewal.",
      renewAndSwitchToPlan: "Renew and switch",
      renewAndScheduledPlanTakesEffectOn:
        "Renew to switch to {{plan}} on {{date}}.",
      currentPlanActiveUntil:
        "Your current {{plan}} plan remains active until {{date}}.",
      scheduledPlanTakesEffectOn: "{{plan}} will take effect on {{date}}.",
      planWithBillingCycle: "{{plan}} {{cycle}}",
      monthly: "Monthly",
      yearly: "Yearly",
      groupWorkspaceWorkUnits: "Workspace & Work Units",
      groupTrainingIntegrations: "Support & integrations",
      teamExclusiveBadge: "Team Exclusive",
      comparePlansTitle: "Compare plans & features",
      comparisonFeature: "Key limits",
      comparisonWorkspace: "Workspace",
      comparisonWorkspaceUsers: "Workspace users",
      comparisonBrandGroups: "Projects",
      comparisonUpTo: "Up to {{value}}",
      comparisonStores: "Store connection limit",
      comparisonConnections: "App connections",
      comparisonSlackAndFeishu: "Slack, Feishu & DingTalk",
      comparisonTotalConnections: "Total connection limit (including stores)",
      comparisonWorkUnits: "Work Units & tasks",
      comparisonMonthlyWorkUnits: "AI Operator Work Units per month",
      comparisonEstimatedTasks: "Tasks handled per month (estimated)",
      comparisonSupported: "Supported",
      comparisonNotSupported: "Not supported",
      notIncluded: "Not included",
      monthlyLower: "monthly",
      yearlyLower: "yearly",
      tenPercentOff: "10% OFF",
      cancelSubscription: "Cancel subscription",
      resubscribe: "Resubscribe",
      editBilling: "Edit billing",
      updatePaymentMethod: "Update payment method",
      paymentFailedTitle: "Payment didn't complete",
      paymentFailedDescription: "Your plan is not active. Update your payment method to try again.",
      paymentPendingTitle: "Payment is still being confirmed",
      paymentPendingDescription: "Your plan is not active yet. Refresh this page in a moment for the latest status.",
      soloName: "Solo",
      teamName: "Team",
      team1499Name: "Team (10 users)",
      teamFiftyUsersName: "Team (50 users)",
      frontendTierPlanName: "{{plan}} ({{value}} Work Units)",
      team1499UserLabel: "10 users",
      teamUnlimitedUserLabel: "50 users",
      scaleName: "Scale",
      soloDescription:
        "For founders and solo operators hiring their first AI operator.",
      teamDescription:
        "For companies rolling out AI operators across the workspace.",
      scaleDescription:
        "For larger organizations rolling out AI operators at scale.",
      soloFeatureOne: "1 workspace user included",
      soloFeatureStoreLimit: "1 store connection included",
      soloFeatureThree: "Available 24/7",
      soloFeatureFour:
        "9,780 AI Operator Work Units per month for lighter daily operations",
      creditTopUps: "On-demand Work Unit top-ups",
      teamFeatureOneStarter: "10 workspace users included",
      teamFeatureOne: "50 workspace users included",
      teamFeatureThree: "Available 24/7",
      teamFeatureFour: "Up to 10 store connections included",
      teamFeatureFiveStarter:
        "20,980 shared AI Operator Work Units per month for growing team operations",
      teamFeatureFive:
        "69,980 shared AI Operator Work Units per month for higher team task volume",
      selectSoloTier: "Select Solo option",
      selectTeamTier: "Select Team option",
      workUnitsPerMonth: "{{value}} Work Units / month",
      workUnitsToBeConfirmed: "Work Units to be confirmed",
      oneUserShort: "1 user",
      usersShort: "{{value}} users",
      oneStoreShort: "1 store",
      storesShort: "{{value}} stores",
      unlimitedUsersShort: "Unlimited users",
      unlimitedStoresShort: "Unlimited stores",
      customStoresShort: "Custom stores",
      unlimited: "Unlimited",
      customStoreConnectionsIncluded: "Custom store connections",
      userLimitShortTbd: "User limit TBD",
      storeLimitShortTbd: "Store limit TBD",
      workspaceUsersIncluded: "{{value}} workspace users included",
      connectionsIncluded:
        "Up to {{total}} connections, including up to {{stores}} store{{storePlural}}",
      teamCollaborationIntegrationsIncluded:
        "Slack, Feishu & DingTalk integrations included",
      brandGroupIncluded: "Up to {{value}} Business group included",
      brandGroupsIncluded: "Up to {{value}} Projects included",
      unlimitedBrandGroupsIncluded: "Unlimited Projects",
      storeConnectionsIncluded: "Up to {{value}} store connections included",
      unlimitedWorkspaceUsersIncluded: "Unlimited workspace users included",
      unlimitedStoreConnectionsIncluded: "Unlimited store connections included",
      workspaceUserLimitTbd: "Workspace user limit to be confirmed",
      storeConnectionLimitTbd: "Store connection limit to be confirmed",
      workUnitsFeature:
        "{{value}} AI Operator Work Units per month for lighter daily operations",
      sharedWorkUnitsFeature:
        "{{value}} shared AI Operator Work Units per month for higher team task volume",
      workUnitsFeatureTbd: "Monthly AI Operator Work Units to be confirmed",
      sharedWorkUnitsFeatureTbd:
        "Monthly shared AI Operator Work Units to be confirmed",
      builtInAiCapabilities: "Built-in AI capabilities",
      createImagesWith: "Create images with {{model}}",
      createVideosWith: "Create videos with {{model}}",
      handleBusinessTasksWith: "Handle business tasks with {{model}}",
      includedIntegrations: "Supported apps",
      estimatedSoloOperationsPerMonth:
        "Handles about {{min}}–{{max}} routine operational tasks per month",
      estimatedTeamOperationsPerMonth:
        "Handles about {{min}}–{{max}} team operational tasks per month",
      estimatedTaskCountTooltip:
        "Actual task counts depend on task complexity, data volume, and how often Bustly reads data from connected apps or performs write actions in them.",
      recordedCoursesIncluded:
        "On-demand video courses to help you get started with Bustly",
      weeklyLiveSessionsIncluded:
        "Weekly live sessions on practical ways to use Bustly",
      onsiteTrainingIncluded:
        "Onsite training to help your team deploy and implement Bustly, valued at $500",
      estimatedScaleOperations: "Estimated capacity tailored to your workflows",
      previewTierUnavailableDescription:
        "This option is not available for purchase yet. Choose another option to continue.",
      configuredTierUnavailableDescription:
        "This option is not available for purchase yet. Choose another option to continue.",
      scaleFeatureOne: "Unlimited workspace users included",
      scaleFeatureTwo: "Unlimited store connections included",
      scaleFeatureThree: "Flexible deployment and support",
      scaleFeatureFour: "Custom monthly AI Operator Work Units",
      scaleFeatureFive: "Tailored workflow design",
      noWorkspaceSelected: "No workspace selected",
      planNotFound: "Plan not found",
      failedToStartPaidCheckout: "Failed to start paid checkout",
      failedToStartCheckout: "Failed to start checkout",
      failedToOpenBillingPortal: "Failed to open billing portal",
      activatePaidPlanConfirmTitle: "Activate {{plan}}?",
      activatePaidPlanConfirmDescriptionPrefix:
        "You are about to activate the paid ",
      activatePaidPlanConfirmDescriptionMiddle: " plan on a {{cycle}}",
      activatePaidPlanConfirmDescriptionSuffix:
        " billing cycle. Your trial will end now and the paid subscription will take effect immediately.",
      upgradeToPlanTitle: "Upgrade to {{plan}}",
      upgradeToPlanDescriptionPrefix: "You are about to upgrade to the ",
      upgradeToPlanDescriptionMiddle: " plan on a {{cycle}}",
      upgradeToPlanDescriptionSuffix:
        " billing cycle. The upgrade will take effect immediately, and you will be charged a prorated amount for the remainder of your current billing cycle.",
      downgradeToPlanTitle: "Downgrade to {{plan}}",
      downgradeToPlanDescriptionPrefix: "You are about to downgrade to the ",
      downgradeToPlanDescriptionSuffix:
        " plan. This change will take effect at the end of your current billing cycle. You will keep your current features until then.",
      switchBillingTitle: "Switch to {{cycle}} Billing",
      switchBillingDescription:
        "Switching to {{cycle}} billing will take effect at the next renewal. You will continue to have access to your current plan until the end of your current {{currentCycle}} cycle.",
      cancelSubscriptionTitle: "Cancel subscription?",
      cancelSubscriptionPendingPaymentDescription:
        "If you cancel now, Shopify will charge only for days used and apply a credit or refund for unused time.",
      cancelSubscriptionActivePaidDescription:
        "After you confirm, auto-renewal will turn off. This subscription is not refunded. Your plan access and Plan Work Units stay active until this billing cycle ends.",
      confirmCancelSubscription: "Cancel subscription",
      resubscribeTitle: "Resubscribe?",
      resubscribeDescription:
        "You'll go to Shopify to approve a new subscription for this plan.",
      confirmResubscribe: "Resubscribe",
      subscriptionPendingPaymentCanceledToast: "Subscription canceled.",
      subscriptionActivePaidCanceledToast:
        "Subscription canceled. Current plan benefits stay active until the cycle ends.",
      failedToCancelSubscription:
        "We couldn't cancel your subscription. Please try again.",
      openingShopifySubscriptionApprovalToast:
        "Opening Shopify to approve your subscription.",
      renewSubscription: "Renew Subscription",
      renewSubscriptionDescriptionPrefix:
        "You are about to renew your current subscription. This will resume ",
      autoRenewal: "auto-renewal",
      renewSubscriptionDescriptionSuffix: " for your current plan.",
      confirmChange: "Confirm Change",
      confirmRenewal: "Confirm Renewal",
      manage: "Manage",
      trialPlanUpdatedToast:
        "Plan updated. You can activate the paid plan now, or keep the trial until it ends.",
      planChangeScheduledToast:
        "Plan change scheduled. It will take effect at your next renewal.",
      planUpgradedToast: "Plan upgraded successfully.",
      subscriptionRenewedToast: "Subscription renewed successfully.",
      subscriptionCreatedToast: "Subscription created successfully.",
      failedToRenewSubscription: "Failed to renew subscription.",
      failedToCreateSubscription: "Failed to create subscription.",
    },
    welcomeModal: {
      nextPulse: {
        title: "Don’t miss tomorrow’s Pulse",
        description:
          "Upgrade your Bustly plan to receive a daily Pulse at 7:00 AM. Your first free Pulse remains available.",
        later: "Maybe later",
        upgrade: "Upgrade",
        imageAlt: "Welcome",
      },
      workspaceReady: {
        checkingTitle: "Preparing your cloud workspace...",
        readyTitle: "Workspace is ready",
        checkingDescription:
          "We are starting your cloud agent and connecting it to your workspace. This usually takes a few minutes.",
        adminPageDescription:
          "This page is for workspace settings, teammates, integrations, and billing.",
        desktopName: "Bustly Desktop",
        desktopDescriptionSuffix:
          " is where you start tasks and review the work.",
        imageAlt: "Workspace is ready",
        caption: "Open Bustly Desktop to start your first task.",
        pluginCaption:
          "Use BizSidekick in ChatGPT, Claude, WorkBuddy, and more.",
        waitingInstance: "Waiting for your instance to become ready...",
        openDesktop: "Open Bustly Desktop",
        gotIt: "Got it",
        downloadQuestion: "Don’t have Bustly Desktop?",
        downloadApp: "Download the app",
      },
      inviteSuccess: {
        title: "You’re in",
        caption: "Download Bustly Desktop to start your first task.",
        downloadDesktop: "Download Bustly Desktop",
        alreadyHaveDesktop: "Already have Bustly Desktop?",
        openApp: "Open the app",
      },
    },
    paymentSuccess: {
      title: "Payment successful",
      pluginSubscriptionSummary: "Your BizSidekick subscription is active.",
      pluginSubscriptionSummaryWithPlan:
        "Your BizSidekick {{plan}} subscription is active.",
      pluginLineUseApps:
        "Use BizSidekick in ChatGPT, Claude, WorkBuddy, and more.",
      pluginLineConnections: "Manage up to 10 connections, including 2 stores.",
      pluginLineManageConnections:
        "Manage connections and permissions in Admin.",
      pluginTeamLineUsers: "Add up to {{count}} workspace users.",
      pluginTeamLineUnlimitedUsers: "Add unlimited workspace users.",
      pluginTeamLineCollaboration:
        "Connect Slack, Feishu, and DingTalk for team collaboration.",
      pluginTeamLineConnections:
        "Manage up to {{connections}} connections across {{stores}} stores.",
      gotIt: "Got it",
      subscriptionSummary: "This workspace is now subscribed to Bustly.",
      subscriptionSummaryWithPlan:
        "This workspace is now subscribed to Bustly {{plan}}.",
      subscriptionLineOpenDesktop: "Start tasks in Bustly Desktop",
      subscriptionLineUsePlan: "Use this workspace plan for daily operations",
      subscriptionLineManageBilling: "Manage billing from Billing & Plans",
      teamLineInvite: "Invite teammates to this workspace",
      teamLineConnect: "Connect multiple stores, accounts, and apps",
      teamLineManageRoles: "Manage roles and access controls",
      soloLineOpenDesktop: "Start tasks in Bustly Desktop",
      soloLineUseCredits:
        "Use monthly AI Operator Work Units for daily operations",
      soloLineBuyCredits: "Buy extra Work Units when needed",
      creditsDescription:
        "Your purchased Work Units have been added to this workspace and are ready to use.",
      close: "Close payment success",
      openDesktop: "Open Bustly Desktop",
      downloadQuestion: "Don’t have Bustly Desktop?",
      downloadApp: "Download the app",
    },
    waiting: {
      paymentReturn: {
        title: "Finalizing your payment...",
        message:
          "Updating your plan and billing details. This usually takes a few seconds.",
        errorMessage: "Payment is still processing",
        continueAction: "Continue",
      },
      desktopOAuth: {
        title: "Signing in to Bustly...",
        message:
          "Finishing the secure handoff to Bustly Desktop. This usually takes a few seconds.",
        retryAction: "Retry",
        missingContext:
          "The Bustly Desktop sign-in handoff is missing. Please restart login from Bustly Desktop.",
        missingWorkspace:
          "The workspace for this sign-in could not be found. Please restart login from Bustly Desktop.",
        missingCallbackUrl:
          "Bustly Desktop did not return the sign-in handoff. Please try again from the desktop app.",
        invalidCallbackUrl:
          "The sign-in handoff could not be verified. Please try again from Bustly Desktop.",
      },
    },
    pricingContactSales: {
      title: "Talk to Sales",
      description: "Get a custom demo and pricing for your specific needs.",
      successTitle: "Thank you!",
      successSubtitle: "Your request has been sent.",
      successMessage:
        "Our team will review your information and get back to you within 24 hours to schedule a personalized demo.",
      close: "Close",
      firstName: "First Name",
      firstNamePlaceholder: "First name",
      lastName: "Last Name",
      lastNamePlaceholder: "Last name",
      workEmail: "Work Email",
      companyName: "Company Name",
      companyNamePlaceholder: "Your company",
      jobTitle: "Job Title",
      jobTitlePlaceholder: "e.g. CEO, Marketing Director",
      companySize: "Company Size",
      companySizePlaceholder: "Select size",
      companySizeOptions: {
        oneToTen: "1-10 employees",
        elevenToFifty: "11-50 employees",
        fiftyOneToTwoHundred: "51-200 employees",
        twoHundredOneToFiveHundred: "201-500 employees",
        fiveHundredPlus: "501+ employees",
      },
      brandCount: "Number of Projects/Stores",
      brandCountPlaceholder: "Select count",
      primaryUseCase: "Primary Use Case",
      primaryUseCasePlaceholder: "Select use case",
      primaryUseCaseOptions: {
        marketAnalysis: "Market Analysis & Insights",
        contentCreation: "Content Creation",
        productResearch: "Product Research",
        competitorTracking: "Competitor Tracking",
        other: "Other",
      },
      needs: "Anything else we should know?",
      needsPlaceholder: "Tell us about your specific needs...",
      submitRequest: "Submit Request",
      legalText:
        "By submitting this form, you agree to our Terms of Service and Privacy Policy.",
      submitFailed: "Failed to send request. Please try again later.",
      errors: {
        firstNameRequired: "First name is required",
        lastNameRequired: "Last name is required",
        emailRequired: "Email is required",
        emailInvalid: "Please enter a valid email address",
        companyNameRequired: "Company name is required",
        companySizeRequired: "Please select company size",
        brandCountRequired: "Please select number of projects/stores",
        primaryUseCaseRequired: "Please select a primary use case",
      },
    },
    shopifyConnectionEntry: {
      title: "Connect Shopify",
      checking: "Checking how to connect Shopify…",
      error: "We couldn’t load the Shopify connection options. Try again or close this window.",
      retry: "Try again",
      adminGuide: "In Shopify, select the review store where Bustly AI is installed. Go to Apps and open Bustly AI to continue connecting.",
      developmentInstallGuide: "Select a development store in Shopify to install Bustly AI. If it is already installed, Shopify may open the app directly.",
      appStoreGuide: "Install Bustly AI from the Shopify App Store, then follow the instructions in Shopify to connect your store.",
      openAdmin: "Open Shopify",
      installApp: "Install Bustly AI",
      popupWaiting: "Finish connecting in the Shopify window. This page will continue automatically once your store is connected.",
      popupReopen: "Reopen Shopify window",
      popupError: "The Shopify connection didn’t finish. Close this window and try again in Bustly.",
      popupDone: "Your Shopify store is connected. You can close this window and return to Bustly.",
      popupClose: "Close window",
    },
    connectShopify: {
      title: "Bring Bustly into the store.",
      subtitle:
        "Pick the platform and share the storefront URL so Bustly can learn the business.",
      connect: "Connect",
      checking: "Checking...",
      storeUrlLabel: "Shopify Store URL",
      storeUrlPlaceholder: "Your store URL",
      storeUrlTooltip:
        "Login to your Shopify and navigate down to Domains section.",
      findUrlTitle: "Find your myShopify URL",
      findUrlDescriptionPrefix: "In Shopify, go to",
      findUrlDescriptionMiddle: ", then navigate to",
      domains: "Domains",
      stepLabel: "Step {{count}}",
      screenshotPlaceholder: "Screenshot placeholder",
      closeImagePreview: "Close image preview",
      helpStepOneAlt: "Shopify settings page showing where to open Domains",
      helpStepTwoAlt: "Shopify domains page showing the myShopify URL location",
      devStates: "Dev states:",
      defaultRemainingTrialText: "3 days",
      validation: {
        alreadyBound: {
          title: "Connection failed.",
          body: "This store is already bound to another workspace. A store can only be connected to one workspace at a time.",
        },
        trialValid: {
          title: "Existing trial detected.",
          body: "This store already has an active trial. After connecting to this workspace, it will continue with the remaining {{remainingTrialText}} only.",
        },
        trialExpired: {
          title: "Trial already used.",
          body: "This store has already consumed its free trial. Trial time cannot be reissued across workspaces. Continue to billing to connect this store with a paid plan.",
        },
      },
      connectFailedRetry: "Connect to Shopify failed. Please try again.",
      validStoreNameRequired: "Please enter a valid Shopify store name",
      failedToConnectStore:
        "Failed to connect to Shopify store. Please try again.",
      invalidStoreUrl: "Invalid Shopify store URL",
      storeNameRequired: "Please enter your Shopify store name",
      validStoreUrlRequired:
        "Please enter a valid Shopify store URL (e.g., yourstore.myshopify.com)",
      storeNameTooShort: "Store name must be at least 3 characters long",
      storeNameInvalidChars:
        "Store name can only contain letters, numbers, and hyphens",
      storeNameHyphenBoundary: "Store name cannot start or end with a hyphen",
    },
    integrationConnectGuides: {
      referenceImageComingSoon: "Reference image coming soon",
      placeholderImageAlt: "Connection guide reference image",
      shopify: {
        title: "Find your myShopify URL",
        description:
          "In Shopify, go to Settings, then open Domains to find the myShopify URL for this store.",
      },
      bigCommerce: {
        title: "Prepare your BigCommerce details",
        description:
          "Have your Store Hash and Account UUID ready before you connect.",
        steps: {
          openControlPanel: {
            title: "Open BigCommerce",
            description:
              "Open the control panel and confirm the store you want to connect.",
          },
          findStoreHash: {
            title: "Find the Store Hash",
            description: "Copy the Store Hash from the store URL or API Path.",
          },
          findAccountUuid: {
            title: "Find the Account UUID",
            description:
              "Copy the Account UUID from the account-level API account or API Path.",
          },
        },
      },
      feishu: {
        title: "Prepare your Feishu custom app",
        description:
          "Have your App ID, App Secret, and app permissions ready before you connect.",
        steps: {
          openPlatform: {
            title: "Open Feishu Open Platform",
            description:
              "Create or open the custom app you want Bustly to use.",
            imageAlt:
              "Feishu Open Platform custom app list with create buttons highlighted.",
          },
          copyCredentials: {
            title: "Copy app credentials",
            description:
              "Copy the App ID and App Secret from Credentials & Basic Info.",
            imageAlt:
              "Feishu app created screen showing App ID and App Secret fields.",
          },
          addPermissions: {
            title: "Add app permissions",
            description:
              "Add the permissions this workspace needs, then publish or approve the app if required.",
            imageAlt:
              "Feishu Permissions and Scopes page showing added permission scopes.",
          },
        },
      },
      dingtalk: {
        title: "Prepare your DingTalk internal app",
        description:
          "A DingTalk enterprise administrator must create and publish an internal app before users can authorize Bustly.",
        steps: {
          createInternalApp: {
            title: "Create an internal app",
            description:
              "In DingTalk Open Platform, create an internal enterprise app and copy its Client ID and Client Secret.",
            imageAlt:
              "Sanitized English DingTalk Developer Platform app list with Create Application highlighted.",
          },
          configureCallback: {
            title: "Configure the OAuth callback",
            description:
              "Add the callback URL shown by Bustly to the app's OAuth redirect URL allowlist.",
            imageAlt:
              "Sanitized English DingTalk Security Settings page with the OAuth callback field highlighted.",
          },
          enablePermissions: {
            title: "Enable and publish access",
            description:
              "Enable Docs, Online Sheets, and AI Tables access as needed, then publish the app for the enterprise.",
            imageAlt:
              "Sanitized English DingTalk Permission Management page showing document, sheet, and AI table permissions.",
          },
        },
      },
      dingtalkMcp: {
        title: "Connect DingTalk MCP",
        description:
          "Choose a service in DingTalk, enable it, then copy its MCP configuration and paste it below.",
        openMarketplace: "Open DingTalk MCP Marketplace",
        steps: {
          chooseService: {
            title: "Choose a service",
            description:
              "Open the MCP Marketplace and select the DingTalk service you want Bustly to use.",
            imageAlt:
              "Sanitized English DingTalk MCP Marketplace with a service card highlighted.",
          },
          enableService: {
            title: "Enable the service",
            description:
              "Open the service details, select Enable, and finish the DingTalk authorization steps.",
            imageAlt:
              "Sanitized English DingTalk MCP service page with the Enable button highlighted.",
          },
          copyConfiguration: {
            title: "Copy the MCP configuration",
            description:
              "Select Get MCP Configuration and copy one full URL or the JSON Config for multiple services.",
            imageAlt:
              "Sanitized English DingTalk MCP configuration dialog with credentials redacted and copy actions highlighted.",
          },
        },
      },
      attribuly: {
        title: "Prepare your Attribuly API key",
        description:
          "Create an API key in Attribuly settings, then paste it into Bustly. The key is used only to read data available to your current plan.",
        openAttribuly: "Open Attribuly",
        steps: {
          openSettings: {
            title: "Open developer settings",
            description:
              "In Attribuly, go to Settings, then open Developer & Domain.",
          },
          createKey: {
            title: "Create an API key",
            description:
              "Open API keys, select Create API key, and copy the full key before closing the screen.",
          },
          pasteKey: {
            title: "Paste into Bustly",
            description:
              "Paste the key and add a label teammates can recognize. Do not share the key in chat or screenshots.",
          },
        },
      },
      ahrefs: {
        title: "Prepare your Ahrefs API key",
        description:
          "Create or copy an API key so Bustly can sync Ahrefs data.",
        steps: {
          openApiKeys: {
            title: "Open API keys",
            description: "Go to Ahrefs Account settings, then open API keys.",
            imageAlt: "Ahrefs account settings API keys page.",
          },
          createKey: {
            title: "Create a key",
            description:
              "Create a new API key. Workspace owners and admins can manage keys.",
            imageAlt: "Ahrefs Generate API key dialog with key title input.",
          },
          pasteKey: {
            title: "Paste the key into Bustly",
            description:
              "Add a clear label so teammates can recognize this connection.",
            imageAlt: "Ahrefs generated API key dialog with copy button.",
          },
        },
      },
      omnisend: {
        title: "Prepare your Omnisend API key",
        description:
          "Create an API key with the data permissions this workspace needs.",
        steps: {
          openApiKeys: {
            title: "Open API keys",
            description: "Go to Omnisend Store Settings, then open API keys.",
            imageAlt: "Omnisend Store settings API keys page.",
          },
          createKey: {
            title: "Create a key",
            description: "Create a new API key and name it for Bustly.",
            imageAlt: "Omnisend Create API key permissions screen.",
          },
          copyKey: {
            title: "Copy the key",
            description:
              "Copy the key before closing the screen, then paste it into Bustly.",
            imageAlt: "Omnisend Copy API key screen with copy button.",
          },
        },
      },
      okendo: {
        title: "Prepare your Okendo API credentials",
        description:
          "Create or copy your Okendo User ID and API Key from Settings > Integrations > Credentials, then paste each value into its matching Bustly field.",
        steps: {
          openCredentials: {
            title: "Open integration credentials",
            description:
              "In Okendo, go to Settings, Integrations, then Credentials.",
            imageAlt:
              "Okendo Integration Settings page with Integrations selected and the edit button highlighted.",
          },
          copyUserIdAndApiKey: {
            title: "Copy User ID and API Key",
            description:
              "Select the Okendo app you want Bustly to use, then copy both the User ID and API Key.",
            imageAlt:
              "Okendo Credentials panel showing User ID and Merchant REST API Key copy controls.",
          },
          pasteCredentials: {
            title: "Paste into Bustly",
            description:
              "Paste the User ID and Merchant REST API Key into their separate fields, then add a label teammates can recognize.",
            imageAlt:
              "Bustly Okendo connection dialog showing separate User ID and Merchant REST API Key fields.",
          },
        },
      },
      prestashop: {
        title: "Prepare your PrestaShop Webservice key",
        description:
          "Enable Webservice in PrestaShop, create a key, and grant the resource permissions this workspace needs.",
        steps: {
          openWebservice: {
            title: "Open Webservice settings",
            description:
              "In PrestaShop, open Advanced Parameters, then Webservice.",
            imageAlt:
              "PrestaShop Webservice settings page with Advanced Parameters and Add new webservice key highlighted.",
          },
          createKey: {
            title: "Create a Webservice key",
            description:
              "Add a new key, enable it, choose the resource permissions, and save.",
            imageAlt:
              "PrestaShop New webservice key page with generated key, permissions, and Save button.",
          },
          copyKey: {
            title: "Copy the key into Bustly",
            description:
              "Confirm Webservice is enabled, copy the key, then paste it into Bustly.",
            imageAlt:
              "PrestaShop Webservice list showing an enabled Bustly Integration key.",
          },
        },
      },
      goaffpro: {
        title: "Prepare your GoAffPro Admin token",
        description:
          "Create a merchant Admin token in GoAffPro, then paste it into Bustly. Public marketplace data needs no additional credential.",
        steps: {
          openApiKeys: {
            title: "Open API Keys",
            description:
              "In the merchant app, go to Settings > Advanced > API Keys.",
            imageAlt:
              "GoAffPro Developer settings with the Access Tokens section highlighted.",
          },
          copyAdminAccessToken: {
            title: "Copy the Admin token",
            description:
              "Create a token or copy the X-GOAFFPRO-ACCESS-TOKEN value.",
            imageAlt: "GoAffPro Access Token panel with token values redacted.",
          },
          pasteAdminAccessToken: {
            title: "Paste into Bustly",
            description:
              "Paste the Admin token, then add a label your teammates can recognize.",
            imageAlt:
              "Bustly GoAffPro connection form with the Admin Access Token field.",
          },
        },
      },
      uppromote: {
        title: "Prepare your UpPromote API key",
        description:
          "Open UpPromote Integration settings, get the API key, then paste it into Bustly.",
        steps: {
          openIntegrationSettings: {
            title: "Open Integration settings",
            description:
              "In UpPromote, go to Settings, then open the Integration tab.",
            imageAlt:
              "UpPromote Integration settings showing the Advanced API Key section.",
          },
          getApiKey: {
            title: "Get the API key",
            description:
              "Under Advanced, find API Key and select Get API Key to view or create it.",
            imageAlt: "UpPromote API key dialog with the credential redacted.",
          },
          pasteApiKey: {
            title: "Paste into Bustly",
            description:
              "Paste the API key, then add a label teammates can recognize.",
            imageAlt:
              "Bustly UpPromote connection form with the API Key field.",
          },
        },
      },
      loox: {
        title: "Prepare your Loox API credentials",
        description:
          "Copy the Public Store ID and a Secret API Key from Loox, then paste both values into Bustly.",
        steps: {
          openApiKeys: {
            title: "Open API keys",
            description: "In Loox, go to Settings, then open API keys.",
            imageAlt:
              "Loox Settings page with the API keys tab selected and credentials redacted.",
          },
          copyCredentials: {
            title: "Copy both credentials",
            description:
              "Copy the Public Store ID, then generate or copy a Secret API Key.",
            imageAlt:
              "Loox Public Store ID and Secret API Key panels with credentials redacted.",
          },
          pasteCredentials: {
            title: "Paste into Bustly",
            description:
              "Paste each value into its matching field, then add a recognizable label.",
            imageAlt:
              "Bustly Loox connection form with Public Store ID and Secret API Key fields.",
          },
        },
      },
      trustwill: {
        title: "Prepare your TrustWILL API key",
        description:
          "Open TrustWILL Developer tools, copy the private API key, then paste it into Bustly.",
        steps: {
          openDeveloperTools: {
            title: "Open Developer tools",
            description:
              "In TrustWILL, go to Preferences, then open Developer tools.",
            imageAlt:
              "TrustWILL Preferences with Developer tools selected and the API key redacted.",
          },
          copyApiKey: {
            title: "Copy the API key",
            description:
              "Under API Key, copy the private API key and keep it secure.",
            imageAlt:
              "TrustWILL API Key panel with the private credential redacted.",
          },
          pasteApiKey: {
            title: "Paste into Bustly",
            description:
              "Paste the API key, then add a label teammates can recognize.",
            imageAlt:
              "Bustly TrustWILL connection form with the API Key field.",
          },
        },
      },
      noxinfluencer: {
        title: "Prepare your NoxInfluencer Skills API Key",
        description:
          "Open the Skills Dashboard, create or copy a Skills API Key, then paste it into Bustly.",
        openSkillsDashboard: "Open Skills Dashboard",
        steps: {
          openSkillsDashboard: {
            title: "Open the Skills Dashboard",
            description:
              "In NoxInfluencer, open AI Marketing, then Skills Dashboard.",
            imageAlt: "NoxInfluencer Skills Dashboard.",
          },
          createSkillsApiKey: {
            title: "Create or copy a Skills API Key",
            description:
              "Under Skills API Keys, create a key or copy an existing one. Each sub-account can have one key.",
            imageAlt: "NoxInfluencer Skills API Key list.",
          },
          pasteSkillsApiKey: {
            title: "Paste into Bustly",
            description:
              "Paste the Skills API Key, then add a label teammates can recognize.",
            imageAlt:
              "Bustly NoxInfluencer connection form with the Skills API Key field.",
          },
        },
      },
      figma: {
        title: "Prepare your Figma personal access token",
        description:
          "Create a Figma personal access token with the file and library permissions this workspace needs.",
        steps: {
          openSettings: {
            title: "Open Figma settings",
            description:
              "In Figma, open Account settings, then go to Security.",
          },
          createToken: {
            title: "Create a token",
            description:
              "Create a personal access token and select the REST API scopes this workspace needs.",
          },
          pasteToken: {
            title: "Paste the token into Bustly",
            description:
              "Copy the token before closing Figma, then paste it here with a recognizable label.",
          },
        },
      },
      semrush: {
        title: "Prepare your Semrush API key",
        description:
          "Copy your Semrush API key so Bustly can access the account data you authorize.",
        steps: {
          openSubscription: {
            title: "Open subscription info",
            description:
              "In Semrush, open the subscription information page for the account you want to connect.",
          },
          copyApiKey: {
            title: "Copy the API key",
            description:
              "Find the API key included with your Semrush plan and copy it.",
          },
          pasteApiKey: {
            title: "Paste it into Bustly",
            description:
              "Paste the API key below and add an optional label your team will recognize.",
          },
        },
      },
      wooCommerce: {
        title: "Prepare your WooCommerce REST API keys",
        description:
          "Create a Consumer Key and Consumer Secret for the store you want Bustly to connect.",
        steps: {
          openRestApi: {
            title: "Open REST API settings",
            description:
              "In WordPress, go to WooCommerce, Settings, Advanced, then REST API.",
          },
          createKey: {
            title: "Create a key",
            description:
              "Add a key for Bustly and grant the read/write permissions your workflows need.",
          },
          pasteCredentials: {
            title: "Paste into Bustly",
            description:
              "Paste the store URL, Consumer Key, and Consumer Secret into their matching fields.",
          },
        },
      },
      magento: {
        title: "Prepare your Adobe Commerce access token",
        description:
          "Create an integration in Adobe Commerce and copy its access token for Bustly.",
        steps: {
          openIntegrations: {
            title: "Open integrations",
            description:
              "In Adobe Commerce Admin, go to System, Extensions, then Integrations.",
          },
          activateIntegration: {
            title: "Create and activate",
            description:
              "Create a Bustly integration, select the required API resources, then activate it.",
          },
          pasteAccessToken: {
            title: "Paste the access token",
            description:
              "Paste the store domain, store code, and generated access token into Bustly.",
          },
        },
      },
    },
    workspaceSwitcher: {
      basic: "Basic",
      manage: "Manage",
      expired: "Expired",
      trial: "Trial",
      startSubscription: "Start subscription",
      upgrade: "Upgrade",
      selectWorkspace: "Select Workspace",
    },
    userMenu: {
      login: "Login",
      homepage: "Homepage",
      user: "User",
    },
    profileSettings: {
      failedToUpdatePassword: "We couldn't update your password. Try again.",
      updatePassword: "Change password",
      newPassword: "New Password",
      enterNewPassword: "Enter new password",
      hidePassword: "Hide password",
      showPassword: "Show password",
      passwordUpdatedSuccessfully: "Password updated successfully!",
      updating: "Updating...",
      confirm: "Confirm",
      selectImageFile: "Please select an image file",
      imageSizeTooLarge: "Image size must be less than 5MB",
      uploadFailed: "Upload failed",
      failedToUploadAvatar: "Failed to upload avatar",
      profileAlt: "Profile",
      uploading: "Uploading...",
      change: "Change photo",
      name: "Name",
      email: "Email",
      password: "Password",
    },
    settingsModal: {
      account: "My account",
      general: "General",
      generalDescription: "General settings and preferences",
      language: "Language",
      languageDescription: "Language for this interface",
      workspace: "Workspace",
      workspaceAccumulation: "Workspace knowledge",
      workspaceNavigation: "Project management",
      brandManagement: "Project management",
      workspaceRecord: "Workspace Record",
      profileDescription: "Personal information and security",
      workspaceSettings: "Workspace Settings",
      workspaceSettingsDescription: "Workspace details and preferences",
      workspaceSkills: "Workspace Skills",
      workspaceSkillsDescription:
        "Review reusable ways of working learned from completed tasks",
      integrations: "Integrations",
      adminIntegrations: "Apps & connections",
      integrationsDescription: "Connect your tools to Bustly",
      brands: "Projects",
      brandsDescription: "Organize Resources and member access by Project",
      users: "Users",
      adminUsers: "Members & roles",
      workspaceMembers: "Workspace members",
      usersDescription: "Manage team access",
      valueLedger: "Outcome Summary",
      outcomeArchive: "Outcomes",
      valueLedgerDescription: "Review workspace outcome summaries and value",
      audit: "Audit log",
      auditDescription: "Review cloud Agent Run events",
      billingAndPlans: "Billing & Plans",
      billingDescription: "Manage subscription and invoices",
      usage: "Usage",
      billingAndUsage: "Plans & usage",
      brandOverview: "Project overview",
      usageDescription: "Monitor Work Units and task usage",
      downloads: "Downloads",
      downloadsDescription: "Download Bustly Desktop",
      saving: "Saving...",
      brandsSettingsLegacy: {
        title: "Projects",
        subtitle:
          "Give each Agent task a clear business scope across stores, ad accounts, analytics, and teammates.",
        brands: "Projects",
        brand: "Project",
        brandDirectory: "Project directory",
        brandDirectoryDescription:
          "A Project groups the exact connection accounts and people an Agent may use for that business.",
        assignedConnections: "Scoped connections",
        needsAssignment: "Needs assignment",
        connections: "Connections",
        users: "Users",
        status: "Status",
        action: "Action",
        ready: "Ready",
        needsAttention: "Needs attention",
        manage: "Manage",
        createBrand: "Create project",
        createBrandDescription:
          "Start with the project identity. You can assign connection accounts and user access next.",
        create: "Create project",
        cancel: "Cancel",
        brandName: "Project name",
        brandNamePlaceholder: "For example, your project name",
        websiteOptional: "Website (optional)",
        newBrandDescription: "New business scope",
        noDomain: "Website not added",
        brandNotFound: "This project is no longer available.",
        backToBrands: "Back to Projects",
        allBrands: "All projects",
        tabs: {
          overview: "Overview",
          connections: "Connections",
          users: "Users",
        },
        brandSections: "Project sections",
        brandSummary: "Project summary",
        brandSummaryDescription:
          "Review the resources the Agent can use when this project is selected.",
        manageConnections: "Manage connections",
        manageUsers: "Manage users",
        agentScope: "Agent scope",
        agentScopeTitle: "{{brand}} is a distinct Agent scope",
        agentScopeDescription:
          "When this ships, tasks started in this Project will use only its assigned accounts plus connections shared with all Projects.",
        setupChecklist: "Setup checklist",
        addBrandDetails: "Add project details",
        assignConnectionAccounts: "Assign connection accounts",
        grantUserAccess: "Grant user access",
        connectionAccounts: "Connection accounts",
        connectionAccountsDescription:
          "Assign the smallest available account, store, property, or advertiser—not the whole integration.",
        connectionAccount: "Connection account",
        application: "Application",
        brandScope: "Project scope",
        connectedBy: "Connected by",
        connectedAt: "Connection time",
        assignedBy: "Assigned by",
        connectionDetails: "Connection details",
        connectionHistory: "Connection history",
        viewDetails: "View details",
        formerWorkspaceUser: "Former workspace user",
        notAssigned: "Not assigned",
        connectionStatus: {
          connected: "Connected",
          "needs-reauthorization": "Reconnect required",
          "sync-issue": "Sync issue",
        },
        addConnections: "Add connections",
        addConnectionsDescription:
          "Choose unassigned connection accounts for {{brand}}. Shared accounts remain available to every Project.",
        noUnassignedConnections: "There are no unassigned connection accounts.",
        assignSelected: "Assign selected",
        unassignedConnections: "Unassigned connections",
        unassignedConnectionsDescription:
          "{{count}} connection accounts are still visible at Workspace level and need a Project.",
        reviewConnections: "Review assignments",
        userAccess: "User access",
        userAccessDescription:
          "Workspace roles define what people can do. Project access defines where they can do it.",
        user: "User",
        workspaceRole: "Workspace role",
        brandAccess: "Project access",
        hasAccess: "Has access",
        noAccess: "No access",
        manageAccess: "Manage access",
        manageUserAccess: "Manage user access",
        ownerAccessFixed: "Owner · All projects",
        ownerAccessFixedDescription:
          "Workspace owners always have access to every Project.",
        allBrandsAccessDescription:
          "This user can work across every current and future Project.",
        selectedBrands: "Selected projects",
        selectedBrandsAccessDescription:
          "This user can work only in the Projects selected below.",
        chooseBrands: "Choose projects",
        saveAccess: "Save access",
        allAccessibleBrands: "All accessible Projects",
        scopeResourceContractUnavailable:
          "Project Resources are temporarily unavailable. Please try again.",
        simple: {
          directoryTitle: "Projects",
          directoryDescription:
            "Create a separate Project space for each business in this Workspace.",
          workspaceBrandRuleTitle:
            "Workspace owns people and billing; Projects own business scope",
          workspaceBrandRuleDescription:
            "Each Project has its own members and connected apps. Billing always stays with the Workspace.",
          createAnotherBrand: "Create another independent business space.",
          createBrandDescription:
            "Create the Project, then optionally add connected apps and Workspace users.",
          brandCreatedTitle: "Project created",
          brandCreatedWithApps:
            "Before a task, choose apps from {{brand}} in Bustly Desktop.",
          brandCreatedWithoutApps:
            "Add apps to this Project before using them in Bustly Desktop.",
          projectCreateFailed: "Unable to create Project. Please try again.",
          createBrandSections: "Create Project sections",
          createTabs: {
            basics: "Basic information",
            integrations: "Add apps (optional)",
            members: "Add members (optional)",
          },
          createTabDescriptions: {
            integrations: "Choose connected apps to add to this Project.",
            members: "Choose Workspace users to add to this Project.",
          },
          initialSetupTitle: "Initial setup (optional)",
          initialSetupDescription:
            "Choose what this Project needs now. You can update these choices later.",
          connectedAppsOptional: "Connected apps (optional)",
          noAppsAvailableForBrand: "No connected apps are available to add.",
          workspaceUsersOptional: "Workspace users (optional)",
          noUsersAvailableForBrand:
            "No other Workspace users are available to add.",
          initialMemberRole: "Role for selected users",
          websiteOptionalDescription:
            "Adding a website helps Bustly better understand this Project.",
          newBrandDescription: "New Project business space",
          connectionCount: "{{count}} integrations",
          memberCount: "{{count}} members",
          brandSections: "Project sections",
          tabs: {
            overview: "Overview",
            integrations: "Integrations",
            members: "Members",
            settings: "Project settings",
          },
          integrationsInBrand: "Project integrations",
          membersInBrand: "Project members",
          integrationsCardDescription:
            "Accounts the Agent can use inside this Project.",
          membersCardDescription:
            "Workspace users and the role they hold inside this Project.",
          manage: "Manage",
          billingTitle: "Billing stays with the Workspace",
          billingDescription:
            "Projects do not buy plans or receive separate bills. Usage, credits, member limits, and billing are managed by the current Workspace.",
          workspaceBilling: "Workspace Billing",
          brandIntegrationsTitle: "Project integrations",
          brandIntegrationsDescription:
            "Choose the connected apps this Project can use. Connection owners still manage authorization and disconnecting.",
          addIntegration: "Add connected apps",
          addIntegrationTitle: "Add connected apps",
          addIntegrationDescription: "Add connected apps to {{brand}}.",
          addSelected: "Add {{count}} apps",
          appsAddedToBrand: "Added {{count}} apps to this Project.",
          noIntegrations: "This Project has no integrations yet.",
          memberInheritanceRule:
            "Project membership does not grant access to any account. Grant account access separately for each connection.",
          removeFromBrand: "Remove from Project",
          removeConnectionTitle: "Remove app from Project",
          removeConnectionConfirmation:
            "Remove {{app}} from {{brand}}? The app stays connected in this Workspace.",
          brandMembersTitle: "Project users",
          brandMembersDescription:
            "Add Workspace users and choose what they can do in this Project.",
          brandRole: "Project role",
          brandCreator: "Project creator",
          memberActions: "Member actions",
          roles: {
            viewer: "Viewer",
            manager: "Manager",
          },
          roleDescriptions: {
            viewer: "View this Project, its assigned apps, and results.",
            manager: "Manage Project settings, users, and assigned apps.",
          },
          manageMembersPermissionDenied:
            "Only Project Managers can manage Project users.",
          manageAppsPermissionDenied:
            "Only Project Managers can add or remove connected apps.",
          connectionManagerRequired:
            "Only the connection manager or a Workspace Owner can change this app’s Project assignment.",
          lastManagerHint: "Each Project needs at least one Manager.",
          addMember: "Add user",
          addMemberTitle: "Add a Project user",
          addMemberDescription: "Add a Workspace user to {{brand}}.",
          memberAddedToBrand: "Added {{name}} to this Project.",
          workspaceMember: "Workspace member",
          workspaceUsers: "Workspace users",
          removeMemberLabel: "Remove {{name}} from Project",
          removeMemberTitle: "Remove Project user",
          removeMemberConfirmation:
            "Remove {{name}} from {{brand}}? They will lose their role in this Project but remain a Workspace user.",
          removeMember: "Remove user",
          selectWorkspaceMember: "Choose a Workspace user",
          connectionBrandOptional: "Project (optional)",
          connectionBrandOptionalHint:
            "Use a Project to group this account. You can skip it and assign it later from the connection details.",
          brandSettingsTitle: "Project settings",
          brandSettingsDescription:
            "Maintain the basic information your team and Agent use to identify this Project.",
          brandLogo: "Project logo",
          brandLogoOptional: "Project logo (optional)",
          uploadLogo: "Upload logo",
          replaceLogo: "Replace logo",
          removeLogo: "Remove",
          readingLogo: "Reading…",
          logoUploadHint: "PNG, JPG, or WebP up to 2 MB.",
          website: "Project website",
          websitePlaceholder: "For example, yourbrand.com",
          primaryMarket: "Primary market (optional)",
          primaryMarketPlaceholder:
            "For example, Europe, United States, or Global",
          brandDescription: "Project description (optional)",
          brandDescriptionPlaceholder:
            "In one or two sentences, describe this project's business, audience, or products.",
          saveBrandSettings: "Save Project settings",
          brandSettingsSaved: "Project settings saved.",
          deleteBrandTitle: "Delete Project",
          deleteBrandDescription:
            "Deleting a Project removes its apps and members from the Project, but keeps them in this Workspace. App connections stay connected.",
          deleteBrandPermissionDenied:
            "Only the person who created this Project or a Workspace Owner can delete it.",
          deleteBrandConfirmation:
            "Delete {{brand}}? Its apps and members will be removed from this Project, but remain in this Workspace. App connections stay connected.",
          deleteBrand: "Delete Project",
        },
        crossModule: {
          assignmentsTitle: "Project assignments",
          assignmentsDescription:
            "Choose the Project that owns each store, ad account, property, or data source.",
          connectedAccountsTitle: "Connected accounts",
          connectedAccountsDescription:
            "Manage Project ownership and member access for every connected account in one place.",
          workspaceOverview: "Workspace overview",
          globalConnectionsDescription:
            "Review every connected account in the Workspace and its optional Project label. Account access stays on each connection.",
          brandAccessRule:
            "Project is optional. Joining a Project does not grant account access; manage permissions on each connection.",
          workspaceMemberBrandsTitle: "Member Projects",
          workspaceMemberBrandsDescription:
            "See which Projects each Workspace user can access. Their system role determines the actions they can take.",
          manageMembersInsideBrand:
            "Add or remove Project access inside the relevant Project.",
          brandMemberships: "Accessible Projects",
          allBrandsOwner: "Workspace Owner · All Projects",
          noBrandMembership: "Not in a Project yet",
          noMatchingConnections: "No connected accounts match this view.",
          searchConnectedAccounts: "Search connected accounts...",
          accountsAssigned: "{{assigned}} of {{total}} accounts assigned",
          applicationsConnected: "{{count}} applications",
          attentionNeeded: "{{count}} need attention",
          batchManageAccess: "Batch manage access",
          batchManageAccessDescription:
            "Choose a Project and member, then set their access across its connected accounts.",
          chooseBrand: "Choose Project",
          chooseMember: "Choose member",
          noConnectionsForBrand:
            "This Project does not have any connected accounts yet.",
          account: "Account",
          currentScope: "Current scope",
          assignment: "Assignment",
          connectionStatus: "Status",
          memberPermissions: "Member access",
          connectedDetails: "Connected by",
          inheritedAccess: "Inherited · {{count}} members",
          customAccess: "Custom · {{count}} members",
          ownerOnlyAccess: "Owner only",
          noBrandAccess: "Assign a Project first",
          manageAccountAccess: "Manage access for {{account}}",
          accountAccessDescription:
            "Project access decides who is eligible. Set only the exceptions for this account.",
          eligibleMembers: "Eligible Project members",
          permission: "Permission",
          permissionLevels: {
            inherit: "Inherit Project default",
            view: "View only",
            operate: "View and operate",
            none: "No access",
          },
          inheritedFromBrand: "Inherited from Project",
          customPermission: "Custom permission",
          resetToBrandDefault: "Reset all to Project defaults",
          savePermissionChanges: "Save changes",
          accessUpdatesInstant: "Changes apply to this prototype immediately.",
          unassigned: "Unassigned",
          sharedWithAllBrands: "Shared with all Projects",
          selectScope: "Select Project scope for {{account}}",
          memberAccessTitle: "Project access",
          memberAccessDescription:
            "Workspace roles control actions. Project access controls which business data each member and Agent task can reach.",
          editAccess: "Edit access",
          accessSummaryAll: "All current and future Projects",
          accessSummarySelected: "{{count}} selected Project{{plural}}",
          accessSummaryNone: "No Project access",
          activityTitle: "Project activity",
          activityDescription:
            "Review scope changes that affect what members and Agents can access.",
          activityEmpty: "Project changes will appear here.",
          activityActor: "Changed by {{name}}",
          activityTypes: {
            "brand-created": "Created {{brand}}",
            "brand-updated": "Updated settings for {{brand}}",
            "connection-assigned": "Assigned {{connection}} to {{brand}}",
            "connection-shared": "Shared {{connection}} with all Projects",
            "connection-unassigned": "Removed the Project from {{connection}}",
            "connection-access-updated":
              "Updated {{user}}’s access to {{connection}}",
            "user-access-updated": "Updated {{user}}’s access to {{brand}}",
            "agent-brand-selected":
              "Selected {{brand}} for the next Agent task",
          },
          agentScopeLabel: "Project",
          agentAllBrands: "All Projects",
          agentScopeConnections: "{{count}} scoped connections",
          agentScopeHint:
            "The Agent will use only Resources available to this Project.",
        },
      },
      brandsSettings: {
        title: "Projects",
        subtitle:
          "Give each Agent task a clear business scope across stores, ad accounts, analytics, and teammates.",
        brandGroups: "Projects",
        description:
          "Group existing integration Resources by Project and use the same Workspace grants for final authorization.",
        initializationFailed: "Initialization failed: {{reason}}",
        loadFailed: "Project data couldn't load",
        loadFailedDescription:
          "We couldn't load Project data right now. Please try again.",
        retry: "Retry",
        loading: "Loading Projects…",
        initializing: "Initializing…",
        enable: "Enable Projects",
        maxBrands: "Your plan includes up to {{count}} Projects.",
        ownerOnly: "Only the Workspace Owner can enable this feature.",
        brands: "Projects",
        selectBrand: "Select a Project",
        selectBrandAria: "Select Project",
        newBrand: "New Project",
        add: "Add",
        selectBrandToManage:
          "Select a Project to manage members and Resources.",
        allAccessibleBrands: "All Projects",
        selectedBrands: "{{count}} Project{{plural}}",
        scopeResourceContractUnavailable:
          "Project Resources are temporarily unavailable. Please try again.",
        brandDescription:
          "A Project groups existing integration Resources; it does not own OAuth connections.",
        manager: "Manager",
        viewer: "Viewer",
        archiveConfirm: "Archive {{brand}}?",
        archive: "Archive",
        save: "Save",
        you: "You",
        remove: "Remove",
        addWorkspaceMember: "Add workspace member…",
        viewerRole: "Viewer",
        managerRole: "Manager",
        authorizedResources: "Authorized Resources",
        assignResource: "Assign authorized Resource…",
        integrationContract: "Integration resource contract",
        integrationContractDescription:
          "Applications remain connected to the Workspace. Projects only select the minimum Resources exposed by each connection.",
        myConnection: "My connection",
        workspaceConnection: "Workspace connection",
        connection: "connection",
        settingsDescription:
          "Project identity and lifecycle controls are managed from the header above. OAuth connections remain owned by the Workspace and are not moved under a Project.",
        members: "Members",
        connections: "Connections",
        tabs: {
          overview: "Overview",
          integrations: "Integrations",
          members: "Members",
          settings: "Project settings",
        },
        prototype: "Project workspace",
        brandName: "Project name",
        brandNamePlaceholder: "For example, Jimmy.eu",
        domain: "Website",
        domainPlaceholder: "https://example.com",
        createBrand: "Create Project",
        create: "Create",
        cancel: "Cancel",
        brandNotFound: "This Project is no longer available.",
        backToBrands: "Back to Projects",
        allBrands: "All Projects",
        noDomain: "Website not added",
        application: "Application",
        connectionAccount: "Connection / Resource",
        status: "Status",
        connectedAt: "Connection",
        action: "Action",
        user: "User",
        workspaceRole: "Workspace role",
        simple: {
          directoryTitle: "Projects",
          directoryDescription: "Group connected minimum Resources by Project.",
          brandDescription:
            "A Project groups existing Resources and never owns an OAuth connection.",
          brandDescriptionPlaceholder: "Describe this Project's scope.",
          newBrandDescription: "New business scope",
          createBrandDescription:
            "Create a Project, then assign authorized minimum Resources and members.",
          tabs: {
            overview: "Overview",
            integrations: "Resources",
            members: "Members",
            settings: "Settings",
          },
          connectionCount: "{{count}} Resource group{{plural}}",
          memberCount: "{{count}} member{{plural}}",
          manage: "Manage",
          noIntegrations: "No Resource has been assigned to this Project.",
          integrationsCardDescription:
            "Applications remain connected to the Workspace; this Project selects their Resources.",
          integrationsInBrand: "Resources in this Project",
          brandIntegrationsTitle: "Authorized Resources",
          brandIntegrationsDescription:
            "Assign only active minimum Resources that the current user is authorized to use.",
          resource: "Resource",
          resourceType: "Type",
          assigned: "Assigned",
          available: "Available",
          unavailable: "Unavailable",
          memberInheritanceRule:
            "Project membership selects scope; existing Workspace and minimum Resource grants still authorize every action.",
          addIntegration: "Add Resources",
          addIntegrationTitle: "Assign Resources",
          addIntegrationDescription:
            "Choose authorized Resources for {{brand}}.",
          addSelected: "Assign {{count}}",
          removeFromBrand: "Remove",
          brandMembersTitle: "Project members",
          brandMembersDescription:
            "Viewer and Manager roles are scoped to this Project; they do not grant OAuth access.",
          addMember: "Add user",
          addMemberTitle: "Add a Project user",
          addMemberDescription: "Add a Workspace user to {{brand}}.",
          brandRole: "Project role",
          memberActions: "Member actions",
          brandCreator: "Project creator",
          removeMember: "Remove user",
          membersCardDescription:
            "Manage Project membership and Viewer/Manager roles.",
          membersInBrand: "Members in this Project",
          brandSettingsTitle: "Project settings",
          brandSettingsDescription:
            "Edit Project identity. Connections remain owned by the Workspace.",
          saveBrandSettings: "Save settings",
          brandLogo: "Project logo",
          uploadLogo: "Upload logo",
          replaceLogo: "Replace logo",
          removeLogo: "Remove logo",
          logoUploadHint: "PNG, JPG, or SVG up to 2 MB.",
          readingLogo: "Reading logo…",
          website: "Website",
          websitePlaceholder: "example.com",
          primaryMarket: "Primary market",
          primaryMarketPlaceholder: "Optional",
          brandSections: "Project sections",
          createAnotherBrand: "Create another Project",
        },
      },
      saveChanges: "Save Changes",
      comingSoon: "Coming Soon",
      reusableSkillSuggestions: "Reusable skill suggestions",
    },
    brandsSettings: {
      title: "Business groups",
      subtitle: "Manage Resources and member access for each Business group.",
      brands: "Business groups",
      brand: "Business group",
      brandDirectory: "Business groups",
      brandDirectoryDescription:
        "A Business group brings together the Resources and people an Agent can use for that scope.",
      assignedConnections: "Scoped connections",
      needsAssignment: "Needs assignment",
      connections: "Connections",
      users: "Members",
      status: "Status",
      action: "Action",
      ready: "Ready",
      needsAttention: "Needs attention",
      manage: "Manage",
      createBrand: "Create Business group",
      createBrandDescription:
        "Name the Business group first. You can assign Resources and user access next.",
      create: "Create Business group",
      cancel: "Cancel",
      brandName: "Business group name",
      brandNamePlaceholder: "For example, your Business group name",
      websiteOptional: "Website (optional)",
      newBrandDescription: "New business scope",
      noDomain: "Website not added",
      brandNotFound: "This Business group is no longer available.",
      backToBrands: "Back",
      allBrands: "All Business groups",
      tabs: {
        overview: "Overview",
        connections: "Connections",
        users: "Members",
      },
      brandSections: "Business group sections",
      brandSummary: "Business group summary",
      brandSummaryDescription:
        "Review the Resources the Agent can use when this Business group is selected.",
      manageConnections: "Manage connections",
      manageUsers: "Manage members",
      agentScope: "Agent scope",
      agentScopeTitle: "{{brand}} is a distinct business scope",
      agentScopeDescription:
        "Tasks started in this Business group use only its assigned Resources and Workspace grants.",
      setupChecklist: "Setup checklist",
      addBrandDetails: "Add Business group details",
      assignConnectionAccounts: "Assign connection accounts",
      grantUserAccess: "Grant user access",
      connectionAccounts: "Connection accounts",
      connectionAccountsDescription:
        "Assign the smallest available account, store, property, or advertiser—not the whole integration.",
      connectionAccount: "Connection account",
      application: "Application",
      brandScope: "Business group scope",
      connectedBy: "Connected by",
      connectedAt: "Connection time",
      assignedBy: "Assigned by",
      connectionDetails: "Connection details",
      connectionHistory: "Connection history",
      viewDetails: "View details",
      formerWorkspaceUser: "Former workspace user",
      notAssigned: "Not assigned",
      connectionStatus: {
        connected: "Connected",
        "needs-reauthorization": "Reconnect required",
        "sync-issue": "Sync issue",
      },
      addConnections: "Add connections",
      addConnectionsDescription:
        "Choose authorized Resources for {{brand}}. A Resource can be assigned to multiple Business groups.",
      noUnassignedConnections: "There are no unassigned connection accounts.",
      assignSelected: "Assign selected",
      unassignedConnections: "Unassigned connections",
      unassignedConnectionsDescription:
        "{{count}} Resources are still visible at Workspace level and can be assigned to Business groups.",
      reviewConnections: "Review assignments",
      userAccess: "Member access",
      userAccessDescription:
        "Workspace roles define what people can do. Business group access defines where they can do it.",
      user: "Member",
      workspaceRole: "Workspace role",
      brandAccess: "Business group access",
      hasAccess: "Has access",
      noAccess: "No access",
      manageAccess: "Manage app access",
      manageUserAccess: "Manage user access",
      ownerAccessFixed: "Owner · All Business groups",
      ownerAccessFixedDescription:
        "Workspace owners always have access to every Business group.",
      allBrandsAccessDescription:
        "This user can work across every current and future Business group.",
      selectedBrands: "Selected Business groups",
      selectedBrandsAccessDescription:
        "This user can work only in the Business groups selected below.",
      chooseBrands: "Choose Business groups",
      saveAccess: "Save access",
      simple: {
        directoryTitle: "Business groups",
        directoryDescription:
          "Organize work, members, and resources by business group.",
        loadFailed: "Business group data couldn't load",
        directoryTabWorkspace: "Public",
        directoryTabPrivate: "Private",
        searchProjectsPlaceholder: "Search Business groups...",
        noProjectsYet: "No Business groups yet",
        noProjectsCategoryDesc:
          "No Business groups created in this category yet.",
        projectScope: "Business group type",
        scopeWorkspace: "Public business group",
        scopeWorkspaceDesc:
          "Everyone in this workspace can view it. You can set access to app accounts for each member.",
        scopePrivate: "Private business group",
        scopePrivateDesc: "Only you can view this Business group.",
        privateProjectNotice:
          "Only you can view this Business group. Other Workspace users can't be added.",
        directorySections: "Business group management sections",
        directoryBrands: "Business groups",
        workspaceIntegrations: "Apps & connections",
        workspaceIntegrationsDescription:
          "Connected app accounts in this Workspace. Assign each account to one or more Business groups.",
        addingExistingConnectionToBrand:
          "Adding a connected app account to “{{brand}}”.",
        clearBrandTarget: "Clear selection",
        notAssignedToBrand: "Not assigned to a Business group",
        noWorkspaceIntegrationsTitle: "No app accounts are connected",
        noWorkspaceIntegrationsDescription:
          "Connect an application before assigning its app account to a Business group.",
        assignToBrands: "Assign Business groups",
        assignToBrandsTitle: "Assign to Business groups",
        assignToBrandsDescription:
          "Choose the Business groups that can use this Resource.",
        brands: "Business groups",
        selectBrands: "Choose Business groups",
        saveBrandAssignments: "Save Business group assignments",
        brandAssignmentsUpdated: "Business group assignments updated.",
        defaultBrand: "Default Business group",
        defaultBrandBadge: "Default",
        defaultBrandMemberCount: "{{count}} Business group members",
        workspaceUsersInDefaultBrand: "Workspace users",
        createdBy: "Created by {{name}}",
        brandGroupUsage: "{{current}} of {{limit}} Business groups used",
        brandGroupUsageUnlimited:
          "{{current}} Business groups used · Unlimited",
        createAction: "New business group",
        projectUsageByCreator: "By creator",
        projectCreatorCount: "{{count}} Business group{{plural}}",
        unknownProjectCreator: "Unknown creator",
        organizeAccessTitle: "Organize app access with Business groups",
        organizeAccessDescription:
          "Use Business groups to organize connected app accounts and access by business. Available with Team $4,999 and higher plans.",
        viewTeamPlans: "View Team plans",
        renewBrandGroupsTitle: "Renew your plan to use Business groups",
        renewBrandGroupsDescription:
          "Your {{count}} Business groups and their assignments are saved. Renew an eligible Team plan to continue managing them.",
        renewSubscription: "Renew subscription",
        upgradeBrandGroupsTitle: "Upgrade to manage your Business groups",
        upgradeBrandGroupsDescription:
          "Your {{count}} existing Business groups are saved and available to view. Upgrade to Team $4,999 or higher to create or manage them.",
        billingPermissionHint:
          "Ask your Workspace Owner or a user with Manage billing permission to upgrade the plan.",
        limitReachedTitle: "You’ve reached your Business group limit",
        limitReachedDescription:
          "Your current plan includes {{limit}} Business groups. Upgrade to create more.",
        upgradePlan: "Upgrade plan",
        readOnlyLabel: "Read-only",
        workspaceBrandRuleTitle:
          "Workspace manages users and billing; Business groups organize business scope",
        workspaceBrandRuleDescription:
          "Business groups organize connected app accounts by business. App account access is assigned separately. Billing stays with the Workspace.",
        createBrandDescription:
          "Create the Business group, then connect applications and assign app access when needed.",
        brandCreatedTitle: "Business group created",
        brandCreatedWithApps:
          "{{brand}} is ready. Assign app access when users need to use an account.",
        brandCreatedWithoutApps:
          "{{brand}} was created. You can now assign app accounts or add members.",
        projectCreateForbidden:
          "Only users with Create and manage Business groups permission can create a Business group.",
        projectCreateFailed:
          "We couldn't create the Business group. Try again.",
        businessGroupNameAlreadyInUse:
          "This Business group name is already in use. Choose another name.",
        createBrandSections: "Create Business group sections",
        createTabs: {
          basics: "Basic information",
          integrations: "Add apps (optional)",
          members: "Set app access (optional)",
        },
        createTabDescriptions: {
          integrations: "Choose connected apps to add to this Business group.",
          members: "Choose Workspace users and set their app access.",
        },
        initialSetupTitle: "Initial setup (optional)",
        initialSetupDescription:
          "Choose what this Business group needs now. You can update these choices later.",
        connectedAppsOptional: "Connected apps (optional)",
        noAppsAvailableForBrand: "No connected apps are available to add.",
        workspaceUsersOptional: "App access (optional)",
        noUsersAvailableForBrand:
          "All Workspace users already have app access for this Business group.",
        initialMemberRole: "App access for selected users",
        websiteOptionalDescription:
          "Adding a website helps Bustly better understand this Business group.",
        newBrandDescription: "New business space",
        connectionCount: "{{count}} app accounts",
        memberCount: "{{count}} members",
        brandSections: "Business group sections",
        tabs: {
          overview: "Overview",
          integrations: "App accounts",
          members: "Members",
          settings: "Business group settings",
        },
        integrationsInBrand: "Connected app accounts",
        membersInBrand: "Business group members",
        integrationsCardDescription:
          "Connected app accounts in this Business group.",
        membersCardDescription:
          "Manage Business group members' access to connected app accounts.",
        defaultBrandMembersCardDescription:
          "See each Workspace user's access to connected app accounts.",
        manage: "Manage",
        billingTitle: "Billing stays with the Workspace",
        billingDescription:
          "Business groups do not buy plans or receive separate bills. Usage, credits, member limits, and billing are managed by the current Workspace.",
        workspaceBilling: "Workspace Billing",
        brandIntegrationsTitle: "Business group app accounts",
        integrationViewLabel: "Business group app account view",
        browseApplications: "Browse apps",
        manageConnections: "Manage connections",
        addIntegration: "Connect application",
        integrationsClientTaskDescription:
          "These app accounts have been assigned to this Business group.",
        googleAdsAccountAssignmentMeta:
          "Google Ads account {{accountId}} · Authorized account: {{authorizedAccount}}",
        authorizedAccountUnknown: "Not recorded",
        assignedBusinessGroups: "Business groups: {{groups}}",
        goToIntegrations: "Go to Apps & connections",
        manageIntegrationsPermissionDenied:
          "Only Workspace Owners or users with Manage integrations permission for this Business group can connect applications.",
        addSelected: "Connect {{count}} apps",
        appsAddedToBrand: "Connected {{count}} apps to this Business group.",
        noConnectionsForBrand: "No app accounts assigned yet",
        noIntegrations:
          "Go to Apps & connections to assign a connected app account to this Business group.",
        memberInheritanceRule:
          "A Business group does not grant access to an account. Grant access separately for each connected account.",
        removeFromBrand: "Remove from Business group",
        removeConnectionTitle: "Remove {{app}} from {{brand}}?",
        removeConnectionConfirmation:
          "This app account will no longer be available in {{brand}}. Its Workspace connection will stay active.",
        unavailable: "Unavailable",
        brandMembersTitle: "Member permissions",
        brandMembersDescription:
          "Manage members and their access to connected app accounts.",
        defaultBrandMembersDescription:
          "Default Business group for this Workspace. Assign connected app access separately.",
        applicationAccess: "Accessible app accounts",
        applicationAccounts: "App accounts",
        applyAccessToAllApplications: "Apply to all",
        individualApplicationAccessNotice:
          "Available access levels differ across these app accounts. Set them individually.",
        individualMemberAccessNotice:
          "Available access levels differ across these members. Set them individually.",
        enableManageAccessForAllApplications: "Enable manage access for all",
        manageAccessEnabledForAllApplications: "Manage access enabled for all",
        applicationAccountAccessCount:
          "Access to {{count}} connected app accounts",
        applicationAccountCount: "{{count}} application accounts",
        accessActions: "Access actions",
        grantAccess: "Assign app access",
        assignApplicationAccess: "Assign app access",
        batchAssignPermissions: "Manage member access",
        batchAssignPermissionsTitle: "Set member access",
        batchAssignPermissionsDesc: "Set member access for this app account.",
        connectedByMember: "Connected by {{name}}",
        noOtherMembersToAssign:
          "No other members need access to this app account.",
        addMembersBeforeManagingAccess: "Add a Business group member first.",
        assignApplicationBeforeManagingAccess:
          "Assign an app account to this Business group first.",
        selectApplicationToAssign: "App account",
        targetAccessLevel: "Access",
        selectMembersToAssign: "Members",
        selectAllMembers: "Select all members ({{count}})",
        batchAccessUpdated: "Application access updated for {{count}} members",
        applyBatchAccess: "Save access",
        noMatchingProjectsTitle: "No matching business groups found",
        noMatchingProjectsDesc:
          "No business groups match your search. Try another keyword or clear the search.",
        clearSearch: "Clear search",
        manageAccess: "Set account access",
        manageApplicationAccess: "Set account access",
        grantAccessTitle: "Assign app access",
        assignApplicationAccessTitle: "Assign app access",
        manageApplicationAccessTitle: "Set account access for {{name}}",
        grantAccessDescription:
          "Select a Workspace member or invite someone to join this Business group.",
        defaultBrandGrantAccessDescription:
          "Choose a Workspace user and set their access and allowed actions for each connected app account. You can assign only app accounts you can manage.",
        manageApplicationAccessDescription:
          "Set this user's access to app accounts in this Business group. You can change only the accounts you manage.",
        workspaceUser: "Workspace member",
        inviteNewWorkspaceMember: "Invite a new Workspace member",
        inviteNewMemberTitle: "Invite a new Workspace member",
        inviteNewMemberDescription:
          "Invite someone to this Workspace and Business group, then set their app access.",
        currentBusinessGroup: "Business group",
        preselectedBusinessGroup: "Current group",
        inviteMembershipNotice:
          "After they accept, they’ll join the Workspace and this Business group.",
        backToWorkspaceMembers: "Back to Workspace members",
        sendBusinessGroupInvite: "Send invitation",
        loadingInviteRoles: "Loading Workspace roles...",
        inviteRoleUnavailable:
          "Workspace roles could not be loaded. Close the drawer and try again.",
        inviteEmailRequired: "Enter an email address.",
        inviteEmailInvalid: "Enter a valid email address.",
        inviteExistingWorkspaceUser:
          "This person is already in the Workspace. Return to the member list to add them.",
        businessGroupInvitationSent:
          "Invitation sent to {{email}}. They’ll join this Business group after they accept.",
        businessGroupInvitationFailed:
          "We couldn't send the invitation. Try again.",
        loadingWorkspaceUsers: "Loading Workspace users...",
        noWorkspaceUsersToAddTitle: "No users to add",
        allWorkspaceUsersAddedTitle:
          "All users are already in this Business group",
        goToWorkspaceUsers: "Go to members",
        alreadyAddedToBrand: "Added",
        connectorUnknown: "Connector not recorded",
        connectorControlsAccess:
          "Ask the connection owner to allow this Business group owner to manage member access for this app account.",
        connectBeforeGrantingTitle: "Connect an application first",
        connectBeforeGrantingDescription:
          "App access can be assigned after an account is connected to this Business group.",
        noApplicationAccessTitle: "No Workspace users have app access yet",
        noApplicationAccessDescription:
          "Assign at least one connected app account to a Workspace user so they can use it.",
        noMembersInBrandTitle: "No Business group members yet",
        noMembersInBrandDescription:
          "Add a Workspace user to this Business group, then assign their app access separately.",
        assignAppAccessLater:
          "Assign an app account to this Business group in Apps & connections before setting member access.",
        brandRole: "App access",
        brandCreator: "Creator",
        memberActions: "Member actions",
        roles: {
          member: "Can view",
          owner: "Can edit",
        },
        roleDescriptions: {
          member:
            "View this Business group. App account access is assigned separately.",
          owner:
            "Manage connected apps and app account access for this Business group.",
        },
        manageMembersPermissionDenied:
          "Business group owners or Workspace Owners only.",
        manageApplicationAccessPermissionDenied:
          "Business group owners or Workspace Owners only.",
        manageAppsPermissionDenied:
          "Only Workspace Owners or users with Manage integrations permission for this Business group can manage connected apps.",
        manageAppAccess: "Manage access",
        manageAppAccessTitle: "Set account access",
        manageAppAccessDescription: "Choose who can use this app in {{brand}}.",
        addAndAssignAccess: "Add member",
        addMemberWithoutAppAccess: "Add member",
        saveAppAccess: "Save changes",
        fullAccess: "Full access",
        appAccessUpdated: "App access updated.",
        userAccessAdded: "Member added. You can assign app access at any time.",
        removeMemberFromBrand: "Remove member",
        removeMemberFromBrandTitle: "Remove {{name}} from {{brand}}?",
        removeMemberFromBrandConfirmation:
          "{{name}} will lose access to every app account in {{brand}}, but will remain a Workspace user.",
        memberRemovedFromBrand: "{{name}} was removed from {{brand}}.",
        accessLevels: {
          no_access: "No access",
          read: "View",
          action: "Can take action",
        },
        connectionOwnerActionRequired:
          "The connection owner must retain Can take action access.",
        connectionOwnerBadge: "Connected by this user",
        connectionOwnerAccessTooltip:
          "Connection owners always keep the highest access.",
        connectionManagerRequired:
          "This app is not assigned to this Business group.",
        lastManagerHint:
          "Each Business group needs at least one user who can manage its connected apps.",
        addMember: "Assign app access",
        addMemberTitle: "Assign app access",
        addMemberDescription:
          "Assign {{name}} access to connected app accounts in {{brand}}.",
        memberAddedToBrand: "App access assigned to {{name}}.",
        workspaceMember: "Workspace member",
        workspaceUsers: "Workspace members",
        removeMemberLabel: "Remove {{name}}'s app access",
        removeMemberTitle: "Remove {{name}}'s app access from {{brand}}?",
        removeMemberConfirmation:
          "They will lose access to this Business group's connected app accounts. Their Workspace account and access to other Business groups will not be affected.",
        removeMember: "Remove app access",
        selectWorkspaceMember: "Choose a Workspace user",
        connectionBrandOptional: "Business groups (optional)",
        connectionBrandOptionalHint:
          "Choose the Business groups that can use this account. You can update this later in Apps & connections.",
        editBrand: "Edit Business group",
        editBrandName: "Edit Business group name",
        brandSettingsTitle: "Business group settings",
        brandSettingsDescription:
          "Update this Business group's basic information.",
        brandLogo: "Business group logo",
        brandLogoOptional: "Business group logo (optional)",
        uploadLogo: "Upload logo",
        replaceLogo: "Replace logo",
        removeLogo: "Remove",
        readingLogo: "Reading…",
        logoUploadHint: "PNG, JPG, or WebP up to 2 MB.",
        website: "Business group website",
        websitePlaceholder: "For example, your-business.com",
        primaryMarket: "Primary market (optional)",
        primaryMarketPlaceholder:
          "For example, Europe, United States, or Global",
        brandDescription: "Business group description (optional)",
        brandDescriptionPlaceholder:
          "In one or two sentences, describe this Business group's business, audience, or products.",
        saveBrandSettings: "Save changes",
        brandSettingsSaved: "Business group updated.",
        brandSettingsSaveFailed: "Failed to save Business group.",
        deleteBrandTitle: "Delete Business group",
        deleteBrandDescription:
          "Deleting a Business group removes its connected app accounts and members from the group. They remain in this Workspace, and app connections stay connected.",
        deleteBrandPermissionDenied:
          "Only the Creator or a Workspace Owner can delete this Business group.",
        deleteBrandConfirmation:
          "Delete {{brand}}? Its connected app accounts and members will be removed from this Business group. They will remain in this Workspace, and app connections will stay connected.",
        deleteBrand: "Delete Business group",
        defaultBrandCannotDelete: "Default Business group cannot be deleted",
      },
      crossModule: {
        assignmentsTitle: "Business group assignments",
        assignmentsDescription:
          "Choose the Business group each store, ad account, property, or data source belongs to.",
        connectedAccountsTitle: "Connected accounts",
        connectedAccountsDescription:
          "Manage Business group assignment and app account access for every connected account in one place.",
        workspaceOverview: "Workspace overview",
        globalConnectionsDescription:
          "Review every connected account in the Workspace and its optional Business group label. Account access stays on each connection.",
        brandAccessRule:
          "A Business group organizes connected app accounts. Set access separately for each connected account.",
        workspaceMemberBrandsTitle: "User access by Business group",
        workspaceMemberBrandsDescription:
          "See which Business groups each Workspace user can access through connected app accounts. Their system role determines what they can manage.",
        manageMembersInsideBrand:
          "Manage app account access in the relevant Business group.",
        brandMemberships: "Business groups with app account access",
        allBrandsOwner: "Workspace Owner · All Business groups",
        noBrandMembership: "No app account access yet",
        noMatchingConnections: "No connected accounts match this view.",
        searchConnectedAccounts: "Search connected accounts...",
        accountsAssigned: "{{assigned}} of {{total}} accounts assigned",
        applicationsConnected: "{{count}} applications",
        attentionNeeded: "{{count}} need attention",
        batchManageAccess: "Batch manage access",
        batchManageAccessDescription:
          "Choose a Business group and Workspace user, then set their access across its connected app accounts.",
        chooseBrand: "Choose Business group",
        chooseMember: "Choose member",
        noConnectionsForBrand:
          "This Business group does not have any connected accounts yet.",
        account: "Account",
        currentScope: "Current scope",
        assignment: "Assignment",
        connectionStatus: "Status",
        memberPermissions: "User access",
        connectedDetails: "Connected by",
        inheritedAccess: "Granted · {{count}} users",
        customAccess: "Custom · {{count}} users",
        ownerOnlyAccess: "No user access",
        noBrandAccess: "Assign a Business group first",
        manageAccountAccess: "Manage access for {{account}}",
        accountAccessDescription:
          "A Business group scopes this account. Set each Workspace user's access to it here.",
        eligibleMembers: "Workspace users with app account access",
        permission: "Permission",
        permissionLevels: {
          inherit: "Inherit Business group default",
          view: "View only",
          operate: "View and operate",
          none: "No access",
        },
        inheritedFromBrand: "Inherited from Business group",
        customPermission: "Custom permission",
        resetToBrandDefault: "Reset all to Business group defaults",
        savePermissionChanges: "Save changes",
        accessUpdatesInstant: "Changes apply to this prototype immediately.",
        unassigned: "Unassigned",
        sharedWithAllBrands: "Shared with all Business groups",
        selectScope: "Select Business group scope for {{account}}",
        memberAccessTitle: "Business group access",
        memberAccessDescription:
          "System roles control what users can manage. App account access controls which connected accounts users and Agent tasks can use.",
        editAccess: "Edit access",
        accessSummaryAll: "All current and future Business groups",
        accessSummarySelected: "{{count}} selected Business group{{plural}}",
        accessSummaryNone: "No Business group access",
        activityTitle: "Business group activity",
        activityDescription:
          "Review scope changes that affect what members and Agents can access.",
        activityEmpty: "Business group changes will appear here.",
        activityActor: "Changed by {{name}}",
        activityTypes: {
          "Project-created": "Created {{brand}}",
          "Project-updated": "Updated settings for {{brand}}",
          "connection-assigned": "Assigned {{connection}} to {{brand}}",
          "connection-shared": "Shared {{connection}} with all Business groups",
          "connection-unassigned":
            "Removed the Business group from {{connection}}",
          "connection-access-updated":
            "Updated {{user}}’s access to {{connection}}",
          "user-access-updated": "Updated {{user}}’s app access in {{brand}}",
          "agent-Project-selected":
            "Selected {{brand}} for the next Agent task",
        },
        agentScopeLabel: "Business group",
        agentAllBrands: "All Business groups",
        agentScopeConnections: "{{count}} scoped connections",
        agentScopeHint:
          "The Agent will use only connections available to this Business group.",
      },
    },
    workspaceSkills: {
      subtitle:
        "Workspace Skills save useful methods from completed tasks so your team can use them again.",
      loading: "Loading workspace skills...",
      unavailable: "Workspace skills are unavailable",
      loadFailed: "Failed to load workspace skills.",
      retry: "Retry",
      emptyTitle: "Workspace Skills will appear here",
      emptyDescription:
        "New Workspace Skills are created after completed tasks are reviewed and approved.",
      pagination: "Workspace Skills pages",
      previousPage: "Previous",
      nextPage: "Next",
      pageNumber: "Page {{page}}",
      showingSkillsRange: "Showing {{start}} to {{end}} of {{total}} skills",
      createdBy: "Created by",
      unknownCreator: "Workspace member",
      updatedAt: "Last updated:",
      viewSkillDetail: "View details",
      skillDetailTitle: "Skill details",
      closeSkillDetail: "Close skill details",
      loadingSkillDetail: "Loading skill details...",
      detailLoadFailed: "Failed to load skill details.",
      deleteSkill: "Remove skill",
      deleteSkillTitle: "Remove skill from this workspace?",
      deleteSkillDescription:
        "{{name}} will be removed from this workspace. Its saved versions will be kept.",
      deleteSkillConfirm: "Remove",
      deleteSkillSuccess: "Skill removed from this workspace.",
      deleteSkillFailed: "Failed to remove skill from this workspace.",
      guideTitle: "Turn best practices into reusable skills.",
      guideDescription:
        "Save repeatable ways of working so your team can execute consistently.",
      turnOn: "Enable",
      ownerOnlyHint: "Only the Workspace Owner can turn on Workspace Skills.",
      creationNote:
        "New Workspace Skills are created only after completed tasks are reviewed and approved.",
      previewCompletedTask: "Completed task",
      previewReviewed: "Reviewed",
      previewReusableSkill: "Reusable skill",
      previewReady: "Ready for your team",
      pausedDescription:
        "Workspace Skills is paused. Existing skills remain available, but Bustly won't create or update skills from newly completed tasks.",
      pauseAction: "Pause",
      resumeAction: "Turn on",
      resumeLabel: "Turn on Workspace Skills",
      pauseTitle: "Pause Workspace Skills?",
      pauseDescription:
        "Bustly will stop creating or updating Workspace Skills from newly completed tasks. Existing skills will remain available.",
      pauseConfirm: "Pause",
      settingsUpdateFailed: "We couldn't update Workspace Skills. Try again.",
      permissionDeniedTitle: "Permission Denied",
      permissionDeniedDescription:
        "You do not have permission to view Workspace Skills.",
    },
    valueLedger: {
      workspaceFallback: "this workspace",
      ownerView: "Owner view",
      title: "Outcome Summary",
      subtitle:
        "Review the traceable outcomes Bustly produced for {{workspaceName}}.",
      lastUpdated: "Last updated {{date}}",
      waitingForData: "Waiting for outcome data",
      waitingForFirstRefresh: "Preparing Outcome Summary",
      outcomeSummaryTag: "Daily outcome preview",
      outcomeSummaryTitle: "Outcome Summary",
      outcomeSummaryDescription:
        "See what Bustly completed for your business and the results it delivered.",
      outcomeSummaryGetStarted: "Put Bustly to work",
      outcomeSummaryScheduleDescription:
        "Generated daily at 8:00 AM for the previous day, based on the workspace time zone.",
      outcomeSummaryPreviewTitle: "After you turn it on, you'll see",
      outcomeSummaryAdvancedWorkTitle: "Work that moved forward",
      outcomeSummaryAdvancedWorkDescription:
        "Results are broken down by checks, execution, delivery, and validation, not just task counts.",
      outcomeSummaryTraceableEvidenceTitle: "Traceable evidence",
      outcomeSummaryTraceableEvidenceDescription:
        "Keeps the checked objects, actions taken, validation results, and risk notes.",
      outcomeSummaryRepeatableWorkflowTitle: "Repeatable workflows",
      outcomeSummaryRepeatableWorkflowDescription:
        "Surfaces recurring decision rules, exception handling, and handoff patterns.",
      outcomeSummaryFinePrintTitle:
        "Note: Daily summaries use workspace Work Units.",
      outcomeSummaryFinePrintDescription:
        "Bustly reads completed Tasks and related context in this workspace only to generate this page. That data is not used for other purposes. Bustly runs on AWS and Google Cloud infrastructure, with mature security and privacy controls that protect data while it is processed.",
      outcomeSummaryToggleLabel: "Toggle Outcome Summary",
      outcomeSummaryPermissionHint:
        "Only users with permission to manage workspace settings can change this.",
      outcomeSummaryExpiredHint:
        "Workspace subscription has expired. Outcome Summary is stopped and historical content remains visible.",
      updateCadence:
        "Outcome Summary refreshes daily at {{time}} ({{timezone}})",
      firstRefreshPendingTitle: "Generating the outcome view",
      firstRefreshPendingDescription:
        "Reading recent workspace tasks and preparing verifiable outcome cards.",
      startingOutcomeSummaryTitle: "Generating the outcome view",
      startingOutcomeSummaryDescription:
        "Reading recent workspace tasks and preparing verifiable outcome cards.",
      preparingOutcomeSummaryTitle: "Generating the outcome view",
      preparingOutcomeSummaryDescription:
        "Reading recent workspace tasks and preparing verifiable outcome cards.",
      preparingOutcomeSummaryStepReadTasks: "Reading completed workspace tasks",
      preparingOutcomeSummaryStepExtractEvidence:
        "Extracting sub-outcomes and evidence notes",
      preparingOutcomeSummaryStepGroupWorkflows: "Grouping reusable workflows",
      dailyUpdates: "Daily updates",
      dailyUpdatesOff: "Daily updates paused",
      historicalDataNotice:
        "Showing historical data. Turn on daily updates to refresh it.",
      disableDialogTitle: "Pause daily updates?",
      disableDialogDescription:
        "Historical Outcome Summary content will stay visible. Bustly will stop daily refreshes and won't use Work Units for Outcome Summary until you turn them on again.",
      disableAction: "Pause",
      disableDialogConfirm: "Pause",
      resumeAction: "Turn on",
      resumeDailyUpdatesLabel: "Turn on daily updates",
      permissionDeniedTitle: "Permission Denied",
      permissionDeniedDescription:
        "You do not have permission to view Outcome Summary details.",
      settingsUpdateFailed:
        "We couldn't update Outcome Summary settings. Try again in a moment.",
      statRange: "Subscription cycle",
      currentCycle: "Current subscription cycle",
      currentCycleDescription: "Tasks and outcomes in this subscription cycle.",
      previousCycle: "Previous subscription cycle",
      previousCycleDescription:
        "Compare against the previous subscription cycle.",
      recentThreeCycles: "Recent 3 subscription cycles",
      recentThreeCyclesDescription: "Review the latest operating pattern.",
      customCycle: "Custom range",
      customCycleDescription: "Choose a specific date range.",
      billingCycle: "Subscription cycle",
      memberTasks: "User-started tasks",
      memberTasksDescription: "Tasks submitted by workspace users.",
      bustlyOutcomes: "Bustly outcomes",
      bustlyOutcomesDescription: "Classified work results ready for review.",
      overviewEyebrow: "Current subscription cycle",
      overviewHeadline: "Bustly handled core {{system}} operations this cycle",
      overviewHeadlineFallback: "Bustly handled core operations this cycle",
      overviewDescription:
        "The team started {{taskCount}} Tasks, with {{userCount}} users involved. {{coreTaskCount}} Tasks focused on {{taskArea}}, making up {{share}} of all user-started Tasks.",
      overviewUserStartedTasksMetric: "User-started Tasks",
      overviewActiveUsersMetric: "Users involved",
      overviewTopAreaTasksMetric: "Tasks in top area",
      overviewTopAreaShareMetric: "Share of user-started Tasks",
      overviewValueJudgmentTitle: "Core value assessment",
      overviewTaskAreasTitle: "Top Task areas this cycle",
      overviewTaskAreasDescription:
        "Sorted by user-started Tasks in this subscription cycle.",
      overviewTaskAreaColumn: "Task area",
      overviewTaskCountColumn: "Tasks",
      overviewUserCountColumn: "Users",
      overviewRepresentativeWorkColumn: "Representative work",
      overviewInsightsTitle: "Efficiency gains Bustly has already created",
      overviewInsightsDescription:
        "These gains come from real Tasks, not general product claims.",
      overviewStandardWorkflowTitle: "First workflow to standardize",
      overviewReviewSuggestionTitle:
        "Review the outcome ledger each week, not only usage",
      overviewChampionsTitle: "Champion users already running repeat Tasks",
      overviewChampionsDescription:
        "These users are using Bustly repeatedly in real work, not just testing it.",
      overviewNextCycleTitle: "Pick 3 goals for the next cycle",
      overviewNextCycleDescription:
        "Avoid broad training. Turn proven, frequent Tasks into a team workflow.",
      estimatedTimeSaved: "Estimated time saved",
      estimatedTimeSavedDescription:
        "Calculated after task duration rules are connected.",
      estimatedValue: "Outcome estimate",
      estimatedValueDescription:
        "Calculated after estimate rules are connected.",
      handoffEyebrow: "Outcome handoff",
      handoffTitle: "Outcome handoff",
      handoffDescription:
        "Each outcome will show its scope, evidence, source task, and review basis.",
      byOutcome: "By outcome",
      byTask: "By task",
      outcomeInbox: "Outcome inbox",
      outcomeRecords: "Outcome records",
      outcomesInRange:
        "{{shown}} / {{total}} outcomes in this subscription cycle",
      searchOutcomes: "Search outcomes or source tasks",
      clearSearch: "Clear search",
      clearFilters: "Clear filters",
      paginationRange: "Showing {{start}} to {{end}} of {{total}} outcomes",
      paginationPage: "Page {{current}} of {{total}}",
      previousPage: "Previous page",
      nextPage: "Next page",
      jumpToPage: "Go to page",
      pageNumber: "Page number",
      goToPage: "Go",
      noMatchingOutcomes: "No matching outcomes.",
      noMatchingTasks: "No matching tasks.",
      detailTabOverview: "Outcome",
      detailTabEvidence: "Proof",
      outcomeType: "Outcome type",
      businessModule: "Business area",
      allOutcomeTypes: "All outcome types",
      allBusinessAreas: "All business areas",
      executionOutcomes: "Execution outcomes",
      executionOutcomesDescription:
        "Operational changes completed in connected tools.",
      judgmentOutcomes: "Judgment outcomes",
      judgmentOutcomesDescription:
        "Decisions, checks, or comparisons Bustly prepared.",
      stewardshipOutcomes: "Stewardship outcomes",
      stewardshipOutcomesDescription:
        "Monitoring, follow-up, and ongoing watch work.",
      blockerOutcomes: "Blocker resolution",
      blockerOutcomesDescription: "Work that moved a stalled task forward.",
      adsModule: "Ads and acquisition",
      productModule: "Product, inventory, merchandising",
      customerModule: "Customer, membership, CRM",
      contentModule: "Content, SEO, page assets",
      emptyTitle: "Workspace outcomes will show up here.",
      emptyDescription:
        "After Bustly classifies tasks, each outcome includes its scope, evidence, and source task.",
      scopeField: "Scope",
      evidenceField: "Evidence",
      sourceTaskField: "Source task",
      businessScope: "Scope",
      evidenceSummary: "Evidence summary",
      systemsEvidence: "Integrations",
      sourceTask: "Source task",
      taskType: "Task type",
      taskStatus: "Task status",
      acceptanceStatus: "Acceptance status",
      memberShort: "User",
      messagesShort: "Messages",
      executionShort: "Execution",
      outcomesShort: "Outcomes",
      taskOutcomes: "Task outcomes",
      traceId: "Source",
      bustlyCost: "Recorded processing cost",
      tokensProcessed: "Processing volume",
      missingInputs: "Missing inputs",
      executionActionCount: "{{count}} actions",
      collaborationRecordCount: "{{count}} collaboration records",
      viewOutcomeDetail: "View outcome details",
      outcomeDetail: "Outcome details",
      outcomeDetailPosition: "Item {{current}} of {{total}}",
      closeOutcomeDetail: "Close outcome details",
      backToList: "Back to list",
      detailLoadError: "Detail failed to load. Showing the list summary.",
      outcomeInformation: "Outcome description",
      concreteOutput: "What Bustly delivered",
      memberNextStep: "User next step",
      bustlyProcessingJudgment: "Bustly judgment",
      classificationBasis: "Why this counts",
      establishmentConditions: "Evidence and next step",
      billingAcceptanceBasis: "Acceptance basis",
      assistantMessages: "Bustly replies",
      billingEvents: "Supporting records",
      collaborationRecords: "Collaboration records",
      systems: "Systems",
      riskNextStep: "Risk and next step",
      nonBillableAndDispute: "Outcome inclusion and review basis",
      nonBillableCheck: "Inclusion check",
      disputeBasis: "Review basis",
      traceRecord: "Source record",
      taskOverview: "Task overview",
      updatedAt: "Updated",
      traceability: "Evidence path",
      traceabilityTitle: "Evidence path",
      memberMessages: "User messages",
      memberMessagesDescription: "Original requests and follow-up context.",
      systemExecution: "System execution",
      systemExecutionDescription: "Actions, tools, and data sources used.",
      traceConversation: "Source conversation",
      traceConversationLoading: "Loading full source conversation...",
      traceConversationLoaded: "{{count}} user/Bustly messages",
      traceConversationExecutionCollapsed:
        "{{count}} execution records collapsed",
      traceConversationMissing:
        "No source conversation is available for this snapshot.",
      traceConversationError: "Source conversation could not be loaded.",
      laborOutcomes: "Outcomes",
      laborOutcomesDescription: "Results Bustly can hand off.",
      acceptanceBasis: "Acceptance basis",
      acceptanceBasisDescription: "Evidence for reviewing the outcome.",
      valueModel: "Outcome estimate",
      valueModelTitle: "Savings basis",
      timeCost: "Time cost",
      timeCostDescription: "Estimated human effort for similar tasks.",
      laborCost: "Labor cost",
      laborCostDescription: "Workspace labor rate used for outcome estimates.",
      economicValue: "Outcome estimate",
      economicValueDescription:
        "Calculated estimate after time and labor rules are available.",
    },
    auditLog: {
      title: "Audit log",
      subtitle: "Review the member, apps, source task, and result for each action.",
      eventCount: "{{count}} events",
      executionCount: "{{count}} executions",
      loading: "Loading cloud audit events...",
      workspaceUnavailable:
        "Choose a workspace to review its cloud audit events.",
      forbidden: "You don't have permission to view the audit log.",
      loadError: "We couldn't load cloud audit events. Try again.",
      tryAgain: "Try again",
      empty: "Audit logs will appear here as activity is recorded.",
      emptyTitle: "No audit logs yet",
      noMatches: "Try changing or clearing your filters.",
      noMatchesTitle: "No matching audit logs",
      clearFilters: "Clear filters",
      searchPlaceholder: "Search audit logs, apps, or members",
      allApplications: "All apps",
      allInitiators: "All members",
      today: "Today",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      last90Days: "Last 90 days",
      allTime: "All time",
      allPlatforms: "All platforms",
      allMembers: "All members",
      allStatuses: "All statuses",
      allOperations: "All experts",
      filterByApplication: "Filter by app",
      filterByInitiator: "Filter by member",
      filterByTime: "Filter by time",
      filterSelectedCount: "{{count}} selected",
      pagination: "Audit log pages",
      previousPage: "Previous",
      nextPage: "Next",
      pageNumber: "Page {{page}}",
      showingPageCount: "Showing {{count}} audit log{{plural}}",
      showSystemEvents: "Show system events",
      systemEventsHidden:
        "{{count}} system events are hidden. Turn on Show system events to review them.",
      platform: "Platform",
      member: "Member",
      initiator: "Member",
      involvedApps: "Apps",
      executionContent: "Execution",
      pendingLocation: "Pending",
      sourceTask: "Source task",
      appReadWrite: "App reads/writes",
      readWriteCount: "Read {{read}} · Write {{write}}",
      keyActions: "Key actions",
      timeline: "Timeline",
      originalConversation: "Original conversation",
      originalConversationWithCount: "Original conversation {{count}}",
      viewOriginalConversation: "View conversation",
      taskSummary: "Task summary",
      viewTaskSummary: "View Task summary",
      taskGoal: "Task goal",
      taskOutcome: "Task outcome",
      taskSummaryExplanation:
        "This summary shows the sanitized goal recorded for the Task and its sanitized outcome. It is not the full conversation.",
      taskSummaryOmitted: "This value was withheld by the audit safety policy.",
      taskSummaryAbsent: "No value was recorded for this Task.",
      taskSummaryLegacy:
        "This historical Task has no verified audit-safe summary.",
      taskSummaryLoading: "Loading Task summary...",
      taskSummaryError: "We couldn't load the Task summary.",
      taskSummaryUnavailable: "This Task summary is not available.",
      action: "Action",
      readApplication: "Read app",
      writeApplication: "Write app",
      noBusinessEvents: "This execution has no business events to display.",
      conversationEmptyTitle: "Original conversation not recorded",
      conversationEmptyBody:
        "This run does not include original conversation content.",
      conversationUnavailable:
        "Original conversation content is not available.",
      conversationLoading: "Loading original conversation...",
      conversationForbidden:
        "You don't have permission to view the original conversation.",
      conversationError: "We couldn't load the original conversation.",
      pluginForbidden:
        "Only workspace owners can view BizSidekick audit records in V1.",
      pluginEmpty: "BizSidekick Task activity will show up here.",
      pluginUnavailable:
        "We couldn't load the BizSidekick audit log. Try again.",
      userRole: "User",
      toolRole: "Tool",
      connection: "Connection",
      resource: "Resource",
      operation: "Operation",
      status: "Status",
      pluginRequestUnits: "Plugin request units",
      pluginConsumed: "Consumed",
      pluginRemaining: "Remaining",
      pluginPeriod: "Current billing period",
      pluginTool: "Tool",
      pluginUnits: "Units",
      pluginRequest: "Request",
      pluginNoEvents: "Plugin request usage will show up here.",
      pluginLoadFailed: "Failed to load Plugin request usage.",
      pluginMvpTitle: "Usage metering is coming soon",
      pluginMvpDescription:
        "Request units and usage charges are not part of the current Plugin MVP.",
      changedFields: "Changed fields",
      viewContentDetails: "View content details",
      contentDetails: "Content details",
      contentLoading: "Loading content details...",
      contentUnavailable: "Content is not available for this event.",
      contentNotCaptured: "No content was captured for this event.",
      contentRedacted: "Content is unavailable because it was redacted.",
      contentDeleted: "Content is no longer available.",
      contentUnknown: "Content availability is not available for this event.",
      contentForbidden: "You don't have permission to view content details.",
      contentError: "We couldn't load content details.",
      close: "Close",
      closeAuditDetails: "Close audit details",
      viewDetails: "View details",
      permissionDeniedTitle: "Permission Denied",
      permissionDeniedDescription:
        "You do not have permission to view the audit log.",
      loadFailedTitle: "Audit logs couldn't load",
      eventTimeline: "Cloud event details",
      noEventSelected: "Select an event to review its facts.",
      executionTime: "Execution time",
    },
    sidebar: {
      welcome: "Welcome",
    },
    clientAuth: {
      logOut: "Log out",
      returnToClient: "Please Return to Bustly Desktop to Continue",
      openBustly: "Open Bustly",
      openBustlyAria: "Open Bustly",
      clientMockupAlt: "Bustly Desktop Mockup",
      mismatchTitle: "Accounts Mismatch",
      mismatchDescription:
        "The website is currently using {{account}}, which does not match the account active in Bustly Desktop. Switch to the same account to keep your workflow in sync.",
      switchToClientAccount: "Switch to Bustly Desktop account",
      continueWithWebsiteAccount: "Continue with the website account",
      successTitle: "You have successfully logged in!",
      redirectPrompt: "Not automatically redirected?",
      pluginSuccessMessage: "You're signed in. You can close this tab.",
      pluginOpeningMessage: "Returning you to your AI app...",
      pluginHandoffError: "We couldn't return you to your AI app. Try again.",
      openAiApp: "Open your AI app",
      openAiAppAria: "Open your AI app",
      pluginOpeningAction: "Opening your AI app...",
      pluginRetryAction: "Try again",
      prepareDesktopHandoffFailed: "Failed to prepare the desktop handoff.",
    },
    logoutDialog: {
      title: "Log out?",
      signedInAs: "You're signed in as {{account}}",
      accountFallback: "your account",
      confirm: "Log out",
      cancel: "Stay signed in",
    },
    auth: {
      continueWithGoogle: "Continue with Google",
      or: "Or",
      emailPlaceholder: "Enter your email address",
      referralInviteFrom: "{{name}} invited you to Bustly",
      referralInviteGeneric: "You're invited to try Bustly",
      referralCreateAccount: "Create your account",
      referralBenefitDescription:
        "Get 10% off the first subscription payment",
      referralBenefitDetails:
        "Create a new workspace through this invite link. The discount applies automatically to its first subscription payment, up to US$500.",
      referralLoading: "Preparing your referral benefit…",
      referralUnavailableTitle: "This referral link is unavailable.",
      referralContinueWithout: "Continue without referral",
      welcomeBack: "Welcome back",
      continueWithVerificationCode: "Continue with verification code",
      continueWithPassword: "Continue with password",
      verifyEmailAddress: "Verify your email address",
      verifyEmailDescription:
        "Enter the code sent to {{email}} to complete verification.",
      verificationCodePlaceholder: "Enter verification code",
      resend: "Resend",
      signInWithPasswordInstead: "Sign in with password instead",
      enterPassword: "Enter your password",
      email: "Email",
      emailAddress: "Email address",
      editEmail: "Edit",
      password: "Password",
      forgotPassword: "Forgot password?",
      passwordPlaceholder: "Enter password",
      signInWithVerificationCodeInstead:
        "Sign in with verification code instead",
      createAccount: "Create your account",
      createPassword: "Create your password",
      setPasswordToContinue: "Set your password to continue",
      minPasswordPlaceholder: "Min 8 characters",
      resetPassword: "Reset your password",
      resetPasswordDescription:
        "We will send a 6-digit verification code to the following address to verify your account.",
      resetPasswordButton: "Reset password",
      sendingEmailCode: "Sending email code...",
      resetPasswordCodeDescription:
        "We have sent a verification code to {{email}}.",
      verificationCode: "Verification code",
      newPassword: "New password",
      newPasswordPlaceholder: "Enter new password",
      confirmPassword: "Confirm Password",
      confirmNewPasswordPlaceholder: "Confirm new password",
      updatePassword: "Update password",
      resetUpdatingPassword: "Updating password...",
      legalPrefix: "By continuing, you agree to the",
      legalAnd: "and",
      termsOfService: "Terms of service",
      privacyPolicy: "Privacy policy",
      showPassword: "Show password",
      hidePassword: "Hide password",
      passwordSetSuccessfully: "Password set successfully!",
      accountSetupComplete: "Account setup complete!",
      verificationCodeResent: "Verification code resent!",
      connectError: "An error occurred during connection",
      connectStore: "Connect Store",
      connecting: "Connecting...",
      errors: {
        incorrectVerificationCode: "Incorrect verification code",
        expiredVerificationCode: "Expired verification code",
        passwordIncorrect: "That password doesn't look right.",
        verifyEmailFirst:
          "Please check your inbox and verify your email first.",
        emailAlreadyUsed:
          "This email is already in use. Try signing in instead.",
        passwordTooWeak: "This password cannot be used. Choose a different password.",
        passwordCompromised: "This password has appeared in a data breach. Choose a different password.",
        passwordReused:
          "Your new password must be different from your current password.",
        checkAndTryAgain:
          "Something doesn't look right. Please check and try again.",
        slowDown: "Whoa, slow down! Give it a moment and try again.",
        serverMoment:
          "Our servers are having a moment. Please try again later.",
        signUpUnavailable: "Signing up is currently unavailable.",
        connectionTrouble:
          "Having trouble connecting. Check your internet and try again.",
      },
      validation: {
        enterEmail: "Please enter your email address",
        enterValidEmail: "Please enter a valid email address",
        enterPassword: "Please enter your password",
        passwordMin: "Password should be at least 8 characters",
        providerPasswordMin: "Password should be at least {{length}} characters.",
        enterSixDigitCode: "Please enter the 6-digit verification code.",
        enterNewPassword: "Please enter your new password.",
        passwordsDoNotMatch: "Passwords do not match.",
      },
    },
    authError: {
      otpExpired: {
        title: "Link Expired",
        description:
          "The password reset link has expired. Please request a new one.",
      },
      accessDenied: {
        title: "Access Denied",
        description: "You do not have permission to access this resource.",
      },
      emailNotConfirmed: {
        title: "Email Not Verified",
        description: "Please verify your email address before continuing.",
        action: "Resend Verification Email",
      },
      invalidCredentials: {
        title: "Invalid Credentials",
        description: "The email or password you entered is incorrect.",
        action: "Try Again",
      },
      shopifyDevelopmentStoreBlocked: {
        title: "Development Store Not Supported",
        description:
          "Bustly can only connect to live Shopify stores. Please connect a real merchant store instead.",
        toast:
          "Development stores are not supported. Please connect a live Shopify merchant store.",
      },
      default: {
        title: "An Error Occurred",
        description: "Something went wrong. Please try again later.",
        action: "Go Back",
      },
      closingConnectionWindow: "Closing connection window",
      backToSignIn: "Back to Sign In",
    },
    ownerTransferAccept: {
      missingToken:
        "This ownership transfer link is missing required information.",
      invalidOrExpired: "This ownership transfer link is invalid or expired.",
      acceptedRefreshFailed:
        "Ownership was accepted, but we could not refresh your workspace list.",
      acceptFailed: "Failed to accept ownership transfer. Please try again.",
      anotherAccount: "another account",
      acceptedTitle: "Ownership accepted",
      expiredTitle: "Ownership transfer link expired",
      cancelledTitle: "Ownership transfer cancelled",
      completedTitle: "Ownership transfer completed",
      unavailableTitle: "Ownership transfer unavailable",
      acceptTitle: "Accept ownership of {{workspace}}",
      thisWorkspace: "this workspace",
      expiredDescription:
        "This ownership transfer link has expired. Ask the current owner to send a new transfer invitation.",
      cancelledDescription:
        "This ownership transfer invitation was cancelled by the current owner. Ask them to send a new invitation if you still need access.",
      completedDescription:
        "This ownership transfer has already been accepted.",
      unavailableDescription:
        "This ownership transfer is no longer available. Ask the current owner to send a new invitation if needed.",
      signingOut: "Signing out...",
      loggingOut: "Logging out...",
      signOutAndSwitch: "Sign out and switch account",
      logOut: "Log out",
      currentOwnerFallback: "The current owner",
      invitedDescription:
        "{bustly-ai} invited you to become the owner of {{workspace}}.",
      loading: "Loading transfer request...",
      differentAccountDescription:
        "This transfer request is for {{email}}. Sign in with that account to accept it.",
      newOwner: "New owner",
      pendingUser: "Pending user",
      signInToContinue: "Sign in to continue",
      accepting: "Accepting...",
      acceptOwnership: "Accept ownership",
    },
    workspaceSettings: {
      ownershipTransferredTitle: "Ownership transferred",
      ownershipTransferred:
        "{{name}} is now the Owner. Your role is now {{role}}.",
      ownerTransferRequestSentTitle: "Confirmation email sent",
      ownerTransferRequestSent:
        "{{name}} will become Owner after accepting the confirmation email.",
      ownerTransferPendingTitle: "Ownership transfer pending",
      ownerTransferPendingDescription:
        "Waiting for {{name}} to accept the ownership transfer email.",
      ownerTransferSentAt: "Sent at {{date}}",
      ownerTransferExpiresAt: "Expires at {{date}}",
      cancelOwnershipTransfer: "Cancel transfer",
      cancellingOwnershipTransfer: "Cancelling...",
      ownershipTransferCancelled: "Ownership transfer request cancelled.",
      ownerTransferNoLongerPending:
        "This ownership transfer request has expired or is no longer pending. The page has been refreshed.",
      transferFailedTitle: "Transfer failed",
      transferFailed: "Transfer failed: {{reason}}",
      transferFailedFallback: "Failed to transfer workspace ownership.",
      workspaceLogo: "Workspace Logo",
      uploading: "Uploading...",
      uploadLogo: "Upload Logo",
      workspaceName: "Workspace Name",
      industry: "Industry",
      selectIndustry: "Select your industry...",
      specifyIndustry: "Please specify your industry",
      specifyIndustryPlaceholder: "e.g. Manufacturing, Real Estate, etc.",
      industries: {
        ecommerce: {
          label: "E-commerce",
          description: "Operate your store with more clarity and control",
        },
        retail: {
          label: "Retail",
          description: "Keep stores, teams, and operations moving together",
        },
        restaurants: {
          label: "Restaurants",
          description: "Stay on top of service, demand, and daily operations",
        },
        fitness: {
          label: "Fitness",
          description: "Run your business with more consistency and momentum",
        },
        beauty: {
          label: "Beauty",
          description: "Keep appointments, clients, and growth on track",
        },
        agencies: {
          label: "Agencies",
          description: "Bring more structure to delivery and client operations",
        },
        "home-services": {
          label: "Home Services",
          description: "Keep jobs, teams, and service running smoothly",
        },
        automotive: {
          label: "Automotive",
          description: "Run a more efficient and responsive service operation",
        },
        events: {
          label: "Events",
          description: "Keep planning, coordination, and execution aligned",
        },
        education: {
          label: "Education",
          description: "Support day-to-day operations with more consistency",
        },
        other: {
          label: "Other",
          description: "Shape Bustly around how your business operates",
        },
      },
      saving: "Saving...",
      saveChanges: "Save Changes",
      transferOwnership: "Transfer workspace ownership",
      transferOwnershipDescription:
        "Send an ownership transfer confirmation email. The new owner only takes over after they open the link and accept.",
      transfer: "Transfer",
      selectNewOwner: "Select new owner",
      selectUser: "Select a user...",
      searchUsers: "Search users...",
      noUsersFound: "No users found",
      ownershipTransferPreviewPrefix: "Once transferred, ",
      selectedTransferUserFallback: "the selected user",
      ownershipTransferPreviewMiddle:
        " will become the new Workspace Owner with full control over billing, settings, and users. You will remain in this workspace as an ",
      ownershipTransferPreviewSuffix: ".",
    },
    admin: {
      unsavedChangesTitle: "Unsaved Changes",
      unsavedChangesDescription:
        "You have unsaved changes. Are you sure you want to leave? Your changes will be lost.",
      discardAndLeave: "Discard & Leave",
    },
    subscriptionExpired: {
      illustrationAlt: "Bustly renewal illustration",
      warningIllustrationAlt: "Billing warning illustration",
      title: "Keep Bustly running",
      description: "Choose a plan to continue with Bustly.",
      viewPlans: "View plans",
      expiredDialogTitle: "Your subscription has expired",
      cancelledDialogTitle: "Your subscription has been canceled",
      dialogDescription: "Update your payment details to get back on track.",
      updatePaymentInfo: "Update Payment Info",
    },
    membersSettings: {
      allRoles: "All roles",
      filterRoles: "Filter roles...",
      noRolesFound: "No roles found",
      clear: "Clear",
      clearUserSearch: "Clear user search",
      noRolesAvailable: "No roles available",
      selectRole: "Select role",
      newRole: "New Role",
      removeUser: "Remove user",
      remove: "Remove",
      accountsTab: "Members",
      rolesTab: "Roles",
      searchUsers: "Search members...",
      addNewUser: "Invite member",
      inviteUser: "Invite member",
      inviteUserDescription:
        "Enter an email address and choose a Workspace role.",
      inviteStepOne: "Step 1 of 2",
      inviteStepTwo: "Step 2 of 2",
      workspaceRole: "Workspace role",
      nextAssignApplicationAccess: "Choose app access",
      assignApplicationAccess: "Assign app access",
      initialApplicationAccess: "Select Projects and assign app permissions",
      selectBusinessGroupForApplicationAccess:
        "Choose a Business group to configure its app permissions",
      noManageableApplicationResources: "No app resources available",
      noManageableApplicationResourcesDescription:
        "The invited user will join the default Business group automatically. A Business group Owner sets app permissions.",
      initialApplicationAccessDescription:
        "Choose where the invited user can use app accounts. This does not add them as a Project member.",
      applicationAccessStillLoading:
        "App permissions are still loading. Try again in a moment.",
      applicationAccessUnavailable: "App permissions are unavailable right now",
      applicationAccessUnavailableDescription:
        "You can still send the Workspace invitation and assign app permissions later.",
      assignApplicationAccessDescription:
        "Choose the connected app accounts {{email}} can use after joining the Workspace.",
      brand: "Project",
      defaultBrand: "Default Project",
      otherBrand: "Other Project",
      addOtherBrandApplicationAccess: "Add Project",
      addBusinessGroup: "Add Project",
      removeBusinessGroup: "Don't assign",
      selectBrand: "Select a Project",
      noBrandsForAccess: "No Projects available",
      noBrandsForAccessDescription:
        "The default Project is unavailable. Refresh the page and try again.",
      noOwnedBusinessGroupsForAccess: "No Business groups you manage",
      noOwnedBusinessGroupsForAccessDescription:
        "The invited user will join the default Business group automatically. A Business group Owner sets app permissions.",
      loadingProjectsForAccess: "Loading Projects...",
      noAssignableApplicationAccounts: "No app accounts you can assign",
      noAssignableApplicationAccountsDescription:
        "Ask the connection owner or Workspace Owner to invite this user and assign app access.",
      otherConnectedApplications: "Other connected apps",
      otherConnectedApplicationsDescription:
        "These apps are not assigned to the selected Projects, so they cannot be granted with this invitation.",
      applicationAccountAccess: "App account access",
      noAccess: "No access",
      viewOnly: "View only",
      viewAndTakeAction: "Can take action",
      applicationAccountsSelected: "{{count}} app accounts selected",
      setAllApplicationAccess: "Set all",
      setAllViewOnly: "All view only",
      setAllViewAndAction: "All view & action",
      selectApplicationAccountToInvite:
        "Select at least one app account to send the invite.",
      sendInvitation: "Send invite",
      sendInvitationAndSaveAccess: "Send invite and save access",
      sendingInvitation: "Sending invite...",
      user: "Member",
      role: "Role",
      businessGroups: "Business groups",
      status: "Status",
      joinedDate: "Joined",
      actions: "Actions",
      noUsersMatching: 'No users found matching "{{query}}"',
      showingUsersRange: "Showing {{start}} to {{end}} of {{total}} users",
      pagination: "User pages",
      previousPage: "Previous page",
      nextPage: "Next page",
      pageNumberLabel: "Page {{page}}",
      emailAddress: "Email Address",
      inviteEmailPlaceholder: "colleague@company.com",
      resendInvite: "Resend",
      resendingInvite: "Resending...",
      invitationResent: "Invitation resent",
      invitationResentDescription:
        "We sent a new invitation email to {{email}}. They can join “{{workspace}}” by clicking “Join the Workspace” in the email.",
      invitationSent: "Invitation sent",
      invitationSentDescription:
        "We sent an invitation email to {{email}}. They can join “{{workspace}}” by clicking “Join the Workspace” in the email.",
      invitationFailed: "Invitation Failed",
      applicationAccessChanged:
        "App permissions changed. Review the latest access levels and send the invitation again.",
      failedToInviteUser: "Failed to invite user.",
      statusPending: "Pending",
      statusActive: "Active",
      emailRequired: "Email is required",
      emailInvalid: "Please enter a valid email address",
      emailAlreadyMember: "This email is already a member.",
      selectedRoleNoLongerAvailable: "Selected role is no longer available.",
      selectedAccessRoleInvalid: "Selected access role is invalid.",
      invitationResendFailed: "Resend failed",
      failedToResendInvite: "Failed to resend invitation.",
      cancelInvite: "Cancel",
      cancellingInvite: "Cancelling...",
      invitationCancelled: "Invitation cancelled",
      invitationCancelledDescription:
        "The pending invitation for {{email}} has been cancelled.",
      invitationCancelFailed: "Cancel failed",
      failedToCancelInvite: "Failed to cancel invitation.",
      removeUserConfirmation: "Are you sure you want to remove",
      removeUserIrreversible: "? This action cannot be undone.",
      integrationOwnerRemovalWarning:
        "This user manages access for {{count}} connection{{plural}}. Access management will transfer to the Workspace Owner.",
      newOwner: "New owner",
      thisUser: "This user",
      transferOwnershipDescription:
        "will receive a confirmation email. They become the new owner only after accepting it. You'll remain in this workspace as an",
      transferring: "Sending...",
      transferOwnership: "Send confirmation",
      userRemoved: "User Removed",
      userRemovedDescription: "The user has been removed from the workspace.",
      removalFailed: "Removal Failed",
      failedToRemoveUser: "Failed to remove user.",
      workspaceUnavailable: "Workspace is not available.",
      selectedRoleUnavailable:
        "Selected role is not available for this workspace.",
      updateFailed: "Update Failed",
      cannotChangeOwnRole: "You cannot change your own role.",
      roleUpdated: "Role Updated",
      roleUpdatedDescription: "{{role}} role has been updated successfully.",
      failedToUpdateMemberRole: "Failed to update member role.",
      usersReassigned: "Users Reassigned",
      usersReassignedDescription:
        "Updated {{count}} user{{plural}} to {{role}}.",
      reassignmentFailed: "Reassignment Failed",
      failedToUpdateMembers: "Failed to update one or more members.",
      rolePermissionsUnavailable:
        "Workspace role permissions are not available.",
      roleSaved: "Role Saved",
      roleSavedDescription: "Role permissions have been saved successfully.",
      saveFailed: "Save Failed",
      failedToSaveRolePermissions: "Failed to save role permissions.",
      roleDeleted: "Role Deleted",
      roleDeletedDescription: "The role has been deleted successfully.",
      deleteFailed: "Delete Failed",
      failedToDeleteRole: "Failed to delete role.",
      roleAccessUnavailable: "Role access unavailable",
      failedToLoadNewRoleAccess:
        "Failed to load connection access for the new role.",
      couldNotResolveCurrentOwner: "Could not resolve current workspace owner.",
      unableToLoadUsers: "Unable to load users",
      tryAgainInMoment: "Try again in a moment.",
      retry: "Retry",
      upgradeRequiredTitle: "You need to upgrade",
      inviteUpgradeDescription:
        "You’re currently on Solo. Upgrade to Team to add users to this workspace.",
      upgradeToInviteMembersTitle: "Upgrade to invite members",
      soloInviteLimitDescription:
        "Your Solo plan supports 1 workspace user. Choose a Team plan to invite members.",
      rolesUpgradeDescription:
        "You’re currently on Solo. Upgrade to Team to create and manage workspace roles.",
      viewTeamPlan: "View Team plan",
      workspaceUserLimitReachedTitle: "Workspace user limit reached",
      workspaceUserLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}} workspace users. Choose a plan with a higher user limit to invite more members.",
      workspaceUserLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}} workspace users. Contact Sales if you need more workspace users.",
      workspaceUserLimitPlansFallbackDescription:
        "This workspace has reached its user limit. Choose a plan with a higher user limit to invite more members.",
      workspaceUserLimitSalesFallbackDescription:
        "Your workspace has reached its user limit. Contact Sales if you need more workspace users.",
      pluginWorkspaceUserLimitDescription:
        "Your Plugin plan supports up to {{limit}} workspace user. This Plugin workspace does not have self-serve upgrades.",
      ownerRole: "Workspace owner",
      adminRole: "Admin",
      billingRole: "Billing manager",
      memberRole: "Member",
      ecommerceOpsRole: "Ecommerce Operations",
      paidMediaRole: "Paid media & growth",
      seoContentRole: "SEO & content growth",
      socialInfluencerRole: "Social & influencer marketing",
      crmLifecycleRole: "CRM & lifecycle marketing",
      businessInsightsRole: "Business analyst",
      siteExperienceRole: "Site experience & QA",
      editRole: "Edit role",
      roleName: "Role name",
      roleNamePlaceholder: "e.g. Marketing, Finance",
      roleNameExists:
        "A role with this name already exists. Choose a different name.",
      workspacePermissions: "Workspace permissions",
      workspaceManagement: "Workspace management",
      businessGroupManagement: "Business group management",
      brands: "Business groups",
      manageWorkspaceSettings: "Manage workspace settings",
      manageWorkspaceSettingsDescription:
        "Users with this role can update how this workspace is set up.",
      manageUsers: "Manage members",
      manageUsersDescription:
        "Users with this role can manage who has access to this workspace.",
      manageRolesAndAccess: "Manage roles",
      manageRolesAndAccessDescription:
        "Users with this role can decide what each role can access and do.",
      manageIntegrations: "Manage integrations",
      manageIntegrationsDescription:
        "Users with this role can connect and disconnect integrations, and manage access to connected accounts.",
      integrationManagementScopeDescription:
        "Select the Business groups where this role can manage integrations. Select none for no access, or all Business groups for full access.",
      integrationManagementAllBrands: "All Business groups",
      manageConnectionAccess: "Manage connection access",
      manageConnectionAccessDescription:
        "Users with this role can set each user's access to connected accounts: No access, View only, or View & take action.",
      manageBilling: "Manage billing",
      manageBillingDescription:
        "Users with this role can manage subscriptions, payment methods, invoices, and Work Unit purchases.",
      viewUsage: "View usage",
      viewUsageDescription:
        "Users with this role can see how Work Units are being used in this workspace.",
      viewOutcomeSummary: "View Outcome Summary",
      viewOutcomeSummaryDescription:
        "Users with this role can view Outcome Summary for this workspace.",
      manageOutcomeSummary: "Manage Outcome Summary",
      manageOutcomeSummaryDescription:
        "Users with this role can turn daily updates on or off and refresh Outcome Summary.",
      manageWorkspaceSkills: "Manage workspace skills",
      manageWorkspaceSkillsDescription:
        "Users with this role can view and delete skills created by workspace members.",
      manageBrandAccess: "Create and manage Business groups",
      manageBrandAccessDescription:
        "Users with this role can create, edit, and delete Business groups they created.",
      viewAuditLog: "View audit log",
      viewAuditLogDescription:
        "Users with this role can view this workspace's audit log, original conversations, and content details.",
      connectionAccess: "Connection access",
      createRole: "Create Role",
      saveRoleChangesTitle: "Save role changes?",
      saveRoleChangesWithUsers:
        "This role is currently assigned to {{count}} user{{plural}}. Saving will update permissions for everyone using this role.",
      saveRoleChangesDescription:
        "Saving will update this role everywhere it is used going forward.",
      assignedTo: "{{count}} user{{plural}}",
      editRoleAria: "Edit {{role}} role",
      deleteRole: "Delete role",
      deleteRoleAria: "Delete {{role}} role",
      deleteRoleDescription:
        'Are you sure you want to delete the role "{{role}}"?',
      manageRolesPermissionDenied:
        "You do not have permission to manage roles and access.",
      rolesIntro: "Roles determine what members can do in the workspace.",
      noRolesYet: "No roles yet",
      noRolesYetDescription:
        "Create a role to define tailored access for your team.",
      reassignUsersBeforeDeleting: "Reassign users before deleting",
      reassignRoleBeforeDeleteDescription:
        'Role "{{role}}" is assigned to {{count}} user{{plural}}. Choose a new role and we\'ll reassign them, then delete this role in one step.',
      newRoleForTheseUsers: "New role for these users",
      noReplacementRoleAvailable:
        "No replacement role available for these users.",
      usersUsingThisRole: "Users using this role",
      reassigning: "Reassigning...",
      reassignAndDeleteRole: "Reassign & Delete Role",
    },
    onboarding: {
      common: {
        selfReferral: "You can’t use your own invite link. Use an invite link from someone else.",
        prepareWorkspaceFailed: "We couldn’t create your workspace. Try again.",
        loadFailed: "We couldn’t load your workspace information. Please try again.",
        back: "Back",
        skip: "Skip",
        continue: "Continue",
        close: "Close",
        logOut: "Log out",
      },
      runtime: {
        provisioning: "Provisioning cloud environment...",
        configuring: "Configuring your workspace...",
        preparing: "Preparing always-on execution...",
        finalizing: "Finalizing your cloud setup...",
        inlineTitle: "Just a moment, and Bustly will be ready to work.",
        fullscreenTitle: "Your Bustly cloud workspace is being set up",
        fullscreenSubtitle: "Just a moment, it'll be ready soon.",
        setupProgress: "Setup Progress",
      },
      failure: {
        title: "We are still checking your workspace",
        description:
          "Your payment or workspace activation is still being confirmed. Please try again in a moment.",
        returnToOnboarding: "Return to onboarding",
        goToAdmin: "Go to Admin",
      },
      notice: {
        dismiss: "Dismiss repeated store notice",
        freeTrialUnavailableTitle: "Free trial unavailable",
        freeTrialUnavailableDescription:
          "This store is already connected to another workspace or has used a Bustly free trial, so it can't start a new free trial here.\n\nConnect another store to continue Plugin setup.",
        freeTrialUnavailableWorkspaceFallback: "Another workspace",
        freeTrialUnavailableCancel: "Connect another store",
        freeTrialUnavailableContinue: "Continue setup",
        freeTrialUnavailableContinueSetup: "Continue setup",
        storeLimitReachedTitle: "Store connection limit reached",
        storeLimitReachedDescription:
          "Your current plan has reached its store connection limit. Upgrade or contact Sales to connect more stores.",
        freeTrialUsedTag: "Free trial used",
        freeTrialUsedTooltip:
          "This account has already used a free trial in another workspace.",
        freeTrialUsedAppTooltip:
          "One or more connected accounts have already used a free trial.",
        freeTrialUnavailableBannerTitle:
          "Free trial unavailable for this workspace",
        freeTrialUnavailableBannerDescription:
          "Because one or more connected accounts have already used a free trial, a paid plan subscription will be required at checkout.",
        workspaceNoticeTitle: "Workspace notice",
        storeLimitNoticeTitle: "Store connection limit reached",
      },
      invite: {
        thisWorkspace: "this workspace",
        joinWorkspaceTitle: "Join {{workspace}}",
        joinWorkspace: "Join a workspace",
        teammate: "A teammate",
        singleSubtitle:
          "{{inviter}} invited you to join this workspace as {{article}} {{role}}.",
        multipleSubtitle: "You have {{count}} pending invitation{{plural}}.",
        acceptFailed: "Failed to accept invitation. Please try again.",
        userAvatarAlt: "User",
        invitedBy: "Invited by",
        roleLabel: "Role:",
        join: "Join",
        joining: "Joining...",
        createWorkspace: "Create workspace",
        expired: "This invite has expired.",
        expiresToday: "This invite expires today.",
        expiresInOneDay: "This invite expires in 1 day.",
        expiresInDays: "This invite expires in {{days}} days.",
        joiningWorkspace: "Joining workspace...",
        checkingInvitation: "Checking your invitation...",
        invalidTitle: "This invite is no longer valid",
        invalidDescription: "This workspace invite is invalid or has expired.",
        goToDashboard: "Go to dashboard",
        differentAccountTitle: "This invite belongs to a different account",
        signedInAs: "You are signed in as",
        unknownEmail: "Unknown email",
        sentTo: "This invite was sent to",
        switchAccountDescription:
          "Log out and continue with the invited account to join {{workspace}}.",
        logOutAndSwitch: "Log out and switch account",
      },
      welcome: {
        title: "Your AI operator is ready to go.",
        description:
          "You can now start handing off day-to-day operations to it.",
        greeting: "Hi, I'm Bustly.",
        pluginGreeting: "Hi, I'm BizSidekick.",
        avatarSubtitle: "Ready to get to work.",
        avatarRole: "AI Operator",
        cta: "Set up Bustly",
        pluginCta: "Set up BizSidekick",
      },
      intent: {
        title: "What should Bustly solve first?",
        description: "Choose the one issue you want off your plate first.",
        hintLine1: "Point me at the problem,",
        hintLine2: "and I'll start there.",
        otherPlaceholder: "Type what you want Bustly to help with...",
        setPriority: "Set priority",
        options: {
          ads: {
            label: "Ads are getting expensive",
            description: "Best match for acquisition efficiency",
          },
          conversion: {
            label: "Conversion is too low",
            description: "Landing pages, offers, and funnel fixes",
          },
          retention: {
            label: "Repeat purchase is weak",
            description: "Retention and lifecycle work",
          },
          execution: {
            label: "The team can’t keep up",
            description: "Execution support across the board",
          },
          other: {
            label: "Something else",
          },
        },
      },
      connect: {
        title: "Put Bustly to work across your business systems.",
        pluginTitle: "Put BizSidekick to work across your business systems.",
        description:
          "Connect one tool to get started. You can add more later.",
        descriptionMore:
          "You can add up to 5 connections, including up to 1 store connection.",
        dataUseTitle: "Why connect?",
        dataUseDescription:
          "Give Bustly accurate business context when it works on a task—without repeatedly copying reports or admin data.",
        dataMeaningTitle: "How do connections work?",
        dataMeaningDescription:
          "Bustly reads relevant information within the access you approve only when you ask a question or start a task. It never receives your account passwords or continuously pulls all of your data in the background.",
        dataSecurityTitle: "Data & security commitments",
        authorizedAccessTitle: "Authorized access only:",
        authorizedAccessDescription:
          "Bustly only accesses the stores, accounts, or data you explicitly authorize. It cannot access content outside that scope.",
        passwordAccessTitle: "No password access:",
        passwordAccessDescription:
          "Connections use official authorization or protected credentials. Bustly never reads your account password.",
        trustBadge2Tag: "Infrastructure from AWS & Google Cloud",
        trustBadge2Desc:
          "Hosted on enterprise-grade AWS and Google Cloud infrastructure, with TLS and AES-256 encryption protecting data.",
        trustBadge3Tag: "Task information stays on your computer",
        pluginTrustBadge3Tag: "Task information stays local",
        trustBadge3Desc:
          "Task context, execution history, and memory are managed locally by the Bustly client and are not uploaded to the cloud.",
        pluginTrustBadge3Desc:
          "Task context, execution history, and memory are managed locally by BizSidekick and are not uploaded to the cloud.",
        trustBadge4Tag: "Official platform partner · Authorized connections",
        trustBadge4Desc:
          "Certified partner across Shopify, Google, Meta, and more. Connections use platform-supported methods such as OAuth and API keys; Bustly accesses only the information needed for tasks within your permissions, and you can disconnect anytime.",
        pluginTrustBadge4Desc:
          "BizSidekick is a certified partner across Shopify, Google, Meta, and more. Connections use platform-supported methods such as OAuth and API keys; BizSidekick accesses only the information needed for tasks within your permissions, and you can disconnect anytime.",
        pluginDescription:
          "Connect at least 1 connection to help BizSidekick better understand your business.",
        pluginDescriptionMore:
          "You can add up to 10 connections, including up to 2 stores.",
        stepLabel: "Step 2 of 3 · Connect apps",
        hintLine1: "Show me the store,",
        hintLine2: "and I'll get up to speed fast.",
        categories: {
          storeOps: "Store Ops",
          advertising: "Advertising",
          emailMarketing: "Email Marketing",
          marketing: "Marketing",
          analytics: "Analytics",
          seo: "SEO",
          socialMedia: "Social Media",
          customers: "Customers",
          finance: "Finance",
          connectionCount: "{{count}} connected",
          descriptions: {
            storeOps: "Stores and commerce platforms",
            advertising: "Paid advertising platforms",
            emailMarketing: "Email campaigns and customer engagement",
            marketing: "Email, affiliate, and growth tools",
            analytics: "Performance measurement and reporting",
            seo: "Search visibility and organic growth",
            socialMedia: "Social channels and audience engagement",
            customers: "Reviews and customer feedback",
            finance: "Payments and accounting tools",
          },
        },
        connectionProgress:
          "{{connected}} connection{{plural}} · at least {{required}} required",
        connectionProgressReady:
          "{{connected}} connection{{plural}} · at least {{required}} required",
        pluginConnectionProgress:
          "{{connected}} of {{required}} connections connected",
        pluginConnectionProgressReady:
          "✓ Ready to go · {{connected}} connections connected",
        storeConnectionUsage: "{{used}} of {{limit}} store connections used",
        connectedAppsLabel: "Connected apps",
        progressComplete: "Complete",
        statusLoadFailed:
          "We couldn't check your connected apps. Try again before continuing.",
        connect: "Connect",
        connected: "Connected",
        disconnect: "Disconnect",
        disconnecting: "Disconnecting...",
        connectFailed: "Failed to connect to {{platform}}",
        duplicateShopifyConnection:
          "{{store}} is already connected to this workspace.",
        duplicateStoreConnection:
          "{{store}} is already connected to this workspace.",
        connectionNotCompleted:
          "Connection wasn’t completed. Click Connect when you’re ready to try again.",
        connectedSuccess: "Successfully connected to {{platform}}",
        resourcesSyncing:
          "{{platform}} connected, but its resources are still syncing. Please try again shortly.",
        disconnectFailed: "Failed to disconnect from {{platform}}",
        disconnectedSuccess: "Successfully disconnected from {{platform}}",
        connectionDisconnectedSuccess: "{{connection}} disconnected",
        usedTrialBadge: "Trial used",
        disconnectTitle: "Disconnect {{tool}}?",
        disconnectConnectionTitle: "Disconnect {{connection}}?",
        disconnectDescription:
          "Disconnecting will stop data synchronization. You can reconnect at any time.",
        connectionPanelTitle: "{{tool}} connections",
        labelPlaceholder: "Add a label",
        labelUpdated: "Label saved",
        labelUpdateFailed: "We couldn’t save this label. Try again.",
        limits: {
          store:
            "This workspace supports up to {{limit}} store connections. You can still connect other business systems.",
          other:
            "This workspace supports up to {{limit}} other app connections.",
          total: "This workspace supports up to {{limit}} total connections.",
        },
        connectToolTitle: "Connect {{tool}}",
        connectToolAction: "Connect {{tool}}",
        disconnectToolAction: "Disconnect {{tool}}",
        storeHash: "Store Hash",
        storeHashPlaceholder: "abc123xyz",
        accountUuid: "Account UUID",
        accountUuidPlaceholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        platformDescriptions: {
          shopify: "Orders, products, inventory, and storefront health",
          woocommerce: "Storefront events, order flow, and fulfillment alerts",
          bigcommerce: "Catalog monitoring, checkout signals, and issue triage",
          magento: "Inventory visibility, orders, and merchandising ops",
          prestashop: "Catalog, orders, customers, inventory, and discounts",
        },
      },
      plan: {
        trialConnections: "During your trial: up to {{total}} connections, including up to {{stores}} store{{storePlural}}",
        title: "Start with a 7-day trial",
        paidTitle: "Choose a plan to continue",
        pluginPaidTitle: "Start your subscription",
        pluginTrialCta: "Start 7-day trial",
        loadingOptions: "Loading your plan options...",
        workspaceNotReady: "Workspace is not ready yet.",
        failedToStartCheckout: "Failed to start checkout.",
        failedToStartTrial: "Failed to start trial.",
        unavailableNow: "{{plan}} is not available right now.",
        loadFailed: "We couldn't load the plan options. Please try again.",
        retry: "Retry",
        storeLimitReached:
          "This workspace already has {{connected}} connected stores. This plan supports up to {{limit}}.",
        startingPriceLabel: "From",
        startingPriceSuffix: "",
        startingAt: "/mo",
        renewalNote:
          "Payment method required. After 7 days, your plan renews automatically at the price shown. Upgrade anytime.",
        trialWorkUnits:
          "During your 7-day trial: {{value}} Work Units refreshed daily",
        checkoutRequired: {
          title: "Free trial unavailable",
          description:
            "This store isn't eligible for a free trial. Continue to checkout for a paid plan, or connect another store.",
          continueToCheckout: "Continue to checkout",
          connectAnotherStore: "Connect another store",
        },
        solo: {
          title: "Solo",
          startingPrice: "$199",
          workUnitsSummary: "2,780 Work Units / month",
          description:
            "For founders and solo operators hiring their first AI operator.",
          bullet1: "1 workspace user included",
          bullet2: "Works like a 3–5 year operator, available 24/7",
          bullet3: "Available 24/7",
          bullet4: "1 store connection included",
          bullet5: "400 Work Units refreshed daily",
          lowestTierBullet1: "1 workspace user included",
          lowestTierBullet2: "After your trial: up to {{total}} connections, including up to {{stores}} store{{storePlural}}",
          lowestTierBullet3:
            "After your trial: {{value}} AI Operator Work Units per month for lighter daily operations",
          lowestTierBullet4: "Up to 5 app connections",
          lowestTierCapacity:
            "Handles about {{min}}–{{max}} routine operational tasks per month",
          cta: "Start with Solo",
          badge: "Choose Solo for 1 user and 1 store",
        },
        team: {
          title: "Team",
          startingPrice: "$1,499",
          workUnitsSummary: "20,980 Work Units / month",
          description:
            "For companies rolling out AI operators across the workspace.",
          bullet1: "Unlimited workspace users",
          bullet2: "Works like a 3–5 year operator, available 24/7",
          bullet3: "Available 24/7",
          bullet4: "Up to 3 store connections included",
          bullet5: "400 Work Units refreshed daily",
          lowestTierBullet1: "10 workspace users included",
          lowestTierBullet2: "After your trial: up to {{total}} connections, including up to {{stores}} store{{storePlural}}",
          lowestTierBullet3:
            "After your trial: {{value}} shared AI Operator Work Units per month for higher team task volume",
          lowestTierBullet4: "Up to 10 app connections",
          lowestTierCapacity:
            "Handles about {{min}}–{{max}} team operational tasks per month",
          cta: "Start with Team",
          badge: "Choose Team for multiple users and up to 3 stores",
        },
        featureGroups: {
          workspace: "Workspace",
          workUnitTopUps: "On-demand Work Unit top-ups",
          builtInAiCapabilities: "Built-in AI capabilities",
          createImages: "Create images with Nano Banana 2",
          createVideos: "Create videos with Seedance 2.0",
          handleBusinessTasks: "Handle business tasks with Opus 4.8",
        },
      },
      contactSales: {
        title: "Talk to Sales",
        description: "Get a custom demo and pricing for your specific needs",
        submittedTitle: "Request Received!",
        submittedSubtitle: "Our team will be in touch shortly.",
        exploreDescriptionPrefix:
          "Once you connect your payment method, you can start your",
        enterpriseTrial: "7-day Enterprise Trial",
        exploreDescriptionSuffix: "immediately.",
        activateTrial: "Activate Enterprise Trial",
        submittedDescription:
          "Thank you for your interest in Bustly Enterprise. Our sales team will review your requirements and contact you within 24 hours.",
        firstName: "First name",
        lastName: "Last name",
        email: "Email",
        jobTitle: "Job title",
        jobTitlePlaceholder: "Marketing Director",
        companyName: "Company name",
        companyNamePlaceholder: "Acme Inc.",
        companySize: "Company size",
        companySizePlaceholder: "Select size...",
        companySizeOptions: {
          oneToTen: "1-10 employees",
          elevenToFifty: "11-50 employees",
          fiftyOneToTwoHundred: "51-200 employees",
          twoHundredOneToFiveHundred: "201-500 employees",
          fiveHundredPlus: "500+ employees",
        },
        brandCount: "Projects/stores",
        brandCountPlaceholder: "Select count...",
        brandCountOptions: {
          one: "1 project/store",
          twoToFive: "2-5 projects/stores",
          sixToTwenty: "6-20 projects/stores",
          twentyOnePlus: "21+ projects/stores",
        },
        primaryUseCase: "Primary use case",
        primaryUseCasePlaceholder: "Select primary goal...",
        primaryUseCaseOptions: {
          analytics: "Unified Analytics & Reporting",
          attribution: "Multi-touch Attribution",
          forecasting: "Inventory & Sales Forecasting",
          automation: "Marketing Automation",
          other: "Other",
        },
        needs: "Specific needs / questions",
        needsPlaceholder: "Tell us about your requirements...",
        submitAndUnlockTrial: "Submit & unlock trial",
        submitRequest: "Submit request",
        legalText:
          "By submitting this form, you agree to our Terms of Service and Privacy Policy.",
        errors: {
          firstNameRequired: "First name is required",
          lastNameRequired: "Last name is required",
          emailRequired: "Email is required",
          emailInvalid: "Please enter a valid email address",
          companyRequired: "Company name is required",
          companySizeRequired: "Please select company size",
          brandCountRequired: "Please select number of projects/stores",
          primaryUseCaseRequired: "Please select a primary use case",
        },
      },
      industryRequest: {
        successTitle: "Thank you!",
        successSubtitle: "We've received your request.",
        successDescription:
          "A member of our team will reach out to you within 24 hours.",
        title: "Let's talk about your business",
        description:
          "Tell us a bit about yourself and we'll be in touch shortly.",
        firstName: "First name",
        lastName: "Last name",
        workEmail: "Work email",
        companyName: "Company name",
        companyNamePlaceholder: "Your company name",
        industry: "Industry",
        industryPlaceholder: "e.g. Healthcare, Logistics, Real Estate",
        companySize: "Company size",
        companySizePlaceholder: "Select company size",
        companySizeOptions: {
          oneToTen: "1-10 employees",
          elevenToFifty: "11-50 employees",
          fiftyOneToTwoHundred: "51-200 employees",
          twoHundredOneToFiveHundred: "201-500 employees",
          fiveHundredPlus: "500+ employees",
        },
        message: "Message (optional)",
        messagePlaceholder: "Tell us about your specific needs...",
        submitting: "Submitting...",
        submit: "Submit request",
        privacyText: "By submitting, you agree to our Privacy Policy.",
        submitError: "Failed to submit your request. Please try again.",
        errors: {
          firstNameRequired: "First name is required",
          lastNameRequired: "Last name is required",
          emailRequired: "Email is required",
          emailInvalid: "Please enter a valid email address",
          companyNameRequired: "Company name is required",
          industryRequired: "Industry is required",
        },
      },
      integrationRequest: {
        submittedTitle: "Request submitted",
        submittedDescription:
          "We'll track this request and notify you when it's available.",
        close: "Close",
        title: "Request an integration",
        description:
          "Tell us which tool you'd like to connect with Bustly and we'll prioritize adding it.",
        firstName: "First name",
        lastName: "Last name",
        workEmail: "Work email",
        toolName: "Tool or platform name",
        toolNamePlaceholder: "e.g. Notion, Airtable, Monday.com",
        toolCategory: "Tool category",
        selectCategory: "Select a category (optional)",
        categories: {
          ecommerce: "E-commerce",
          marketing: "Marketing & Advertising",
          analytics: "Analytics",
          crm: "CRM & Support",
          finance: "Finance & Accounting",
          other: "Other",
        },
        toolWebsite: "Tool website",
        toolWebsitePlaceholder: "https://example.com (optional)",
        reason: "Why do you need this integration?",
        reasonPlaceholder:
          "Tell us how you would use this integration... (optional)",
        submit: "Submit request",
        errors: {
          firstNameRequired: "First name is required",
          lastNameRequired: "Last name is required",
          emailRequired: "Email is required",
          emailInvalid: "Please enter a valid email address",
          toolNameRequired: "Tool or platform name is required",
        },
      },
      registrationGate: {
        title: "Configure Bustly for your D2C team",
        description:
          "Bustly is configured around your stores, team permissions, and daily operating workflows. Our product specialist will first understand your business goals, then guide the initial setup so your AI Operator is aligned with real outcomes from day one.",
        valueTitle: "What we’ll help you set up",
        values: {
          connections: {
            title: "Store and data connections",
            body: "How your stores, accounts, and core data sources should be connected",
          },
          access: {
            title: "Workspace and access control",
            body: "How to configure your workspace, roles, and access controls",
          },
          useCases: {
            title: "AI Operator use cases",
            body: "Which AI Operator use cases your team should start with",
          },
          plan: {
            title: "Plan and Work Units setup",
            body: "The right plan, Work Units setup, and expansion path for your team",
          },
        },
        formTitle: "Tell us about your business",
        submitLabel: "Contact Sales",
        logoutLabel: "Log out",
        successTitle: "Thank you!",
        successSubtitle: "Your request has been sent.",
        successMessage:
          "Our team will review your information and get back to you within 24 hours to schedule a personalized demo.",
        form: {
          firstNameLabel: "First Name",
          firstNamePlaceholder: "First name",
          lastNameLabel: "Last Name",
          lastNamePlaceholder: "Last name",
          emailLabel: "Email",
          emailPlaceholder: "you@company.com",
          workEmailLabel: "Work email",
          workEmailPlaceholder: "you@company.com",
          fullNameLabel: "Full name",
          fullNamePlaceholder: "Your name",
          workTitleLabel: "Work title",
          workTitlePlaceholder:
            "Founder, COO, Ecommerce Lead, Operations Manager...",
          companyNameLabel: "Company Name",
          companyNamePlaceholder: "Company name",
          storeUrlLabel: "Store or website URL",
          storeUrlPlaceholder: "https://yourstore.com",
          preferredContactMethodLabel: "Preferred contact method",
          preferredContactMethodPlaceholder: "Select contact method",
          preferredContactMethods: {
            email: "Email",
            phone: "Phone",
            whatsApp: "WhatsApp",
            weChat: "WeChat",
          },
          industryLabel: "Industry",
          industryPlaceholder: "e.g., Apparel, Beauty, Home Goods",
          companySizeLabel: "Company Size",
          companySizePlaceholder: "Select company size",
          companySizeOptions: {
            oneToTen: "1-10 employees",
            elevenToFifty: "11-50 employees",
            fiftyOneToTwoHundred: "51-200 employees",
            twoHundredOneToFiveHundred: "201-500 employees",
            fiveHundredPlus: "500+ employees",
          },
          messageLabel: "Message (optional)",
          messagePlaceholder:
            "Tell us about your store, team, and key use cases...",
          submittingLabel: "Submitting...",
          privacyText: "By submitting, you agree to our Privacy Policy.",
          notSpecifiedLabel: "Not specified",
          noMessageLabel: "No message provided",
          errorTitle: "Submission Failed",
          errorMessage:
            "There was an error submitting your request. Please try again.",
          errors: {
            firstNameRequired: "First name is required",
            lastNameRequired: "Last name is required",
            workEmailRequired: "Work email is required",
            fullNameRequired: "Full name is required",
            workTitleRequired: "Work title is required",
            emailRequired: "Email is required",
            emailInvalid: "Please enter a valid email address",
            companyNameRequired: "Company name is required",
            storeUrlRequired: "Store or website URL is required",
            storeUrlInvalid: "Please enter a valid URL",
            preferredContactMethodRequired:
              "Preferred contact method is required",
            industryRequired: "Industry is required",
          },
        },
      },
    },
    integrationsSettings: {
      integrations: "Apps & connections",
      connectedFilter: "Connected",
      addIntegrations: "Add integrations",
      allIntegrations: "Workspace connections",
      connectionManagement: "Connection management",
      browseIntegrations: "Add integrations",
      connectedStores: "Connected stores",
      connectedAccounts: "Connected accounts",
      brandWorkspaceConnectionUsageLabel: "Workspace limits",
      brandStoreConnectionUsage:
        "{{current}} / {{limit}} store connections used",
      brandTotalConnectionUsage: "{{current}} / {{limit}} connections used",
      storeOps: "Store Ops",
      advertising: "Advertising",
      marketing: "Marketing",
      emailMarketing: "Email Marketing",
      analytics: "Analytics",
      seo: "SEO",
      socialMedia: "Social Media",
      customers: "Customers",
      finance: "Finance",
      teamCollaboration: "Team Collaboration",
      searchIntegrations: "Search apps or connections...",
      searchConnections: "Search connections, accounts, or labels...",
      clearSearch: "Clear search",
      searchResults: "Search results",
      searchResultsSummary: "{{count}} match{{plural}} across all categories",
      manageConnectionAccess: "Manage access",
      connectionFilter: "Connection filter",
      connectionScope: "Connection view",
      connectionOwnerFilter: "Connected by",
      allConnectors: "Anyone",
      onlyMyConnections: "Only me",
      allConnections: "Workspace connections",
      allBrandConnections: "All Project connections",
      ownedByMe: "Connected by me",
      member: "Member",
      user: "Member",
      role: "Role",
      connector: "Connector",
      connectionOwner: "Connection owner",
      you: "You",
      myConnectionsTitle: "App Connections",
      connectionAccessSubtitle:
        "Manage connected accounts, status, ownership, and who can use each connection.",
      userAccessForConnection: "Manage connection access",
      connectionAccessDescription:
        "Choose which workspace members can use this connected account.",
      userAccessForGoogleAdsAccount: "Manage access for {{account}}",
      loadingConnectionAccess: "Loading connection access...",
      loadingBusinessGroupMemberAccountAccess:
        "Loading members and account access...",
      failedToLoadConnectionAccess: "Failed to load connection access.",
      connectionAccessUnavailable:
        "Connection access is unavailable for this connection.",
      failedToSaveConnectionAccess: "Failed to save connection access.",
      duplicateConnectionInCurrentWorkspace:
        "{{tool}} is already connected to this workspace.",
      canManageAccess: "Manage access",
      canManageAccessHelp:
        "Users with this permission can set other users' access for this connection: No access, View only, or Can take action.",
      connectionOwnerManageAccessLocked:
        "Connection owners always have Manage access.",
      connectionOwnerAccessProtected:
        "Only the connection owner can change their own access.",
      canManageAccessStatus: "Has access",
      cannotManageAccessStatus: "No access",
      connectionAccessLevel: "Connection access",
      manageOthersAccess: "Manage others' access",
      managePermission: "Manage Permission",
      descNoAccess: "This user cannot view or use this connection",
      descViewOnly: "This user can only view connection status and data",
      descViewAction: "This user can use and operate this connection",
      connectionManageAccessRequired:
        "This connection needs at least one person who can manage access.",
      googleAdsCustomerAccess: "Google Ads accounts",
      googleAdsCustomerAccessCount: "{{count}}/{{total}} selected",
      googleAdsNoActiveCustomers:
        "No active Google Ads accounts are selected for this connection.",
      applyToAll: "Set all to",
      searchMembers: "Search users...",
      noConnectionAccessRows: "No app connections to manage",
      noConnectionAccessRowsDescription:
        "Connect an app in Apps & connections, then return here to manage the connected account and member access.",
      noMyConnections: "You haven't connected any apps yet",
      noMyConnectionsDescription:
        "Connections you create and own will appear here.",
      noWorkspaceConnections: "This workspace has no connections yet",
      noWorkspaceConnectionsDescription:
        "Connect an app to make its accounts available in this workspace.",
      noConnectionMatches: "No connections match this view",
      noConnectionMatchesDescription: "Try another filter or search term.",
      noAssignableMembers:
        "Invite teammates to give them access to this connection.",
      connectionAccessUpdated: "Connection access updated",
      brandAssigned: "Project set to {{brand}}",
      brandRemoved: "Project removed",
      noConnectedIntegrationsFound:
        'No connected integrations found for "{{query}}"',
      noIntegrationsFound: 'No integrations found for "{{query}}"',
      noIntegrationsInCategory: "No integrations in {{category}} yet",
      editLabel: "Edit Label",
      editLabelAction: "Edit label",
      editLabelTooltip: "Edit this app account's label.",
      assignBrands: "Assign to Business groups",
      assignAppsToBrands: "Assign to Business groups",
      bulkAssignAppsToBrands: "Bulk assign to Business groups",
      assignConnectedAppsToBrands:
        "Assign connected app accounts to Business groups",
      assignAppsToBrandsDescription:
        "Choose a Business group, then set access for the app accounts you can manage.",
      businessGroupAssignmentDescription:
        "Choose Business groups and set their maximum access. Business group Owners get the selected access automatically and can give members the same or lower access.",
      assignBusinessGroupsForAccount: "Assign “{{account}}” to Business groups",
      businessGroups: "Business groups",
      assignedBusinessGroups: "Assigned Business groups",
      chooseBusinessGroup: "Choose a Business group",
      appAccounts: "App accounts",
      searchAppAccountsPlaceholder: "Search app accounts...",
      selectedAppAccounts: "{{count}} app accounts selected",
      confirmBulkAssignment: "Assign to Business groups",
      assigningApps: "Assigning...",
      noAppAccounts: "No app accounts are available to assign.",
      noMatchingAppAccounts: "No app accounts match your search.",
      assignBusinessGroup: "Assign Business group",
      saveBusinessGroupAssignments: "Save assignments",
      assignmentSelection: "Select",
      selectAllBusinessGroups: "Select all Business groups",
      noBusinessGroupsAvailable: "No Business groups available.",
      selectConnectedAccounts: "1. Choose connected app accounts",
      selectConnectedAccountsDescription:
        "Select one or more accounts to share with Business groups.",
      selectBusinessGroups: "2. Choose Business groups and access",
      selectBusinessGroupsDescription:
        "Choose each Business group that can use these accounts, then set its access ceiling.",
      businessGroupAccessCeiling: "Group access",
      businessGroupOwnerAccessDescription:
        "The Business group Owner automatically receives this access and can give members this level or no access. They cannot grant a higher level.",
      projectAssignmentOwnerAccessDescription:
        "Choose Projects to assign. After assignment, the Project owner can assign read or write access to members.",
      projectAssignmentOwnerAccessUpdated:
        "Project assignment updated. The Project owner can now manage member access.",
      manageProjectOwnerAccess: "Manage access",
      manageProjectOwnerAccessRequiresBusinessGroup:
        "Assign this account to a Business group before managing its owner's access.",
      manageProjectOwnerAccessTitle:
        "Manage access for Project owners · {{account}}",
      manageProjectOwnerAccessDescription:
        "Give Project owners Manage access so they can assign this app to members in their Project.",
      projectOwner: "Project owner",
      projectOwnerManageAccessHelp:
        "This Project owner can assign this app to members in their Project.",
      connectionOwnerProjectAccessFixed:
        "This Project owner is also the connection owner, so Manage access is already enabled.",
      saveProjectOwnerAccess: "Save changes",
      projectOwnerAccessUpdated: "Project owner access updated.",
      bulkBrandAssignmentSuccess:
        "Assigned {{count}} app account to {{brands}}.",
      bulkBrandAssignmentSuccessPlural:
        "Assigned {{count}} app accounts to {{brands}}.",
      selectBrandBeforeAccounts:
        "Select a Project before choosing connected accounts.",
      assignedToBusinessGroup: "Assigned",
      assignedToSomeBusinessGroups: "Assigned to some groups",
      access: "Access",
      label: "Label",
      labelPlaceholder: "e.g. North America",
      saving: "Saving",
      roleAccessForConnection: "Role access for this connection",
      roleAccessTooltip:
        "Set how each role can use this connection. Changes apply to everyone assigned to that role.",
      viewPermissionDetails: "View permission details",
      notSet: "Not set",
      accessByRole: "Access by role",
      roleCount: "{{count}} role{{plural}}",
      yourRole: "Your role",
      noUsers: "No users",
      updateAccess: "Update access",
      upgradeAddConnections: "Upgrade to add more connections",
      notNow: "Not now",
      upgradeToTeam: "Upgrade to Team",
      storeConnectionLimitReached: "Store connection limit reached",
      adminConnectionLimitReached: "Connection limit reached",
      connectedStoreCount: "{{count}} connected store{{plural}}",
      otherIntegrationConnectionLimitReached:
        "Other integration connection limit reached",
      totalIntegrationConnectionLimitReached:
        "Total integration connection limit reached",
      upgrade: "Upgrade",
      connectToolTitle: "Connect {{tool}}",
      connectToolAction: "Connect {{tool}}",
      connectingTool: "Connecting {{tool}}...",
      connectWithApiKey: "Connect with API key",
      connectFeishuTitle: "Connect Feishu",
      connectFeishuAction: "Connect Feishu",
      connectDingTalkTitle: "Connect DingTalk",
      connectDingTalkAction: "Connect DingTalk",
      dingtalkConnectionMethod: "DingTalk connection method",
      dingtalkInternalAppMethod: "Internal app",
      dingtalkMcpMethod: "MCP Server",
      dingtalkMcpConnectedSuccess:
        "DingTalk MCP server connected and tools synced.",
      dingtalkMcpSensitiveWarning:
        "The copied URL or JSON Config contains account credentials. Keep it private. Bustly encrypts the credential parameters after submission.",
      dingtalkMcpInputMethod: "DingTalk MCP address input method",
      dingtalkMcpSingleInput: "Paste one URL",
      dingtalkMcpBatchInput: "Import JSON Config",
      dingtalkMcpBatchJsonLabel: "DingTalk MCP JSON Config",
      dingtalkMcpBatchJsonPlaceholder:
        "Paste JSON Config copied from DingTalk, or an MCP configuration containing mcpServers",
      dingtalkMcpBatchJsonHelp:
        "Paste the JSON Config copied from DingTalk. Duplicate addresses are removed automatically.",
      dingtalkMcpServerNamePlaceholder: "For example: DingTalk AI Table",
      dingtalkMcpUrlHelp:
        "Paste the complete URL copied from DingTalk, including its query parameters.",
      dingtalkMcpImportUnsupported:
        "The import contains unsupported configuration: {{details}}",
      dingtalkMcpImportEmpty: "No importable MCP address was found.",
      dingtalkMcpImportInvalid:
        "We could not parse this DingTalk MCP configuration. Check the JSON and addresses.",
      dingtalkMcpImportAndConnect: "Import and connect",
      dingtalkMcpBatchSuccess:
        "Connected and synced {{count}} DingTalk MCP services.",
      dingtalkMcpBatchPartial:
        "Connected {{connectedCount}} DingTalk MCP services; {{failedCount}} failed. Successful connections were kept. Check the remaining addresses and try again.",
      dingtalkAdminNotice:
        "Only DingTalk enterprise administrators can create and publish the required internal app. App secrets are encrypted after submission.",
      dingtalkCallbackUrl: "OAuth callback URL",
      apiKeyConnectionDescription: "Paste your {{tool}} API key below.",
      apiKey: "API Key",
      apiKeyPlaceholder: "Paste your {{tool}} API key",
      showApiKey: "Show {{tool}} API key",
      hideApiKey: "Hide {{tool}} API key",
      showCredential: "Show {{credential}}",
      hideCredential: "Hide {{credential}}",
      semrushApiKeyPlaceholder: "Paste your Semrush API key",
      woocommerceStoreUrlPlaceholder: "https://shop.example.com",
      woocommerceConsumerKey: "Consumer Key",
      woocommerceConsumerKeyPlaceholder: "ck_...",
      woocommerceConsumerSecret: "Consumer Secret",
      woocommerceConsumerSecretPlaceholder: "cs_...",
      magentoStoreDomain: "Store Domain",
      magentoStoreDomainPlaceholder: "shop.example.com",
      magentoStoreCode: "Store Code",
      magentoStoreCodePlaceholder: "default",
      magentoAccessToken: "Access Token",
      magentoAccessTokenPlaceholder: "Paste your Adobe Commerce access token",
      noxInfluencerCredentialDescription:
        "Paste the Skills API Key from NoxInfluencer's Skills Dashboard. Bustly validates it with NoxInfluencer Skills and uses the Skill quota and capabilities attached to that key.",
      noxKey: "Skills API Key",
      noxKeyPlaceholder: "Paste your Skills API Key",
      okendoUserId: "User ID",
      okendoUserIdPlaceholder: "Paste Okendo User ID",
      okendoMerchantRestApiKey: "Merchant REST API Key",
      okendoMerchantRestApiKeyPlaceholder: "Paste Merchant REST API Key",
      showOkendoMerchantRestApiKey: "Show Okendo Merchant REST API Key",
      hideOkendoMerchantRestApiKey: "Hide Okendo Merchant REST API Key",
      looxCredentialDescription:
        "Loox requires both the Public Store ID and Secret API Key from your Loox API settings. The Reviews API is available on eligible Loox plans.",
      looxPublicStoreId: "Public Store ID",
      looxPublicStoreIdPlaceholder: "Paste Loox Public Store ID",
      looxSecretApiKey: "Secret API Key",
      looxSecretApiKeyPlaceholder: "Paste Loox Secret API Key",
      showLooxSecretApiKey: "Show Loox Secret API Key",
      hideLooxSecretApiKey: "Hide Loox Secret API Key",
      goAffProCredentialDescription:
        "Use the Merchant Admin Access Token for store and affiliate management. Public marketplace data needs no additional credential.",
      goAffProAdminAccessToken: "Merchant Admin Access Token",
      goAffProAdminAccessTokenPlaceholder:
        "Paste X-GOAFFPRO-ACCESS-TOKEN from merchant settings",
      showGoAffProAdminAccessToken: "Show Merchant Admin Access Token",
      hideGoAffProAdminAccessToken: "Hide Merchant Admin Access Token",
      connectionLabel: "Connection label",
      accountLabel: "Account Label",
      businessGroupLabel: "Project label",
      businessGroupLabelPlaceholder: "For example, North America Store",
      businessGroupAssignmentFailed:
        "Your app is connected, but we couldn't save its Project. Try again from the Project.",
      optional: "Optional",
      accountLabelPlaceholder: "Acme Growth Workspace",
      connect: "Connect",
      connectApplication: "Connect {{tool}}",
      feishuAppId: "App ID",
      feishuAppSecret: "App Secret",
      feishuAppSecretPlaceholder: "Paste App Secret",
      dingtalkAppId: "Client ID",
      dingtalkAppSecret: "Client Secret",
      dingtalkAppSecretPlaceholder: "Paste Client Secret",
      toolStoreUrl: "{{tool}} Store URL",
      shopifyLabelPlaceholder: "e.g. North America Store",
      genericToolLabelPlaceholder: "e.g. {{tool}} US Account",
      connectionDetailsTitle: "Connection Details: {{tool}}",
      feishuCustomAppTitle: "Connect Feishu",
      appIdClientId: "App ID / Client ID",
      appSecret: "App Secret",
      appSecretPlaceholder: "Paste app secret",
      storeUrl: "Store URL",
      prestashopCredentialDescription:
        "Orders, products, customers, and store data use a PrestaShop Webservice key. Bustly only verifies that the key is valid; available skills follow the permissions you grant in PrestaShop.",
      prestashopStoreUrl: "Store URL",
      prestashopStoreUrlPlaceholder: "https://shop.example.com",
      prestashopWebserviceKey: "Webservice Key",
      prestashopWebserviceKeyPlaceholder: "PrestaShop Webservice key",
      showPrestaShopWebserviceKey: "Show Webservice key",
      hidePrestaShopWebserviceKey: "Hide Webservice key",
      account: "Account",
      myAccess: "Your access",
      connectionStatus: "Connection status",
      action: "Action",
      noAccountSelected: "No Account Selected",
      viewConnectionDetails: "View Connection Details",
      roleAccess: "Role access",
      disconnecting: "Disconnecting...",
      disconnect: "Disconnect",
      howItWorks: "How it works",
      whatBustlyCanView: "What can Bustly view?",
      whatBustlyCanDo: "What can Bustly help do?",
      connected: "Connected",
      disconnected: "Disconnected",
      soon: "Coming soon",
      connecting: "Connecting...",
      waitingForRegistration: "Waiting for registration...",
      returnAfterCreatingAccount: "Return here after creating your account.",
      connectNow: "Connect now",
      addConnection: "Add connection",
      attentionNeeded: "Attention needed",
      error: "Error",
      connectedAt: "Connected at",
      storeLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}}. Choose a plan with a higher store limit to connect another store.",
      storeLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}}. Contact Sales if you need to connect more stores.",
      storeLimitPlansFallbackDescription:
        "Your current plan has reached its store connection limit. Choose a plan with a higher store limit to connect another store.",
      storeLimitSalesFallbackDescription:
        "Your current plan has reached its store connection limit. Contact Sales if you need to connect more stores.",
      adminStoreLimitPlansDescription:
        "Your {{plan}} plan supports up to {{totalLimit}} connections, including up to {{storeLimit}} store connections. Choose a plan with a higher store connection limit to connect another store.",
      adminStoreLimitSalesDescription:
        "Your {{plan}} plan supports up to {{totalLimit}} connections, including up to {{storeLimit}} store connections. Contact Sales if you need to connect more stores.",
      adminConnectionLimitPlansDescription:
        "Your {{plan}} plan supports up to {{totalLimit}} connections, including up to {{storeLimit}} store connections. Choose a plan with a higher connection limit to add more.",
      adminConnectionLimitSalesDescription:
        "Your {{plan}} plan supports up to {{totalLimit}} connections, including up to {{storeLimit}} store connections. Contact Sales if you need more connections.",
      otherIntegrationLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}} other integration connections. Choose a plan with a higher integration limit to add another connection.",
      otherIntegrationLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}} other integration connections. Contact Sales if you need more connections.",
      otherIntegrationLimitPlansFallbackDescription:
        "Your current plan has reached its other integration connection limit. Choose a plan with a higher integration limit to add another connection.",
      otherIntegrationLimitSalesFallbackDescription:
        "Your current plan has reached its other integration connection limit. Contact Sales if you need more connections.",
      totalIntegrationLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}} total integration connections. Choose a plan with a higher total connection limit to add another connection.",
      totalIntegrationLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}} total integration connections. Contact Sales if you need more connections.",
      totalIntegrationLimitPlansFallbackDescription:
        "Your current plan has reached its total integration connection limit. Choose a plan with a higher total connection limit to add another connection.",
      totalIntegrationLimitSalesFallbackDescription:
        "Your current plan has reached its total integration connection limit. Contact Sales if you need more connections.",
      pluginStoreLimitDescription:
        "BizSidekick supports up to {{limit}}. Disconnect a store before adding another.",
      pluginOtherIntegrationLimitDescription:
        "BizSidekick supports up to {{limit}} other app connections. Disconnect one before adding another.",
      pluginTotalIntegrationLimitDescription:
        "BizSidekick supports up to {{limit}} total connections. Disconnect one before adding another.",
      pluginIntegrationLimitReached:
        "This BizSidekick workspace has reached its connection limit.",
      roleAccessAriaLabel: "{{label}} for {{connection}}",
      dontHaveAccountSignUp: "Don't have an account? Sign up",
      klaviyoUnavailable: "Klaviyo unavailable",
      klaviyoUnavailableDescription:
        "Klaviyo reports this account is unavailable. Re-enable the account in Klaviyo, then reconnect or refresh Bustly.",
      descriptionTab: "Description",
      connectionsTab: "Connections",
      connectionCount: "{{count}} connection{{plural}}",
      showingConnectionsRange:
        "Showing {{start}} to {{end}} of {{total}} connections",
      userCount: "{{count}} user{{plural}}",
      thisIntegration: "this integration",
      teamPlan: "Team plan",
      storeHash: "Store Hash",
      accountUuid: "Account UUID",
      idLabel: "ID",
      nameLabel: "Name",
      labelUpdated: "Label saved",
      labelUpdateFailed: "We couldn’t save this label. Try again.",
      failedToUpdateIntegrationLabel: "Failed to update integration label",
      connectedSuccess: "Successfully connected to {{tool}}",
      connectedAndBusinessGroupsAssignedSuccess:
        "{{tool}} connected and assigned to Business groups.",
      unsupportedIntegration: "Unsupported integration: {{tool}}",
      connectPermissionDenied:
        "Permission Denied: You do not have permission to connect integrations",
      connectionFailed: "Connection Failed",
      failedToConnectToTool: "Failed to connect to {{tool}}",
      disconnectTitle: "Disconnect {{target}}?",
      disconnectSubtitle:
        "Disconnecting will stop data synchronization and remove this app account from the Workspace and every Project. You can reconnect at any time.",
      pluginDisconnectSubtitle:
        "Disconnecting will stop data synchronization. You can reconnect at any time.",
      disconnectGoogleAdsConnectionTitle: "Disconnect Google Ads?",
      disconnectGoogleAdsConnectionSubtitle:
        "This will remove all {{count}} Google Ads accounts in this connection from the Workspace and every Project. You can reconnect them at any time.",
      pluginDisconnectGoogleAdsConnectionSubtitle:
        "Disconnecting will stop data synchronization for all {{count}} Google Ads accounts in this connection. You can reconnect them at any time.",
      disconnectGoogleAdsCustomer: "Disconnect account",
      disconnectTooltip:
        "Disconnect this app account from the workspace and every Project.",
      pluginDisconnectTooltip:
        "Disconnect this app account and stop data synchronization.",
      disconnectGoogleAdsCustomerTooltip:
        "Disconnect this Google Ads account from the workspace and every Project.",
      pluginDisconnectGoogleAdsCustomerTooltip:
        "Disconnect this Google Ads account and stop data synchronization.",
      disconnectGoogleAdsCustomerTitle: "Disconnect {{account}}?",
      disconnectGoogleAdsCustomerSubtitle:
        "This Google Ads account will be removed from the Workspace and every Project. Other Google Ads accounts will stay connected.",
      pluginDisconnectGoogleAdsCustomerSubtitle:
        "Disconnecting this Google Ads account will stop data synchronization. Other Google Ads accounts will stay connected.",
      disconnectGoogleResourceTitle: "Disconnect {{account}}?",
      disconnectGoogleResourceSubtitle:
        "This account will be removed from the Workspace and every Project. If it is the last account in this Google connection, the app account will also disconnect.",
      pluginDisconnectGoogleResourceSubtitle:
        "Disconnecting this account will stop data synchronization. If it is the last account in this Google connection, the app account will also disconnect.",
      googleResourceDisconnectedSuccess: "{{account}} disconnected",
      disconnectGoogleResourceFailed: "Couldn't disconnect this Google account",
      disconnectFromBrand: "Disconnect application",
      removeConnectionFromBrand: "Remove from Project",
      removeConnectionFromBrandTitle: "Remove {{account}} from {{brand}}?",
      removeConnectionFromBrandSubtitle:
        "This app account will no longer be available in {{brand}}. Its workspace connection will stay active.",
      removeConnectionFromBrandTooltip:
        "Remove this app account from this Project. Its workspace connection stays active.",
      removedConnectionFromBrandSuccess: "{{account}} removed from {{brand}}",
      disconnectFromBrandTitle: "Disconnect {{account}}?",
      disconnectFromBrandSubtitle:
        "This application account will be removed from {{brand}}. Users will no longer be able to use it through this Project.",
      disconnectGoogleAdsCustomerFromBrandTitle: "Disconnect {{account}}?",
      disconnectGoogleAdsCustomerFromBrandSubtitle:
        "This Google Ads account will be removed from {{brand}}. Users will no longer be able to use it through this Project.",
      disconnectedFromBrandSuccess: "{{account}} disconnected",
      googleAdsCustomerDisconnectedSuccess: "{{account}} disconnected",
      disconnectGoogleAdsCustomerFailed:
        "Couldn't disconnect this Google Ads account",
      googleAdsCustomerNotSelected:
        "This Google Ads account is no longer selected.",
      disconnectPermissionDenied:
        "Permission Denied: You do not have permission to disconnect integrations",
      disconnectedSuccess:
        "{{tool}} Disconnected: Successfully disconnected from {{tool}}",
      disconnectFailed: "Disconnect Failed",
      failedToDisconnectFromTool: "Failed to disconnect from {{tool}}",
      currentTrialCantBeUsedHere: "Current trial can't be used here",
      currentTrialCantBeUsedHereDescription:
        "Looks like this app was already tried, so this trial can't continue here. You can still continue with a subscription plan.",
      subscriptionExpiredForConnectTitle:
        "Restore subscription to connect apps",
      subscriptionExpiredForConnectDescription:
        "This workspace subscription has expired. Restore the subscription to continue connecting apps.",
      freeTrialExpiredForConnectTitle: "Start subscription to connect apps",
      freeTrialExpiredForConnectDescription:
        "Your free trial has ended. Start a subscription to continue connecting apps.",
      paymentPastDueForConnectTitle: "Payment method needs attention",
      paymentPastDueForConnectDescription:
        "Update your payment method to continue connecting apps.",
      restoreSubscription: "Restore subscription",
      updatePaymentMethod: "Update payment method",
      pluginFreeTrialUsedTitle: "Free trial already used",
      pluginFreeTrialUsedDescription:
        "This {{tool}} account has already used a free trial in another workspace. Start a subscription to connect it here.",
      tryAnotherApp: "Try another app",
      storeBoundToAnotherWorkspace:
        "This store is already bound to another workspace.",
      roleCreatedSuccessfully: "Role created successfully",
      failedToCreateRole: "Failed to create role",
      updateRoleAccessTitle: "Update role access?",
      updateRoleAccessSubtitle:
        "This will update access for {{count}} role{{plural}} on this connection.",
      update: "Update",
      roleAccessSavedSuccessfully: "Role access saved successfully",
      failedToSaveRoleAccess: "Failed to save role access",
      upgradeAddConnectionsDescription:
        "To connect multiple accounts under {{tool}}, upgrade your workspace to {{plan}}.",
      connectRestrictedToolTitle: "Connect {{tool}}",
      connectRestrictedToolDescription:
        "{{tool}} integration is only available on the Team plan. Upgrade your workspace to start connecting team collaboration tools.",
      pluginConnectRestrictedToolTitle:
        "{{tool}} isn't available with BizSidekick",
      pluginConnectRestrictedToolDescription:
        "The BizSidekick plan doesn't include {{tool}} connections.",
      pluginRestrictionAcknowledge: "Got it",
      readAndWrite: "Read & write",
      readOnly: "Read only",
      writeOnly: "Write only",
      noAccess: "No access",
      viewOnly: "View only",
      viewAndTakeAction: "Can take action",
      connectedRecentlyBy: "Connected recently by {{user}}",
      connectedRecently: "Connected recently",
      connectedByOn: "Connected by {{user}} on {{date}}",
      connectedOn: "Connected on {{date}}",
      mcpCustomTitle: "MCP Configuration",
      mcpJsonImportTitle: "Import MCP from JSON",
      mcpUrlTitle: "Add MCP by URL",
      mcpServerName: "Server name",
      mcpServerNamePlaceholder: "e.g. My Custom Server",
      mcpTransportType: "Transport type",
      mcpServerUrl: "Server URL",
      mcpAdvancedSettings: "Advanced settings",
      mcpAddCustomHeader: "Add custom header",
      mcpHeaderName: "Header name",
      mcpHeaderValue: "Header value",
      mcpCustomMethod: "Custom MCP",
      mcpCustomMethodDescription:
        "Manually configure an HTTP MCP server and optional authentication header.",
      mcpJsonMethod: "Import from JSON",
      mcpJsonMethodDescription:
        "Paste an MCP config JSON and import URL-based servers.",
      mcpUrlMethod: "Add by URL",
      mcpUrlMethodDescription:
        "Quickly add a trusted remote MCP server by URL.",
      mcpOAuthDescription:
        "Connect with the server's standard authorization flow. Manual setup is available only when the server does not support it.",
      mcpOAuthMethod: "OAuth authorization",
      mcpConnectWithOAuth: "Connect",
      mcpOAuthFailed:
        "We couldn't start authorization for this MCP server. Try again.",
      mcpRefreshFailed:
        "Authorization finished, but the connector list could not refresh. Refresh this page in a moment.",
      mcpManualSetupTitle: "Set up MCP manually",
      mcpManualSetupDescription:
        "This server does not support standard authorization. Choose the access method it provides.",
      mcpManualAuthType: "Access method",
      mcpManualNone: "No credentials",
      mcpManualBearer: "Bearer token",
      mcpManualApiKey: "API key header",
      mcpManualSecret: "Credential",
      mcpOAuthReturnTitle: "Authorization complete",
      mcpOAuthReturnDescription: "You can close this window.",
      mcpOAuthReturnSuccessTitle: "Authorization received",
      mcpOAuthReturnSuccessDescription:
        "Bustly is confirming the connection. You can close this window.",
      mcpOAuthReturnErrorTitle: "Authorization wasn't completed",
      mcpOAuthReturnErrorDescription:
        "Close this window and try connecting again from Integrations.",
      mcpOAuthReturnMalformedTitle: "This authorization return is incomplete",
      mcpOAuthReturnMalformedDescription:
        "Close this window and start the connection again from Integrations.",
      mcpOAuthReturnClose: "Close window",
      mcpTest: "Test",
      mcpTestSuccess: "MCP connection is working.",
      mcpTestFailed: "We couldn't test this MCP connection. Try again.",
      mcpSync: "Sync",
      mcpSyncSuccess: "MCP tools synced.",
      mcpSyncFailed: "We couldn't sync MCP tools. Try again.",
      mcpPermissionRequired:
        "You don't have permission to manage MCP connections.",
      mcpSaving: "Saving...",
      mcpImport: "Import",
      mcpSaveAndSync: "Save and sync",
      mcpInvalidConfiguration: "Invalid MCP configuration.",
      mcpInvalidJson: "Invalid MCP JSON.",
      mcpLoadFailed: "Failed to load MCP connectors.",
      mcpSaveFailed: "Failed to save MCP connector.",
      mcpConnectedSuccess: "MCP connector saved.",
      mcpImportSuccess: "{{count}} MCP connectors imported.",
      mcpToolCount: "{{count}} tool{{plural}}",
      mcpToolsSynced: "Tools synced",
      mcpNoConnectors: "No MCP connectors yet.",
      mcpManageTools: "Manage tools",
      mcpHideTools: "Hide tools",
      mcpToolName: "MCP tool",
      mcpToolEnabled: "Use",
      mcpToolAccess: "Access",
      mcpToolRisk: "Risk",
      mcpToolRequireConfirmation: "Confirm before run",
      mcpSelectAllEnabledTools: "Enable all tools",
      mcpClearEnabledTools: "Disable all tools",
      mcpSelectAllConfirmations: "Require confirmation for all tools",
      mcpClearAllConfirmations: "Clear confirmation for all tools",
      mcpMemberAccess: "Member access",
      mcpToolUpdateSuccess: "MCP tool settings saved.",
      mcpToolUpdateFailed: "Failed to save MCP tool settings.",
      mcpSupabaseProjectRef: "Supabase Project Ref (optional)",
      mcpSupabaseProjectRefDescription:
        "Supabase MCP tools that require a Project Ref will use this default. Save an empty value to clear it.",
      mcpSupabaseProjectRefInvalid:
        "Supabase Project Ref must contain 16–32 lower-case letters or numbers.",
      mcpSaveResourceContext: "Save default project",
      mcpResourceContextSaved: "MCP default resource saved.",
      mcpResourceContextSaveFailed: "Failed to save MCP default resource.",
      mcpReauthorize: "Reauthorize",
      mcpRepairUrlCredential: "Repair connection",
      mcpCredentialRecoveryFailed:
        "We couldn't recover this MCP connection. Try again.",
      connectionBrandsLabel: "Assign Business groups (optional)",
      connectionBrandsDescription:
        "Choose the Business groups that can use this app account. You can select more than one.",
      connectionBrandsEmpty: "Don't assign a Business group yet",
      connectionBrandsSelected: "{{count}} Business groups selected",
      connectionBrandsAriaLabel: "Select Business groups",
      postAuthorizationBrandAssignmentTitle:
        "Set up {{tool}} for Business groups",
      postAuthorizationBrandAssignmentDescription:
        "Your app is connected. You can share it with Business groups and set their permission ceiling now, or do this later from Integrations.",
      assignBrandsLater: "Not now",
      keepDefaultBusinessGroupOnly: "Keep default Business group only",
      saveBrandAssignment: "Save",
      googleAdsAccountSelection: {
        title: "Select Google Ads accounts",
        updateFailed: "Failed to update Google Ads accounts.",
        loadingAccounts: "Loading accounts",
        noActiveConnection: "No active Google Ads connection found.",
        selectAllCustomers: "Select all customers",
        customer: "Customer",
        customerId: "Customer ID",
        selectAccountsDescription:
          "Choose the client accounts to save to this workspace. You can assign Business groups after saving.",
        selectedAccountsCount: "{{count}} accounts selected",
        saveAccounts: "Save {{count}} accounts",
        assignSavedAccountsTitle:
          "Assign Google Ads accounts to Business groups",
        accountsSavedTitle: "Google Ads accounts saved",
        accountsSavedDescription:
          "{{count}} accounts are now saved. Assign Business groups to each account, or do this later.",
        savedAccounts: "Saved accounts",
        savedAccount: "Google Ads account",
        selectSavedAccountDescription:
          "Choose a Google Ads account, then set its Business groups and permissions.",
        notAssignedToBusinessGroup: "Not assigned to a Business group",
        assignedBusinessGroupsCount: "Assigned to {{count}} Business groups",
        businessGroupsForAccount: "Business groups and permissions",
        assignmentProgress: "{{assigned}} of {{count}} accounts assigned",
        assignLater: "Not now",
        saveAssignments: "Save assignments",
        businessGroupAssignmentFailed:
          "We couldn't save the Business group assignments. Try again.",
        noEligibleAccounts: "No eligible Google Ads customer accounts found.",
      },
      toolDescriptions: {
        shopify:
          "Connect Shopify so Bustly can keep track of your store operations across products, orders, customers, inventory, and discounts. It can answer store questions, spot changes that need attention, and help update listings, stock, discounts, or orders when you ask.",
        bigCommerce:
          "Connect BigCommerce so Bustly can understand your store activity across catalog, orders, customers, and inventory. It can answer business questions, surface store issues, and help update products or stock when you ask.",
        wooCommerce:
          "Connect WooCommerce so Bustly can keep your store data in context across products, orders, customers, and inventory. It can answer store questions, track activity, and help edit listings, update stock, or handle order updates when you ask.",
        magento:
          "Connect Magento so Bustly can understand your catalog, inventory, customer, and order context. It can monitor store activity, answer operational questions, and help create or update catalog products or stock when you ask.",
        prestashop:
          "Connect PrestaShop with a Webservice key so Bustly can read the store data you grant, including catalog, orders, customers, stock, and discounts. After connection, agents can answer operational questions, pull store reports, and help update products, variants, inventory, order details, customers, or promotions within the permissions configured in PrestaShop.",
        wix: "Connect Wix so Bustly can understand your store content, products, orders, collections, and inventory. It can answer questions about your store, track sales activity, and help update listings, details, or stock when you ask.",
        googleAds:
          "Connect Google Ads so Bustly can keep track of your campaign performance across campaigns, ad groups, keywords, ads, assets, and spend. It can answer marketing questions, spot performance changes, and help create or update campaigns, ad groups, keywords, ads, and assets when you ask.",
        tikTokAds:
          "Connect TikTok Ads so Bustly can understand ad performance across advertiser accounts, campaigns, ad groups, ads, and reports. It can track spend, impressions, and results, helping you spot what is changing across your TikTok campaigns.",
        metaAds:
          "Connect Meta Ads so Bustly can keep track of performance across campaigns, ad sets, creatives, audiences, and budgets. It can answer campaign questions, spot changes in performance, and help pause, resume, or adjust campaigns when you ask.",
        linkedInAds:
          "Connect LinkedIn Ads so Bustly can understand performance across ad accounts, campaign groups, campaigns, creatives, matched audiences, targeting, and analytics. It can answer campaign questions, review performance changes, help create or boost ads, and update campaign or campaign group status, budgets, and settings when you ask.",
        pinterestAds:
          "Connect Pinterest Ads so Bustly can understand your ad performance across ad accounts, campaigns, ad groups, promoted Pins, audiences, targeting, and analytics. It can answer campaign questions, review performance changes, and help update campaign or ad group status, budgets, schedules, targeting, and settings when you ask.",
        xAds: "Connect X Ads so Bustly can track performance across ad accounts, campaigns, line items, promoted tweets, targeting, and analytics. It can answer advertising questions, surface performance changes, help create or promote tweets, and update campaign or line item status, budgets, and settings when you ask.",
        youtubeAds:
          "Connect YouTube Ads so Bustly can understand channel, video, comment, and analytics activity.",
        youtube:
          "Connect YouTube so Bustly can review channels, videos, Shorts, playlists, comments, and available analytics, and help manage supported video publishing and metadata actions after confirmation.",
        reddit:
          "Connect Reddit so Bustly can review subreddit context, search, feeds, posts, comments, messages, and limited engagement data, and perform supported confirmed content, text-message, flair, and human-requested vote actions.",
        attribuly:
          "Connect Attribuly with your API key for read-only attribution, event, ad, product, website, influencer, link, order-journey, touchpoint, and connection analysis. Available data depends on your Attribuly plan and connected sources.",
        snapchatAds:
          "Connect Snapchat Ads so Bustly can review account, post, comment, and analytics activity.",
        googleSearchConsole:
          "Connect Google Search Console so Bustly can understand how people find you through organic search. It can review queries, pages, rankings, and indexing signals to help you spot search opportunities and technical issues.",
        googleAnalytics:
          "Connect Google Analytics 4 so Bustly can understand your traffic, audience, and conversion patterns across accounts, properties, audiences, custom dimensions, and reports. It can answer analytics questions, generate reports, and help manage properties, audiences, or custom dimensions when you ask.",
        klaviyo:
          "Connect Klaviyo so Bustly can understand your email marketing and customer engagement across profiles, lists, segments, campaigns, flows, catalog items, templates, metrics, events, and reports. It can review performance, answer marketing questions, and help manage profiles, lists, list membership, segments, campaigns, flows, templates, metrics, and events when you ask.",
        mailchimp:
          "Connect Mailchimp so Bustly can understand your email marketing across account data, audiences, audience members, segments, campaigns, templates, and reports. It can review campaign performance, answer audience questions, and help manage audiences, members, tags, segments, campaigns, schedules, sends, and templates when you ask.",
        omnisend:
          "Connect Omnisend with an API key so Bustly can review lifecycle marketing data across contacts, campaigns, orders, and products.",
        okendo:
          "Connect Okendo with User ID and API Key so Bustly can review product reviews, ratings, customer feedback, survey responses, quiz responses, and webhook metadata. Write access is limited to supported review moderation, review replies, and Shopify-backed review groups.",
        goaffpro:
          "Connect GoAffPro with a Merchant Admin Access Token to review store configuration, affiliates, referrals, orders, commissions, transactions, payments, coupons, and webhooks, manage supported affiliate operations, and browse public programs and products.",
        uppromote:
          "Connect UpPromote with an API key so Bustly can review affiliate programs, affiliates, referrals, payments, and webhooks, and manage supported affiliate operations when you ask.",
        loox: "Connect Loox with your Public Store ID and Secret API Key so Bustly can read product reviews, ratings, review content, and related metadata. The current Loox API integration is read-only.",
        trustwill:
          "Connect TrustWILL Loyalty (LoLoyal) with an API key so Bustly can review customers, points, rewards, referrals, and webhooks, and manage supported loyalty activities when you ask.",
        noxinfluencer:
          "Connect NoxInfluencer with a Skills API Key so Bustly can discover and evaluate creators, retrieve approved contacts, monitor content, and operate Nox campaigns, collections, CRM, email, messages, products, short links, affiliate workflows, brand monitoring, files, and exports. Availability follows your Nox Skills plan and quota.",
        insiderOne:
          "Connect Insider One so Bustly can review customer engagement, campaign, and analytics data. If OAuth is not available for your account yet, you can connect with an API key instead.",
        emarsys:
          "Connect Emarsys so Bustly can understand customer engagement across campaigns, contacts, messages, and analytics.",
        judgeme:
          "Connect Judge.me so Bustly can read Shopify product review data, product review summaries, and Judge.me product records. With write access, Bustly can create reviews and send manual review requests when you ask.",
        facebookPage:
          "Connect Facebook Page so Bustly can understand your Page content and engagement across posts, comments, Messenger conversations, Messenger menu, and Page insights. It can review post performance, help create Page posts, drafts, or scheduled posts, and reply to comments when you ask.",
        instagram:
          "Connect Instagram so Bustly can understand your account, media, audience, comments, messages, ice breakers, and insights. It can review content performance, help create media posts, drafts, or scheduled posts, and reply to comments when you ask.",
        feishu:
          "Connect a Feishu custom app so Bustly can work with authorized calendar events, docs/wiki/drive, sheets, Bitable records, and tasks. Available actions depend on the permissions configured and granted for the Feishu app. Mail and IM sending are not available from this integration entry yet.",
        dingtalk:
          "Connect a DingTalk custom app so Bustly can work with contacts, calendars and attendance, chats and todos, docs/wiki/drive, Online Sheets and AI Tables, approvals, reports, live sessions, mail, and AI Minutes within the permissions actually enabled for the app. Available capabilities update as the app permissions change.",
        figma:
          "Connect Figma with a personal access token so Bustly can read authorized design files, nodes, image renders, comments, versions, components, styles, and dev resources available to the token scopes. It can support design collaboration, design-system, and multilingual product-listing handoff workflows through Figma REST APIs; it is not a full visual canvas editor.",
        customMcp:
          "Connect remote MCP servers so Bustly can discover their tools and make them available in the workspace under the configured access rules.",
        slack: "Receive instant notifications and alerts in your workspace.",
        semrush: "Track SEO performance and domain analytics data.",
        similarweb:
          "Connect Similarweb with an API key so Bustly can review website traffic, market intelligence, and competitive analytics data.",
        ahrefs:
          "Connect Ahrefs with an API key so Bustly can review SEO usage, backlink intelligence, and domain rating data.",
        aliexpress:
          "Connect AliExpress so Bustly can help source products for your store. It can search by keyword or image, review product details, pricing, availability, images, and SKU variants, and help compare sourcing options when you ask.",
        squarespace: "Manage your Squarespace commerce operations.",
        default:
          "Connect {{tool}} to enhance your Bustly workspace capabilities.",
      },
    },
    connectionDisclosure: {
      "title": "Before connecting {{tool}}",
      "purpose": "How it works",
      "read": "What it can read",
      "write": "What it can help do",
      "scopeTitle": "Access scope explained",
      "scopeSummary": "Understand what determines access.",
      "revocationSummary": "Learn how to disconnect and revoke access.",
      "privacySummary": "Learn how connection data is handled.",
      "scope": "These are supported product capabilities, not the OAuth permissions requested for this connection. The platform authorization screen or the permissions configured for your API key or token determine the access granted. Workspace access rules can limit who may use the connection.",
      "noWrite": "No execution capabilities are listed for this integration.",
      "unavailable": "A capability list is not available yet. Review the platform authorization screen or your key permissions before connecting.",
      "dynamic": "Custom MCP capabilities come from tools discovered from the server you connect. Before discovery, Bustly cannot list that server’s tools or access. Review the server’s authorization request and credentials; after connection, review its discovered tools and workspace access settings.",
      "revocation": "How to revoke access",
      "disconnectTitle": "Disconnect in Bustly",
      "disconnect": "In Admin, open Apps & connections and select the app. Use Disconnect for the relevant connection in the Connections tab or the Custom MCP server list. You need permission to manage that connection.",
      "providerTitle": "Revoke at the provider",
      "provider": "Disconnecting in Bustly and revoking the provider’s authorization are separate actions. To revoke provider access, use that provider’s connected-app controls, or revoke the API key or token you supplied. For Custom MCP, use the connected server’s authorization controls. Revoking a shared key or token may also affect other tools using it.",
      "retention": "For data retention, deletion requests, and contact options, see the privacy policy.",
      "privacy": "Privacy & data",
      "privacyTitle": "Connection data and privacy",
      "privacyDataTitle": "Data from connected apps",
      "privacyData": "Bustly processes data available through your authorized connection to support work in your workspace. The app’s purpose and capability list explain the types of data and actions it supports; actual access depends on the provider’s authorization and workspace access settings.",
      "privacyCredentialsTitle": "Authorization and credentials",
      "privacyCredentials": "OAuth permissions are shown by the provider. For an API key or personal token, review the permissions you assigned before sharing it. Custom MCP servers define their own tools and authorization requirements.",
      "privacyPolicy": "Open privacy policy",
      "privacyPolicyText": "Read the policy for details about collection, processing, service providers, retention, and privacy requests.",
      "cancel": "Cancel",
      "back": "Back",
      "continue": "Continue to connection",
      "close": "Close"
    },
    integrationCapabilityItems: {
      figmaDesignFilesAndNodes: "Authorized design files and nodes",
      figmaImageRenders: "Image renders from authorized files",
      figmaCommentsAndVersions: "File comments and version history",
      figmaComponentsAndStyles: "Components, component sets, and styles",
      figmaDevResources: "Dev resources available to the token",
      storeInformationAndSettings: "Store information and settings",
      productCatalogAndVariants: "Product catalog and variants",
      catalogProductsAndVariants: "Catalog products and variants",
      ordersAndOrderDetails: "Orders and order details",
      customerProfiles: "Customer profiles",
      customerProfilesAndSegments: "Customer profiles and segments",
      inventoryLevels: "Inventory levels",
      discountCodes: "Discount codes",
      storeAnalyticsAndPerformanceData: "Store analytics and performance data",
      collections: "Collections",
      createUpdateOrDeleteProducts: "Create, update, or delete products",
      createUpdateOrDeleteCatalogProducts:
        "Create, update, or delete catalog products",
      uploadProductImages: "Upload product images",
      publishProductsToSalesChannels: "Publish products to sales channels",
      updateProductVariants: "Update product variants",
      adjustInventoryLevels: "Adjust inventory levels",
      updateOrderDetails: "Update order details",
      editOrderLineItems: "Edit order line items",
      updateCustomerInformation: "Update customer information",
      updateCollections: "Update collections",
      createUpdateOrDeleteDiscounts: "Create, update, or delete discounts",
      productSearchResults: "Product search results",
      productDetails: "Product details",
      pricingAndAvailability: "Pricing and availability",
      productImages: "Product images",
      skuVariants: "SKU variants",
      adAccounts: "Ad accounts",
      customerAccounts: "Customer accounts",
      advertisers: "Advertisers",
      campaigns: "Campaigns",
      adSets: "Ad sets",
      adGroups: "Ad groups",
      ads: "Ads",
      adCreatives: "Ad creatives",
      customAudiences: "Custom audiences",
      targeting: "Targeting",
      analytics: "Analytics",
      comments: "Comments",
      conversionDestinations: "Conversion destinations",
      assets: "Assets",
      keywords: "Keywords",
      reports: "Reports",
      promotedPins: "Promoted Pins",
      audiences: "Audiences",
      lineItems: "Line items",
      promotedTweets: "Promoted tweets",
      updateCampaigns: "Update campaigns",
      updateAdSets: "Update ad sets",
      updateAdGroups: "Update ad groups",
      updateLineItems: "Update line items",
      createOrUpdateCampaigns: "Create or update campaigns",
      createOrUpdateAdGroups: "Create or update ad groups",
      createOrUpdateAds: "Create or update ads",
      createAndAttachAssets: "Create and attach assets",
      createOrUpdateKeywords: "Create or update keywords",
      createOrPromoteTweets: "Create or promote tweets",
      pages: "Pages",
      posts: "Posts",
      pageInsights: "Page insights",
      messengerConversations: "Messenger conversations",
      messengerPersistentMenu: "Messenger persistent menu",
      createPagePostsDraftsAndScheduledPosts:
        "Create Page posts, drafts, and scheduled posts",
      replyToComments: "Reply to comments",
      youtubeChannelProfile: "Channel profile",
      youtubeVideosAndShorts: "Videos and Shorts",
      youtubePlaylists: "Playlists",
      youtubeAnalytics: "Available channel and content analytics",
      youtubeDedicatedAnalytics:
        "Channel insights, daily views, demographics, and retention",
      publishYoutubeVideosShortsAndScheduledPosts:
        "Publish videos, Shorts, and scheduled posts",
      manageYoutubePostLifecycle:
        "Update, delete, retry, unpublish, or sync supported videos",
      updateYoutubePublishedVideoMetadata: "Update published video metadata",
      editOwnComments: "Edit own comments",
      deleteOwnComments: "Delete own comments",
      setYoutubeDefaultPlaylist: "Set the default playlist",
      redditAccount: "Reddit account",
      redditSubredditsRulesAndFlairs: "Subreddits, rules, and flairs",
      redditValidationSearchAndFeeds: "Subreddit validation, search, and feeds",
      redditConversationsAndMessages: "Conversations and messages",
      redditLimitedEngagementAnalytics: "Limited engagement analytics",
      publishRedditPostsAndScheduledPosts:
        "Publish Reddit posts and scheduled posts",
      manageRedditPostLifecycle:
        "Update, delete, retry, unpublish, edit, or sync supported posts",
      sendRedditTextMessages: "Create conversations and send text messages",
      manageRedditConversationStatus: "Archive or reactivate conversations",
      manageRedditDefaultsAndFlairs:
        "Manage the default subreddit and post flairs",
      castHumanInitiatedRedditVotes: "Cast votes directly requested by a human",
      attribulyGoals: "Attribution goals",
      attribulyModels: "Attribution models",
      attribulyAttributionReports: "Attribution summary and detail reports",
      attribulyCustomerAndConversionDetails:
        "Customer and conversion detail reports",
      attribulyAdAnalysis: "Ad analysis",
      attribulyProductAnalysis: "Product analysis",
      attribulyWebAnalysis: "Website analysis",
      attribulyInfluencerAndLinkAnalysis:
        "Influencer and tracking-link analysis",
      attribulyOrderJourneysAndTouchpoints:
        "Order journeys and attribution touchpoints",
      attribulyConnectedDataSources: "Connected data sources",
      attribulyEventStream: "Tracked event stream",
      account: "Account",
      media: "Media",
      insights: "Insights",
      audience: "Audience",
      messages: "Messages",
      iceBreakers: "Ice breakers",
      createMediaPostsDraftsAndScheduledPosts:
        "Create media posts, drafts, and scheduled posts",
      profiles: "Profiles",
      lists: "Lists",
      segments: "Segments",
      flows: "Flows",
      catalogItems: "Catalog items",
      templates: "Templates",
      metrics: "Metrics",
      events: "Events",
      manageProfiles: "Manage profiles",
      manageLists: "Manage lists",
      manageSegments: "Manage segments",
      manageCampaigns: "Manage campaigns",
      manageFlows: "Manage flows",
      manageTemplates: "Manage templates",
      updateMetrics: "Update metrics",
      manageEvents: "Manage events",
      audienceMembers: "Audience members",
      createAndUpdateAudiences: "Create and update audiences",
      addUpdateUnsubscribeAndTagAudienceMembers:
        "Add, update, unsubscribe, and tag audience members",
      contacts: "Contacts",
      supportTickets: "Support tickets",
      ticketConversations: "Ticket conversations",
      supportContacts: "Support contacts",
      createPrivateTicketNotes: "Create private ticket notes",
      activeCampaignContacts: "Contacts",
      activeCampaignTags: "Tags",
      updateContactNames: "Update contact first and last names",
      helpScoutMailboxes: "Mailboxes",
      helpScoutConversations: "Conversations",
      helpScoutConversationThreads: "Conversation threads",
      createHelpScoutInternalNotes: "Create internal notes",
      shipStationShipments: "Shipments",
      shipStationCarriers: "Carriers",
      shipStationLabelTracking: "Tracking for existing labels",
      mixpanelSavedInsightsReportData: "Saved Insights report data",
      manageContacts: "Manage contacts",
      manageOrders: "Manage orders",
      accounts: "Accounts",
      properties: "Properties",
      customDimensions: "Custom dimensions",
      createAndUpdateCustomDimensions: "Create and update custom dimensions",
      sites: "Sites",
      searchAnalytics: "Search analytics",
      inspectUrls: "Inspect URLs",
      sitemaps: "Sitemaps",
      addOrRemoveSites: "Add or remove sites",
      submitOrRemoveSitemaps: "Submit or remove sitemaps",
      requestIndexingUpdates: "Request indexing updates",
      domainAnalytics: "Domain analytics",
      keywordRankings: "Keyword rankings",
      backlinkInsights: "Backlink insights",
      competitorResearch: "Competitor research",
      websiteTraffic: "Website traffic",
      marketIntelligence: "Market intelligence",
      competitorAnalytics: "Competitor analytics",
      seoUsage: "SEO usage",
      domainRating: "Domain rating",
      reviews: "Reviews",
      reviewSummaries: "Review summaries",
      customerFeedbackProfiles: "Customer feedback profiles",
      surveyResponses: "Survey responses",
      quizResponses: "Quiz responses",
      webhookSubscriptions: "Webhook subscriptions",
      manageReviews: "Manage reviews",
      createReviewGroups: "Create review groups",
      sendReviewRequests: "Send review requests",
      storeConfiguration: "Store configuration",
      affiliatePrograms: "Affiliate programs",
      affiliates: "Affiliates",
      referralsAndOrders: "Referrals and orders",
      commissionsAndTransactions: "Commissions and transactions",
      payments: "Payments",
      coupons: "Coupons",
      publicAffiliateProgramsAndProducts:
        "Public affiliate programs and products",
      manageAffiliates: "Manage affiliates",
      manageReferralsAndOrders: "Manage referrals and orders",
      manageCommissionsAndTransactions: "Manage commissions and transactions",
      manageCommissions: "Manage commissions",
      managePayments: "Manage payments",
      manageCoupons: "Manage coupons",
      manageWebhookSubscriptions: "Manage webhook subscriptions",
      productReviews: "Product reviews",
      reviewRatingsAndContent: "Review ratings and content",
      loyaltyCustomers: "Loyalty customers",
      pointsActivity: "Points activity",
      rewardsActivity: "Rewards activity",
      referralActivity: "Referral activity",
      updateLoyaltyCustomers: "Update loyalty customers",
      adjustCustomerPoints: "Adjust customer points",
      createLoyaltyActivities: "Create loyalty activities",
      creatorSearchAndChannelLookup: "Creator search and channel lookup",
      creatorProfilesAndAudienceSignals:
        "Creator profiles and audience signals",
      creatorContactInformation: "Creator contact information",
      creatorContentAndPerformance: "Creator content and performance",
      creatorContentComments: "Creator content comments",
      creatorDiscoveryAndEvaluation: "Creator discovery and evaluation",
      approvedCreatorContacts: "Approved creator contact information",
      contentAndFutureContentMonitoring:
        "Content and future content monitoring",
      campaignsCollectionsAndCrm: "Campaigns, collections, and CRM",
      emailMessageAndCollaborationState:
        "Email, messages, and collaboration state",
      productsShortLinksAndAffiliation:
        "Products, short links, and affiliation workflows",
      brandMonitoringDisputesAndExports:
        "Brand monitoring, disputes, and exports",
      skillsQuotaPricingAndUsage: "Skills quota, pricing, and usage",
      manageCreatorListsAndExports: "Manage creator lists and exports",
      manageMonitoringCampaignsAndCollections:
        "Manage monitoring, campaigns, and collections",
      manageCrmProductsAndAffiliateWorkflows:
        "Manage CRM, products, and affiliate workflows",
      manageApprovedEmailAndMessages: "Manage approved email and messages",
      manageBrandMonitoringDisputesFilesAndFeedback:
        "Manage brand monitoring, disputes, files, and feedback",
      feishuAuthorizedUserAndContactProfiles:
        "Read authorized user and contact profile details",
      feishuCalendars:
        "Read calendars, event ranges, attendees, and free/busy status",
      feishuDocs: "Read docs, wiki nodes, comments, and drive metadata",
      feishuSpreadsheets:
        "Read spreadsheet metadata, ranges, and exported content",
      feishuBitable: "Read Bitable apps, tables, fields, views, and records",
      feishuTasks: "Read tasks and tasklists",
      feishuManageCalendarAttendees:
        "Create, update, reply to, and manage event attendees",
      feishuManageDocsAndWiki:
        "Create, update, move, copy, and comment on docs or wiki content",
      feishuWriteSpreadsheets: "Create spreadsheets and write or append ranges",
      feishuManageBitableRecords:
        "Create, update, and delete Bitable records and manage Bitable schema",
      feishuManageTasks:
        "Create and update tasks, tasklists, comments, subtasks, and tasklist members",
      dingtalkDocs: "Read Ding Talk documents and their structured content",
      dingtalkDocComments: "Read Ding Talk document comments",
      dingtalkOnlineSheets: "Read Ding Talk Online Sheets and cell ranges",
      dingtalkAiTables: "Read Ding Talk AI Tables, fields, views, and records",
      dingtalkManageDocs: "Create and update Ding Talk documents",
      dingtalkManageDocComments:
        "Create and manage Ding Talk document comments",
      dingtalkWriteOnlineSheets: "Create and update Ding Talk Online Sheets",
      dingtalkManageAiTables:
        "Create and update Ding Talk AI Table records and schema",
      dingtalkPeopleAndOrganization:
        "Read contacts, departments, HR, and organization information",
      dingtalkCollaboration:
        "Read calendars, attendance, chats, DINGs, todos, and bot information",
      dingtalkKnowledgeAndFiles:
        "Read enterprise search, docs, wiki, drive, Online Sheets, and AI Tables",
      dingtalkBusinessWorkflows:
        "Read approvals, reports, live sessions, mail, and AI Minutes",
      dingtalkManageCollaboration:
        "Manage authorized calendars, attendance, chats, DINGs, todos, and bot operations",
      dingtalkManageKnowledgeAndFiles:
        "Manage authorized docs, wiki, drive, Online Sheets, and AI Tables",
      dingtalkManageBusinessWorkflows:
        "Act on authorized approvals, reports, mail, AI Minutes, and related workflows",
    },
    inviteAccept: {
      alreadyAccepted:
        "This invitation has already been accepted. Continue to sign in and open the workspace.",
      backToSignIn: "Back to Sign In",
      cancelledDescription:
        "This workspace invite has been cancelled by the workspace owner.",
      checkingInvitation: "Checking your invitation...",
      close: "Close",
      createWorkspace: "Create workspace",
      differentEmail:
        "This invite belongs to a different email. Please sign out and continue with the invited account.",
      invalidDescription:
        "This workspace invite is invalid, expired, or has been cancelled.",
      invalidTitle: "Link Expired",
      join: "Join",
      joinTitle: "Join a workspace",
      loadFailed: "We could not load this invitation.",
      logOut: "Log out",
      memberRole: "Member",
      missingToken: "This invite link is missing an invitation token.",
      needHelp: "Need help? Contact our support team",
      roleLabel: "Role:",
      sentTo: "Sent to",
      singleSubtitle:
        "{{inviter}} invited you to join this workspace as {{article}} {{role}}.",
      startingLogin: "Starting login...",
      startFailed: "We could not start this invitation login.",
      teammate: "A teammate",
      thisWorkspace: "this workspace",
      unavailableTitle: "Link Unavailable",
    },
    planText: {
      renewSubscription: "Renew subscription",
      basic: "Basic",
      trial: "Trial",
      expired: "Expired",
      startSubscription: "Start subscription",
      upgrade: "Upgrade",
      renew: "Renew",
      manage: "Manage",
      freeTrialExpiredOn: "Free trial expired on {{date}}.",
      expiredOn: "Expired on {{date}}.",
      daysLeft: "{{days}} days left.",
      expiresOn: "Expires on {{date}}.",
      autoRenewsOn: "Your plan auto-renews on {{date}}.",
      trialEndsToday: "Free trial ends today.",
      oneDayLeftInTrial: "Free trial ends in 1 day.",
      daysLeftInTrial: "Free trial ends in {{days}} days.",
    },
    billing: {
      permissionDenied: "Permission Denied",
      noPermissionViewSubscriptionDetails:
        "You do not have permission to view subscription details.",
      billingAccessLoading: "Billing & Plans is loading.",
      billingAccessLoadFailed: "We couldn't load Billing & Plans",
      billingAccessLoadFailedDescription:
        "Try again to continue managing this workspace's plan.",
      currentPlan: "Current plan",
      pluginPlanName: "BizSidekick Plugin",
      currentBillingPlanLoading: "Current billing plan is loading.",
      active: "Active",
      updatePaymentMethod: "Update payment method",
      paymentFailedTitle: "Payment didn't complete",
      paymentFailedDescription: "Your plan is not active. Update your payment method to try again.",
      paymentPendingTitle: "Payment is still being confirmed",
      paymentPendingDescription: "Your plan is not active yet. Refresh this page in a moment for the latest status.",
      pluginPlanLimitUsers: "{{count}} workspace user",
      pluginPlanLimitStores: "{{count}} connected store",
      pluginPlanLimitApps: "Up to {{count}} app connections",
      viewOnly: "View only",
      billingHistory: "Billing history",
      date: "Date",
      description: "Description",
      type: "Type",
      amount: "Amount",
      status: "Status",
      action: "Action",
      view: "View",
      billingHistoryLoading: "Billing history is loading.",
      billingHistoryUnavailable: "Billing history is not available.",
      invoiceDocumentOpenFailed: "We couldn't open this invoice. Try again.",
      showingHistoryRange: "Showing {{start}} to {{end}} of {{total}} records",
      referralTitle: "Referral rewards",
      referralDescription: "After the invited workspace's first eligible subscription payment succeeds, you earn 10% of the amount paid as subscription credit, up to US$500 per referral, for renewals or plan upgrades.",
      referralEligibilityRequired: "Workspaces with an active paid subscription can share an invite link.",
      referralShareLink: "Referral share link",
      referralCopyLink: "Copy invite link",
      referralLinkCopied: "Invite message and link copied.",
      referralCopyFailed: "We couldn't copy the invite message. Try again.",
      referralCreateLink: "Create share link",
      referralWorking: "Working…",
      referralLoadFailed: "We couldn't load invitation details. Try again.",
      referralActionFailed: "We couldn't update the invite link. Try again.",
      referralAttributionExpired: "Your referral session has expired. Reopen the invitation link to continue with the discount.",
      referralNextPeriod: "Next billing period",
      referralNextNone: "No discount scheduled",
      referralNextPending: "Applying discount",
      referralNextApplied: "Discount applied",
      referralNextInvoiced: "Invoice issued, awaiting payment",
      referralNextSettled: "Discount used",
      referralNextReleased: "Credit retained for a future bill",
      referralNextReview: "Discount needs review",
      referralProgramUnavailable: "The referral program is temporarily unavailable. Existing credits are retained.",
      referralCount: "{{count}} invited workspaces",
      referralWorkspace: "Workspace {{index}}",
      referralPending: "Pending",
      referralLoading: "Loading invitation details…",
      referralSummary: "Referral rewards",
      referralRewardUnits: "Available credit",
      referralPendingUnits: "Pending credit",
      referralPeriodCap: "Maximum credit this billing period",
      referralYourRewards: "Subscription credits",
      referralReferredWorkspaces: "Referred workspaces",
      referralRewardUnit: "subscription credit",
      referralRecipientRoleReferrer: "Inviter",
      referralRecipientRoleReferred: "Invitee",
      referralRecipientRoleUnknown: "Workspace",
      referralCooldownUntil: "Available from {{date}}",
      referralAppliedAmount: "Applied {{amount}}",
      referralIntroCouponPrepared: "The referral is linked. Waiting for their first subscription payment.",
      referralAwaitingFirstPayment: "Pending",
      referralFirstPaymentConfirmed: "They completed their first subscription payment on {{date}}.",
      referralStatusAttributed: "Signed up through your link",
      referralStatusPending: "Pending",
      referralStatusPendingQualification: "Awaiting first subscription",
      referralStatusPendingCooldown: "Credit pending confirmation",
      referralStatusPendingPeriodCap: "Carried forward",
      referralStatusPendingEligibility: "Awaiting an active subscription",
      referralStatusCrediting: "Recording credit",
      referralStatusEarned: "Credit available",
      referralStatusCredited: "Credit applied",
      referralStatusIneligible: "Not eligible",
      referralStatusVoided: "Voided",
      referralAvailableCredit: "Available credit",
      referralAllocatedCredit: "Applied to next renewal",
      referralRewardCap: "Per referral cap",
      referralInvitee: "Invitee",
      referralWorkspaceColumn: "Workspace",
      referralCreditColumn: "Credit",
      referralStatusColumn: "Status",
      referralDateColumn: "Date",
      referralStatusReversed: "Reversed",
      referralCreditStatusAvailable: "Available",
      referralCreditStatusApplying: "Pending",
      referralCreditStatusAllocated: "Active",
      referralCreditStatusSettled: "Applied",
      referralCreditStatusVoided: "Voided",
      referralCreditStatusManualRecovery: "Needs review",
    },
    topup: {
      permissionDeniedBuyCredits:
        "You do not have permission to buy Work Units.",
      joinWorkspaceToBuyCredits: "Join this workspace to buy Work Units.",
      availableForSoloAndTeam:
        "Work Unit top-up is available for Solo and Team plans.",
      restorePaidPlan: "Restore your paid plan to buy Work Units.",
      availableForActivePaidPlans:
        "Work Unit top-up is available for active paid plans.",
      checkoutUrlMissing: "Checkout URL is missing.",
      failedToStartCheckout: "Failed to start checkout.",
      title: "Buy more Work Units",
      description:
        "Add Work Units to keep using Bustly beyond your plan limits.",
      amountToAdd: "Amount to add",
      credits: "Work Units",
      next: "Next",
    },
    referralCard: {
      badge: "Referral reward",
      title: "Refer a team, earn credit",
      description: "Save on your plan. No limit on total earnings.",
      action: "Invite now",
      collapsedTooltip: "Refer another team to Bustly and earn subscription credit. Use it for renewals and upgrades, with no limit on total earnings.",
    },
    referralModal: {
      title: "Refer a team, earn credit",
      titleLead: "Refer a team, ",
      titleAccent: "earn credit",
      heroReward: "Use credit for renewals and upgrades. No limit on total earnings.",
      subtitle: "After the invited workspace's first eligible subscription payment succeeds, you earn 10% of the amount paid as subscription credit, up to US$500 per referral, for renewals or plan upgrades.",
      howItWorks: "How to earn subscription credit",
      step1: "Share your invite link with another team",
      step2: "They create a new workspace through your invite link",
      step3: "After their first payment, you earn 10% of the amount paid as subscription credit for renewals or plan upgrades.",
      summaryTitle: "Invitation summary",
      creditOverview: "Subscription credit overview",
      availableCredit: "Available credit",
      remainingCredit: "Remaining subscription credit",
      availableSubscriptionCredit: "Available subscription credit",
      appliedToInvoices: "Applied to invoices",
      appliedToNextRenewal: "Applied to next renewal",
      autoApplyNote: "Automatically applied to renewals or plan upgrades.",
      createdWorkspaces: "Workspaces created",
      rewardedWorkspaces: "Rewards received",
      subscribed: "Subscribed",
      notSubscribed: "Not subscribed",
      rewardCountUnavailable: "The number of rewarded workspaces is currently unavailable",
      totalEarned: "Total subscription credit earned",
      totalUsed: "Total used",
      totalEarnedHelp: "Includes used and unused credit",
      invitedWorkspaces: "Workspaces invited",
      paidWorkspaces: "First payment completed",
      creditEarned: "Credit earned",
      creditUnavailable: "Credit amounts are currently unavailable",
      creditUsageNote: "Credit applies to renewals or plan upgrades in this workspace. Unused credit remains available.",
      shareLinkTitle: "Your invite link",
      shareMessage:
        "Meet Bustly — the business AI brain built for DTC brands.\n\n" +
        "Help operators focus on high-impact growth and give the whole team better odds at every stage.\n\n" +
        "Sign up through my exclusive invite link:\n" +
        "🎁 Start a 7-day free trial and get 10% off your first eligible subscription payment (up to US$500):\n" +
        "👉 {{inviteLink}}",
      shareInstruction: "Share with another team. They can take part by creating a new workspace through your link.",
      copied: "Copied",
      upgradeToRefer: "Start subscription",
      upgradePrompt: "Start a paid subscription to share your unique invite link and earn subscription credit.",
      emptyHistoryTitle: "No referrals yet",
      emptyHistory: "Once someone creates a workspace through your invite link, you can track their progress here.",
      termsNotice: "The invited workspace gets 10% off its first eligible subscription payment, up to US$500. After payment succeeds, you earn 10% of the amount paid as subscription credit, up to US$500 per referral. Credits accumulate and are automatically applied to renewals and plan upgrade charges in this workspace, up to the eligible charge amount. Unused credit remains available and cannot be withdrawn as cash.",
      viewTerms: "View terms and conditions",
      openInModal: "Open referral details",
      historyTitle: "Subscription credit",
      viewHistory: "View credit details",
      usageCount: "Workspaces using your link: {{count}}",
      viewFaq: "Rules and common questions",
      faqTitle: "Rules and common questions",
      faqFootnote: "Check your billing records for credit amounts and usage.",
      faq: {
        eligibility: {
          question: "Who can share an invite link?",
          answer: "Workspaces with an active paid subscription can share an invite link to invite other teams to Bustly.",
        },
        existingUsers: {
          question: "How does the invite link work?",
          answer: "The recipient creates a new workspace through your invite link and gets a discount on its first subscription payment.",
        },
        earned: {
          question: "When is subscription credit earned?",
          answer: "Your workspace earns credit after the invited workspace's first eligible subscription payment succeeds and the amount paid is more than US$0. Signing up, starting a trial, adding a payment method, or a failed payment does not earn credit.",
        },
        amount: {
          question: "What does each workspace receive?",
          answer: "The invited workspace gets 10% off its first eligible subscription payment, up to US$500. After payment succeeds, you earn 10% of the amount paid as subscription credit, up to US$500 per referral.",
        },
        firstPayment: {
          question: "What can I use subscription credit for?",
          answer: "You can use it for future subscription renewals and plan upgrade charges in this workspace.",
        },
        usage: {
          question: "Do I need to apply the credit myself?",
          answer: "No. Available credit is automatically applied when you renew or upgrade your plan. Unused credit remains available and cannot be withdrawn as cash.",
        },
        stacking: {
          question: "Can credits from multiple referrals be used together?",
          answer: "Yes. Credits accumulate and can be used together, up to the eligible charge amount. US$500 is the reward cap per referral, not the limit per use.",
        },
      },
      backToInvite: "Back",
      tableWorkspace: "Workspace name",
      tableEmail: "Invitee email",
      tableStatus: "Subscription status",
      tableDiscount: "Discount",
      tableDate: "Updated",
      referredUser: "Invited user {{index}}",
      statusPendingPayment: "Awaiting payment",
      statusFirstPaymentCompleted: "First payment completed",
      rewardBadgeTitle: "Earn 10% of the invitee's actual payment as subscription credit",
      rewardBadgeSub: "Based on the first eligible payment · Up to US$500 per invited workspace",
    },
    usage: {
      agentSelfImproving: "Agent self improving",
      pluginMvpTitle: "Usage metering is coming soon",
      pluginMvpDescription:
        "Request units and usage charges are not part of the current Plugin MVP.",
      dailyCredits: "Daily Work Units",
      monthlyCredits: "Monthly Work Units",
      shopifyPendingPaymentTooltipLabel: "Why is there a daily limit?",
      shopifyPendingPaymentTooltip:
        "Shopify payment is approved, but Bustly hasn't received it yet. {{dailyCredits}} Work Units refresh daily for now. Remaining monthly Work Units unlock after payment is received.",
      shopifyPendingPaymentTooltipWithoutLimit:
        "Shopify payment is approved, but Bustly hasn't received it yet. Plan Work Units refresh daily for now. Remaining monthly Work Units unlock after payment is received.",
      planStatusActive: "Active",
      planStatusTrial: "Trial",
      planStatusEnding: "Ending",
      planStatusExpired: "Expired",
      planStatusLimitReached: "Limit reached",
      grantStatusInUse: "In use",
      grantStatusAvailable: "Available",
      grantStatusExpired: "Expired",
      grantStatusUsedUp: "Used up",
      failedToLoadCreditOptions: "Failed to load Work Unit options.",
      resetsOn: "Next reset: {{date}} ({{timezone}})",
      endsOn: "Ends on {{date}}",
      expiresOn: "Expires on {{date}} ({{timezone}})",
      aboutCredits: "About Work Units",
      startSubscriptionToContinue: "Start subscription to continue",
      upgradeToContinue: "Upgrade to continue",
      purchasedCreditsSeparate:
        "Purchased Work Units stay separate from your monthly plan balance",
      noPurchasedCreditsAvailable:
        "Use purchased Work Units to continue using Bustly beyond your plan limits",
      purchasedCreditsSummary: "Purchased Work Units summary",
      expShort: "exp.",
      totalRemaining: "Total remaining",
      purchasedCredits: "Purchased Work Units",
      purchasedCreditsUsageOrder:
        "Purchased Work Units are used after monthly Work Units.",
      usePurchasedCreditsDescription:
        "Use purchased Work Units to continue using Bustly beyond your plan limits",
      enabledAutoReload: "Enabled auto reload",
      savedAutoReloadSettings: "Saved auto reload settings",
      disabledAutoReload: "Disabled auto reload",
      amountRemainingSuffix: "remaining",
      creditsRemaining: "Work Units remaining",
      yourPlan: "Plan Work Units",
      credit: "Purchased Work Units",
      empty: "Empty",
      available: "Available",
      frozen: "Frozen",
      expired: "Expired",
      blocked: "Blocked",
      permissionDenied: "Permission Denied",
      noPermissionViewUsageDetails:
        "You do not have permission to view usage details.",
      noExtraCreditsDescription: "No extra Work Units available.",
      frozenCreditsDescription:
        "Work Units are frozen. Restore your paid subscription to use them.",
      expiredCreditsDescription:
        "Your purchased extra Work Units have expired.",
      purchaseBlockedDescription: "Purchase blocked on free or expired plans.",
      upgradeToPurchase: "Upgrade your plan to purchase extra Work Units",
      buyCreditsTrialTooltip:
        "Only workspaces with an active Solo or Team subscription can buy Purchased Work Units",
      buyCreditsPermissionTooltip:
        "Only Workspace Owners or users with Manage billing permission can buy Work Units",
      autoReload: "Auto reload",
      autoReloadCredit: "Auto-reload Work Units",
      active: "Active",
      autoReloadDescription:
        "Automatically add Work Units when you reach your minimum balance.",
      monitorCollection: "Collection",
      monitorAnalysis: "Content analysis",
      channelNotRecorded: "Channel not recorded",
      clientUsage: "Expert tasks",
      marketUsage: "Keyword monitors",
      tree: {
        loading: "Loading monitor usage…", period: "Current billing period",
        noPeriod: "A current billing period is not available.", updateCount: "{{count}} updates", analysisCount: "{{count}} independent analyses",
        previous: "Previous", next: "Next", refresh: "Refresh", details: "Charge details",
        update: "Monitor update", analysis: "Independent analysis", historical: "Historical usage · update unknown",
        amountPending: "Not yet confirmed", runTotal: "Across periods:", subtotal: "Subtotal", noCharges: "No charges recorded",
        includedInMonitorTotal: "Included in monitor total", includedInUpdateTotal: "Included in this update",
        status: { complete: "Complete", partial: "Some data updated", failed: "Stopped", running: "Updating", unknown: "Update details unavailable" },
        billing: { pending: "Charges pending", settled: "Settled", recorded: "Recorded usage", unknown: "Settlement unconfirmed" },
      },
      recordsLoadError: "Usage records could not be loaded.",
      retryRecords: "Try again",
      noMarketUsage: "Work Units used by Keyword monitors will show up here.",
      usageRecord: "Usage record",
      user: "User",
      task: "Task",
      taskName: "Task name",
      monitorName: "Monitor name",
      time: "Last updated",
      creditsChange: "Total Work Units change",
      noUsageEvents: "Work Units used by Expert tasks will show up here.",
      showingEventsRange: "Showing {{start}} to {{end}} of {{total}} events",
      showingZeroEvents: "Showing {{total}} events",
      purchasedCreditPackages: "Purchased Work Unit packages",
      purchasedCreditPackagesDescription:
        "Each purchased Work Unit package expires 12 months after purchase.",
      total: "Total",
      used: "Used",
      remaining: "Remaining",
      expiresAt: "Expires at",
      status: "Status",
      noPurchasedCreditPackages: "No purchased Work Unit packages yet.",
      showingRangeOfTotal: "Showing {{start}}-{{end}} of {{total}}",
      viewDetails: "View details",
      received: "Received",
      balance: "Balance",
      minimumBalance: "Minimum balance",
      minimumBalanceDescription:
        "Auto reload triggers when your Work Unit balance goes below this amount.",
      targetBalance: "Target balance",
      targetBalanceDescription:
        "Auto reload brings your Work Unit balance back up to this amount.",
      minimumPurchase:
        "At least {{credits}} Work Units will be purchased, equivalent to",
      autoChargeDescription:
        "Bustly will charge your payment method automatically when you reach your minimum balance.",
      autoReloadInitialPurchase:
        "Enabling auto reload will trigger a one-time purchase of {{credits}} Work Units to reach your target balance. Estimated cost:",
      turnOff: "Turn off",
      turnOn: "Turn on",
      credits: "Work Units",
    },
    downloads: {
      usingBustlyAnywhere: "Using Bustly anywhere",
      downloadDescription:
        "Download Bustly Desktop to run tasks and manage your workspaces with a more integrated native experience.",
      downloadDescriptionShort:
        "Download Bustly Desktop to start tasks and work with your connected apps.",
      downloadClient: "Download",
    },
  },
  startup: {
    expertPreparationFailed: "We couldn't prepare your expert. Try again.",
    configuring: "Configuring your workspace...",
    openingWorkspace: "Opening your workspace...",
    ready: "Just a moment, and Bustly will be ready to work.",
    problem: "Bustly hit a problem while configuring your workspace.",
  },
  updater: {
    checkForUpdates: "Check for Updates",
    checking: "Checking",
    checkingForUpdates: "Checking for updates...",
    checkFailed: "Failed to check for updates.",
    preparing: "Preparing update...",
    openingWindowsInstaller: "Opening Windows App Installer...",
    continueInWindowsInstaller: "Continue in Windows App Installer",
    windowsInstallerProgress:
      "Windows shows download and installation progress.",
    updated: "Updated",
    updatedToVersion: "Updated to version {{version}}",
    installFailed: "Install failed",
    installing: "Installing update...",
    failedDescription: "The updater could not finish this release.",
    installingDescription:
      "Please keep Bustly closed. It will reopen automatically.",
    failed: "Update failed",
    failedShort: "Update failed.",
    startFailed: "Failed to start the updater.",
    ready: "Update ready to install",
    downloading: "Downloading update",
    downloadingEllipsis: "Downloading update...",
    available: "Update available",
    pendingVersion: "Version pending",
    versionLabel: "Version {{version}}",
    hideDetails: "Hide details",
    minimize: "Minimize",
    couldNotFinish: "The updater could not finish.",
    tryAgain: "Try again",
    downloadedWithVersion:
      "Version {{version}} has been downloaded. Restart the app to apply the update.",
    downloaded:
      "The update has been downloaded. Restart the app to apply the update.",
    installingShort: "Installing...",
    backgroundNote:
      "You can minimize this window. The update will continue in the background.",
    later: "Later",
    update: "Update",
    restartNow: "Restart now",
    starting: "Starting...",
    downloadNow: "Download now",
  },
  appShell: {
    restore: "Restore",
  },
  cloudAgent: {
    sessionFailed: "Cloud task failed.",
    runtimePreflightFailed: "Cloud runtime preflight failed.",
    workspaceSyncFailed: "Workspace sync failed.",
    finalizing: "Finalizing your workspace...",
    unknownError: "Unknown error",
  },
  agents: {
    labels: {
      overview: "Overview",
      marketing: "Marketing",
      storeOps: "Store Ops",
      customers: "Customers",
      finance: "Finance",
    },
    descriptions: {
      overview: "How can I help you today?",
      marketing: "Highlights wasted spend and recommends where to move budget.",
      storeOps:
        "Flags stockout, inventory backlog, and operational risks early.",
      customers:
        "Identifies churn-risk segments and high-value customer opportunities.",
      finance:
        "Explains how discounts, refunds, and fees affect true net revenue.",
    },
    useCases: {
      dailyPulse: {
        label: "Daily Pulse",
        prompt:
          "Surface the most important business changes of the day across revenue, orders, customers, and risk signals.",
      },
      anomalyDetection: {
        label: "Anomaly Detection",
        prompt:
          "Detect cross-channel anomalies and rank the issues that need attention first, such as refund spikes, stockout risk, or order backlog.",
      },
      executiveHub: {
        label: "Executive Hub",
        prompt:
          "Act as the command center that routes users from a high-level business summary into the right specialized channel for deeper analysis.",
      },
      campaignDiagnosis: {
        label: "Campaign Diagnosis",
        prompt:
          "Identify which campaigns and acquisition channels are driving efficient growth versus wasting budget.",
      },
      budgetGuidance: {
        label: "Budget Guidance",
        prompt:
          "Recommend where to cut spend, where to scale, and which campaigns need immediate optimization.",
      },
      funnelAnalysis: {
        label: "Funnel Analysis",
        prompt:
          "Pinpoint where demand is leaking across the funnel, from traffic to click, conversion, and checkout.",
      },
      fulfillmentRisk: {
        label: "Fulfillment Risk",
        prompt:
          "Highlight overdue, unfulfilled, or exception orders so operators can act before delays impact customer experience.",
      },
      inventoryRisk: {
        label: "Inventory Risk",
        prompt:
          "Detect low-stock and high-velocity SKUs that are likely to stock out in the next 7-14 days.",
      },
      productIssues: {
        label: "Product Issues",
        prompt:
          "Surface products with abnormal sales drops, high return rates, or unusual discount pressure.",
      },
      customerMix: {
        label: "Customer Mix",
        prompt:
          "Show whether growth is driven by healthy repeat demand or by one-time acquisition.",
      },
      retentionOpps: {
        label: "Retention Opps",
        prompt:
          "Identify at-risk customer cohorts and uncover segments that are most worth re-engaging.",
      },
      highValueCustomers: {
        label: "High-Value Customers",
        prompt:
          "Reveal which customer segments contribute the most long-term value, repeat revenue, and profitability.",
      },
      revenueQuality: {
        label: "Revenue Quality",
        prompt:
          "Evaluate business performance beyond top-line sales by focusing on net sales, margin quality, and revenue composition.",
      },
      discountLeakage: {
        label: "Discount Leakage",
        prompt:
          "Quantify how discounts and refunds are eroding revenue and trace the impact back to products, campaigns, or customer groups.",
      },
      healthSignals: {
        label: "Health Signals",
        prompt:
          "Turn financial metrics into clear business signals, such as rising discount dependency or concentrated refund pressure.",
      },
    },
  },
  userOnboarding: {
    defaultName: "there",
    exit: "Back to client",
    done: "Get Started",
    logout: "Log out",
    title: "Set up your first expert.",
    subtitle:
      "Bustly prepared one expert based on your workspace role. Review it before you start.",
    confirmOperation: "Create expert",
    creatingOperation: "Creating expert...",
    promptHint: "You can adjust this prompt before creating the expert.",
    inputPlaceholders: {
      operationName: "e.g. Store health check",
      prompt:
        "Monitor store health, flag issues early, and highlight what needs attention.",
    },
    questions: {
      confirmOperation: "What should this expert handle for you?",
    },
    fields: {
      operationName: "Expert name",
      operationPrompt: "What should this expert handle for you?",
    },
    roles: {
      owner: "Owner",
      admin: "Admin",
      billing: "Billing",
      member: "Member",
      customRole: "Custom Role",
      ecommerceOperations: "Ecommerce Operations",
      paidMediaChannelGrowth: "Paid Media & Channel Growth",
      seoContentGrowth: "SEO & Content Growth",
      socialInfluencerOps: "Social & Influencer Ops",
      crmLifecycleMarketing: "CRM & Lifecycle Marketing",
      businessAnalyticsInsights: "Business Analytics & Insights",
      siteExperienceQa: "Site Experience & QA",
    },
    defaultPrompts: {
      operationsManager:
        "Track workspace operations, key metrics, settings, members, permissions, integrations, and usage so the team knows what needs attention.",
      billing:
        "Monitor plan status, payment methods, invoices, Work Units, billing health, and usage questions.",
      generalist:
        "Help with general business tasks, summarize context, surface next steps, and keep routine work organized.",
      ecommerceOps:
        "Review ecommerce operations, platform health, product operations, orders, inventory, promotion follow-up, and daily recaps.",
      paidMedia:
        "Track Google Ads, Meta Ads, channel performance, ROAS, CAC, campaign changes, and likely causes behind performance shifts.",
      seoContent:
        "Plan SEO work, blog topics, landing pages, PDP copy, content optimization, and reusable execution checklists.",
      socialInfluencer:
        "Monitor Instagram, TikTok, Reddit, creators, KOLs, community signals, sentiment, and content opportunities.",
      crmLifecycle:
        "Analyze member segments, EDM, Klaviyo, winback, repeat purchase, lifecycle flows, and automation opportunities.",
      businessInsights:
        "Prepare daily and weekly reporting, business analysis, anomaly checks, attribution, channel performance, and user insights.",
      siteExperience:
        "Diagnose CRO issues, conversion leaks, Pixel and Shopify connections, site QA, tracking repairs, and product value clarity.",
      placeholder:
        "Monitor store health, flag issues early, and highlight what needs attention.",
    },
    operationNames: {
      operationsManager: "Business operations expert",
      billing: "Billing expert",
      generalist: "General business expert",
      ecommerceOps: "Ecommerce operations expert",
      paidMedia: "Paid media and channel growth expert",
      seoContent: "SEO and content growth expert",
      socialInfluencer: "Social and influencer operations expert",
      crmLifecycle: "Customer lifecycle marketing expert",
      businessInsights: "Business insights expert",
      siteExperience: "Site experience and QA expert",
    },
    result: {
      title: "Your expert has been created.",
      description:
        "{{operationName}} will appear in your client as your first expert.",
      badgeMessage: "I'm ready to help with business tasks.",
      badgeAlt: "Bustly expert badge",
    },
  },
  skillPage: {
    upload: {
      title: "Upload skill",
      dropOrClick: "Drag and drop or click to upload",
      whatHappensNext: "What happens next",
      localSource:
        "You can choose any local file or folder as the skill source.",
      installsShared:
        "Bustly validates it and installs into the shared `~/.bustly/skills` directory.",
      unableToResolvePath:
        "Unable to resolve the dropped path. Please choose a file manually.",
      unavailable: "Skill upload is unavailable in this environment.",
    },
    github: {
      title: "Import from GitHub",
      description: "Import a skill directly from a public GitHub repository.",
      urlLabel: "URL",
      invalidUrl: "Please enter a valid GitHub repository URL.",
      import: "Import",
    },
    uninstall: {
      title: "Delete skill?",
      description: "This will uninstall {{name}} from your workspace.",
      cancel: "Cancel",
      delete: "Delete",
      uninstalling: "Uninstalling...",
    },
    edit: {
      tooltip: "Edit custom skill",
      loading: "Opening skill...",
      title: "Edit custom skill",
      description: "Update the reusable instructions saved for this skill.",
      name: "Skill name",
      skillDescription: "Description",
      instructions: "Instructions",
      save: "Save skill",
      saving: "Saving...",
      savedTitle: "Skill saved",
      savedMessage: "{{name}} is ready to use.",
      savedRefreshFailedMessage:
        "Your changes are saved, but the skill list couldn't refresh. Refresh this page to see the latest version.",
      savedCloudSyncPendingMessage:
        "Your changes are saved locally, but cloud sync is still pending. Bustly will retry automatically.",
      loadFailedTitle: "Couldn't open skill",
      loadFailedMessage: "We couldn't open this skill. Try again.",
      saveFailedMessage:
        "We couldn't save your changes. Review the fields and try again.",
    },
    toasts: {
      installSuccessTitle: "Skill Installed",
      installSuccessMessage:
        "{{name}} has been added to your workspace and assigned to all experts.",
      installFailedTitle: "Install Failed",
      workspaceInstallSuccessTitle: "Skill Installed",
      workspaceInstallSuccessMessage:
        "{{name}} is now available on this device and assigned to all experts.",
      workspaceInstallFailedTitle: "Install Failed",
      updateSuccessTitle: "Skill Updated",
      updateSuccessMessage: "{{name}} has been updated to the latest version.",
      updateRestartRequiredTitle: "Skill Update Ready",
      updateRestartRequiredMessage:
        "{{name}} has been prepared. Restart Bustly to finish updating.",
      updateFailedTitle: "Update Failed",
      uninstallSuccessTitle: "Skill Uninstalled",
      uninstallSuccessMessage:
        "{{name}} has been removed from your workspace. You can reinstall it later.",
      uninstallFailedTitle: "Uninstall Failed",
      batchInstallTitle: "Skills Installed",
      batchUpdateTitle: "Skills Updated",
      batchUninstallTitle: "Skills Uninstalled",
      batchWithErrorsTitle: "{{title}} with Errors",
      installedAction: "installed",
      updatedAction: "updated",
      uninstalledAction: "uninstalled",
      batchPartialMessage:
        "{{succeeded}} {{skillLabel}} {{actionLabel}}, {{failed}} failed.",
      batchSuccessMessage:
        "Successfully {{actionLabel}} {{succeeded}} {{skillLabel}}.",
      batchUpdateRestartRequiredMessage:
        "{{prepared}} {{skillLabel}} prepared. Restart Bustly to finish updating.",
      batchUpdateRestartRequiredPartialMessage:
        "{{prepared}} {{skillLabel}} prepared and {{failed}} failed. Restart Bustly to finish updating.",
      skillsSingular: "skill",
      skillsPlural: "skills",
      assignedTitle: "Skills Assigned",
      assignedMessage: "{{count}} {{skillLabel}} assigned to {{operation}}.",
      uploadSuccessTitle: "Skill Uploaded",
      uploadSuccessMessage:
        "{{name}} has been installed into your workspace and assigned to all experts.",
      uploadFailedTitle: "Upload Failed",
      thisOperation: "this expert",
      workspaceNotReady: "Workspace is not ready yet.",
    },
  },
  chat: {
    modelPickerTooltip: "Choose a model for this task.",
    projectScopePicker: {
      applyFailed: "We couldn't apply this Business group scope. Try again.",
      authorizedResources: "Business group apps and resources",
      businessGroups: "Business groups",
      connectorNotRecorded: "Connector not recorded",
      hint: "Choose the Business group and resources for this task.",
      integrationCount: "{{count}} app accounts",
      loading: "Loading Business group resources…",
      loadFailed: "Business group data couldn't be loaded. Try again.",
      minimumSelection: "Select at least one Business group.",
      noResources: "No apps or resources are assigned to this Business group.",
      projects: "Business groups",
      required: "Choose a Business group before sending a task.",
      selectAll: "Select all",
      unnamedResource: "Unnamed resource",
      selectedCount: "{{count}} selected",
      apply: "Apply scope",
    },
    modelLevels: {
      auto: {
        label: "Auto",
        description: "Automatically selects a model based on the task.",
      },
      standard: {
        label: "Standard",
        description: "Fast and efficient for daily tasks.",
      },
      advanced: {
        label: "Advanced",
        description: "Best for tasks that need complex reasoning.",
      },
      ultra: {
        label: "Ultra",
        description: "Our strongest model for difficult tasks.",
      },
    },
    scrollToBottom: "Scroll to bottom",
    selfEvolution: {
      panel: {
        title: "Turn this task into a reusable skill",
        memoryShort: "Short-term memory",
        memoryLong: "Long-term memory",
        approve: "Create skill",
        review: "Review & save",
        retry: "Retry",
        dismiss: "Dismiss",
        evaluating: "Bustly is reviewing this task for reusable skills",
        applying: "Creating skill",
        approved: "Creating skill",
        drafted: "Needs review",
        applied: "Skill saved",
        failed: "Couldn't create skill",
        reviewFailed:
          "Couldn't generate reusable skill suggestions. Please retry.",
        actionFailed: "We couldn't update this skill. Try again.",
        preferenceLabel: "Reusable skill suggestions",
        preferenceEnabledTooltip:
          "On: Bustly helps you capture reusable skills after each task.",
        preferenceDisabledTooltip:
          "Off: Bustly won’t suggest reusable skills after tasks.",
      },
      memoryReview: {
        title: "Review Memory",
        memoryType: "Memory type",
        shortTerm: "Short-term memory",
        longTerm: "Long-term memory",
        heading: "Heading",
        body: "Memory",
        save: "Save",
        saving: "Saving",
        saved: "Saved",
        failed: "Save failed. Check the memory and try again.",
      },
      workflowReview: {
        title: "Review and save skill",
        displayName: "Name",
        description: "Description",
        body: "Skill body",
        save: "Save skill",
        saving: "Saving skill",
        saved: "Skill saved",
        failed: "We couldn't save this skill. Review the draft and try again.",
      },
    },
    taskSidebar: {
      review: "Review",
      toggle: "Toggle review panel",
      show: "Show review panel",
      hide: "Hide review panel",
      title: "Review panel",
      close: "Close review panel",
      expand: "Expand panel",
      restore: "Restore panel width",
      reviewTitle: "Review record",
      loadingTitle: "Loading review records...",
      emptyTitle: "No review records yet",
      emptyDescription:
        "Key steps and results will appear here after a task runs.",
      loadError: "Review records could not be loaded from the local ledger.",
      noActionsDescription:
        "This task did not perform any actions that need review.",
      incompleteDescription:
        "This task ran, but its action details were not fully recorded.",
      unavailableDescription: "This task's action details could not be loaded.",
      contentDetails: "Content details",
    },
    composer: {
      monitors: {
        expertOnly: "Monitor mentions are available only with the Market Research Expert. Switch experts or remove the monitor mentions.",
        title: "Monitors",
        loading: "Loading monitors...",
        retry: "Couldn’t load monitors. Retry",
        empty: "No matching monitors in this workspace.",
        paused: "Paused · saved data",
        remove: "Remove monitor",
        scopeError: "Remove the monitor mentions and select them again in this workspace (up to 20).",
      },
      placeholder: "What should we do next for your business?",
      followUpPlaceholder: "Assign a task to an expert",
      dropFilesHere: "Drop files here",
      addMenu: "Add files and more",
      addPhotosAndFiles: "Add photos & files",
      skills: "Skills",
      plugins: "Plugins",
      chromePlugin: "Chrome",
      chromePluginDescription: "Use signed-in Chrome tabs for this task",
      addPalette: {
        add: "Add",
        filesAndFolders: "Files and folders",
        useChrome: "Use Chrome",
        noResults: "No matches",
      },
      defaultPermission: "Request approval",
      defaultPermissionDescription:
        "Ask before write actions that may affect external apps or business data.",
      fullAccessPermission: "Full access",
      fullAccessPermissionDescription:
        "Run actions directly without approval prompts.",
      stop: "Stop",
    },
    approval: {
      allowOnce: "Yes",
      denyAndAdjust: "No, let Bustly adjust",
      skip: "Skip",
      submit: "Submit",
      submitting: "Submitting...",
      failed: "Approval failed.",
      adjustPlaceholder: "Tell Bustly what to adjust...",
    },
    queue: {
      steer: "Steer",
      queue: "Queue",
      edit: "Edit",
      turnOffQueueing: "Turn off queueing",
      turnOnQueueing: "Turn on queueing",
      removeQueuedMessage: "Remove queued follow-up",
      queuedMessageActions: "Queued follow-up actions",
      defaultQueuedTitle: "Queued follow-up",
      imageCount: "{{count}} image{{plural}}",
      referenceCount: "{{count}} reference{{plural}}",
    },
    timeline: {
      executionFailed: "Execution Failed",
      executionError: "Execution error.",
      connectionError: "Connection error.",
      reason: "Reason:",
      retryNow: "Retry Now",
      editYourMessage: "Edit your request",
      cancel: "Cancel",
      send: "Send",
      sending: "Sending...",
      copy: "Copy",
      copyMessage: "Copy request",
      edit: "Edit",
      editMessage: "Edit request",
      editedMessageEmpty: "Edited request cannot be empty.",
      publishCurrentResultRequest: "Publish this result",
      executionCompleted: "Execution Completed",
      processed: "Processed",
      processedIn: "Processed in {{duration}}",
      openFile: "Open file",
      toolLabel: "Tool",
      detailLabel: "Detail",
      argsLabel: "Args",
      outputLabel: "Output",
      runtimeOffline: "Runtime connection is offline. Reconnect and try again.",
      runtimeTransportUnavailable:
        "Runtime transport unavailable. Reconnect and try again.",
      modelSelectionFailed: "Failed to apply model selection.",
      connectionRecoveredRetry:
        "Retry the request after the connection recovers.",
      rateLimitRetry:
        "The upstream model temporarily rejected the request due to rate limiting. Retry in a moment or switch to a different model tier.",
      refreshCredentials: "Please login again to refresh your credentials.",
      executionStoppedBeforeFinish:
        "Execution stopped before the agent could finish this run. Retry the request or check the runtime connection and model availability.",
      retryUnavailableMissingContext:
        "Retry is unavailable because the original request context is no longer in memory. Submit the request again to continue.",
      retryUnavailableOffline:
        "Retry is unavailable because the runtime connection is offline. Reconnect and try again.",
      retryUnavailableInactivePlan:
        "Retry is unavailable because the current plan is inactive.",
      retryUnavailableRunning:
        "Retry is unavailable while another request is still running.",
      retryUnavailableMissingImageData:
        "Retry is unavailable because the original request included image attachments that are no longer available in memory. Submit the request again and reattach the images to continue.",
    },
    subscription: {
      expiredTitle: "Your plan has expired",
      expiredDescription:
        "Renew to keep chatting with Bustly and continue follow-up tasks.",
      defaultAction: "Upgrade",
    },
  },
};

const settingsModal = ((en.website as TranslationDictionary).settingsModal ||
  {}) as TranslationDictionary;
en.brandsSettings = {
  ...(settingsModal.brandsSettingsLegacy as TranslationDictionary),
  ...(en.brandsSettings as TranslationDictionary),
};

function normalizeBusinessGroupCopy(value: TranslationValue): TranslationValue {
  if (typeof value === "string") {
    return value
      .replace(/Brand Groups/g, "Business groups")
      .replace(/Brand Group/g, "Business group")
      .replace(/Projects/g, "Business groups")
      .replace(/Project/g, "Business group")
      .replace(/projects/g, "business groups")
      .replace(/project/g, "business group");
  }

  Object.entries(value).forEach(([key, child]) => {
    value[key] = normalizeBusinessGroupCopy(child);
  });

  return value;
}

// Keep the internal project/brand keys and API model stable while presenting one
// consistent Business group concept across every Admin copy surface.
normalizeBusinessGroupCopy(en.website as TranslationDictionary);
normalizeBusinessGroupCopy(en.brandsSettings as TranslationDictionary);

export default en;
